// ════════════════════════════════════════════════════════════════════
// STAMP ILLUSTRATIONS  (extracted from App.jsx Jun 19, 2026)
// ════════════════════════════════════════════════════════════════════
// SVG illustrations for the Stamps catalog. Each case returns an inline
// SVG with a 280×110 viewBox locked across all illustrations so card
// chrome stays consistent. The foreground band on every scene is the
// signature device.
//
// The component receives a stamp `id` and switches on it. The default
// branch falls back to a generic passport-stamp placeholder rendered
// from the catalog's metadata (title + country) — used for any stamp
// that doesn't have bespoke art yet.
//
// Adding a new illustration:
//   1. Add the catalog entry to ./catalog.js with hasIllustration: true
//   2. Add a matching `case "your-stamp-id":` here
//   3. Return an SVG with viewBox="0 0 280 110" and the editorial palette
// ════════════════════════════════════════════════════════════════════

import { SUGGESTED_STAMPS } from "./catalog.js";


export function StampIllustration({ id }) {
  switch (id) {
    case "cherry-blossoms":
      return (
        <svg className="scene" viewBox="0 0 280 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="100" fill="#F5DDE0"/>
          <g transform="translate(218, 60)" fill="#5C2A35" opacity="0.55">
            <rect x="0" y="0" width="32" height="22"/>
            <polygon points="-4,0 36,0 32,-5 0,-5"/>
            <rect x="3" y="-13" width="26" height="8"/>
            <polygon points="-1,-13 33,-13 29,-18 3,-18"/>
            <rect x="7" y="-24" width="18" height="6"/>
            <polygon points="3,-24 29,-24 25,-29 7,-29"/>
            <rect x="15" y="-32" width="2" height="3"/>
          </g>
          <rect x="0" y="82" width="280" height="18" fill="#D8B0B5"/>
          <path d="M 280 95 Q 200 75 130 60 Q 75 50 0 38" stroke="#3A2418" strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          <path d="M 150 58 Q 145 45 138 32" stroke="#3A2418" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
          <path d="M 80 48 Q 78 38 75 25" stroke="#3A2418" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
          <path d="M 200 70 Q 205 60 210 48" stroke="#3A2418" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
          <path d="M 235 80 Q 240 70 245 60" stroke="#3A2418" strokeWidth="1.3" fill="none" strokeLinecap="round"/>
          <g fill="#F5A8BC">
            <circle cx="10" cy="35" r="3.2"/><circle cx="15" cy="32" r="2.6"/><circle cx="7" cy="40" r="2.6"/><circle cx="19" cy="38" r="2.2"/>
            <circle cx="78" cy="25" r="3.2"/><circle cx="82" cy="28" r="2.6"/><circle cx="74" cy="29" r="2.2"/>
            <circle cx="138" cy="32" r="3.2"/><circle cx="143" cy="34" r="2.6"/><circle cx="134" cy="37" r="2.2"/>
            <circle cx="210" cy="48" r="3.2"/><circle cx="214" cy="51" r="2.6"/><circle cx="207" cy="53" r="2.2"/>
            <circle cx="245" cy="60" r="2.6"/><circle cx="248" cy="63" r="2.2"/>
          </g>
        </svg>
      );

    case "northern-lights":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#1A2540"/>
          <rect y="60" width="280" height="50" fill="#0F1A30"/>
          <g fill="white">
            <circle cx="30" cy="12" r="0.7" opacity="0.9"/>
            <circle cx="70" cy="20" r="0.5" opacity="0.7"/>
            <circle cx="110" cy="8" r="0.8" opacity="0.95"/>
            <circle cx="155" cy="15" r="0.5" opacity="0.75"/>
            <circle cx="200" cy="10" r="0.7" opacity="0.9"/>
            <circle cx="240" cy="18" r="0.5" opacity="0.8"/>
          </g>
          <path d="M 0 30 Q 60 12 130 26 T 280 22" fill="none" stroke="#5DCAA5" strokeWidth="9" opacity="0.4" strokeLinecap="round"/>
          <path d="M 0 30 Q 60 12 130 26 T 280 22" fill="none" stroke="#9FE1CB" strokeWidth="2.8" opacity="0.9" strokeLinecap="round"/>
          <path d="M 0 48 Q 70 32 150 44 T 280 40" fill="none" stroke="#7F77DD" strokeWidth="7" opacity="0.4" strokeLinecap="round"/>
          <path d="M 0 48 Q 70 32 150 44 T 280 40" fill="none" stroke="#AFA9EC" strokeWidth="1.8" opacity="0.75" strokeLinecap="round"/>
          <path d="M 0 60 L 0 56 L 30 50 L 60 54 L 95 46 L 125 52 L 155 44 L 185 50 L 215 46 L 250 52 L 280 48 L 280 60 Z" fill="#0A1228"/>
          <g transform="translate(95, 53)">
            <rect x="0" y="0" width="16" height="7" fill="#1A0D08"/>
            <polygon points="-2,0 18,0 8,-6" fill="#0A0604"/>
            <rect x="7" y="3" width="2.5" height="4" fill="#E8C878"/>
          </g>
        </svg>
      );

    case "pyramids-giza":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0C898"/>
          <rect width="280" height="38" fill="#E8A878"/>
          <rect width="280" height="20" fill="#D8907A"/>
          <circle cx="140" cy="48" r="16" fill="#F7DCA8"/>
          <circle cx="140" cy="48" r="11" fill="#F9E6BC"/>
          <path d="M 0 78 Q 40 70 90 73 T 200 70 T 280 75 L 280 95 L 0 95 Z" fill="#C8A888"/>
          <path d="M 0 85 Q 60 78 130 82 T 280 80 L 280 100 L 0 100 Z" fill="#B89878"/>
          <polygon points="75,95 117,42 158,95" fill="#B89878"/>
          <polygon points="117,42 158,95 134,95" fill="#9A7858"/>
          <polygon points="115,95 167,30 220,95" fill="#C8A878"/>
          <polygon points="167,30 220,95 195,95" fill="#A88858"/>
          <polygon points="200,95 222,58 245,95" fill="#B89878"/>
          <polygon points="222,58 245,95 230,95" fill="#9A7858"/>
          <rect x="0" y="95" width="280" height="15" fill="#D8B898"/>
        </svg>
      );

    case "whale-sharks":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#0A4A6A"/>
          <rect width="280" height="55" fill="#2A8AAC"/>
          <rect width="280" height="22" fill="#5DBAD0"/>
          <rect width="280" height="7" fill="#9DDAE8"/>
          <polygon points="40,0 65,0 88,80 68,80" fill="white" opacity="0.18"/>
          <polygon points="140,0 165,0 185,80 160,80" fill="white" opacity="0.15"/>
          <polygon points="220,0 245,0 265,80 240,80" fill="white" opacity="0.13"/>
          <g transform="translate(70, 28)">
            <ellipse cx="20" cy="-12" rx="2.2" ry="4.5" fill="#1B3A5C"/>
            <circle cx="20" cy="-16" r="2.4" fill="#1B3A5C"/>
            <ellipse cx="17.5" cy="-7" rx="2.5" ry="1.1" fill="#1B3A5C" transform="rotate(-30 17.5 -7)"/>
            <ellipse cx="22.5" cy="-7" rx="2.5" ry="1.1" fill="#1B3A5C" transform="rotate(30 22.5 -7)"/>
          </g>
          <g transform="translate(160, 65)">
            <path d="M -100 -6 C -90 -18 -70 -26 -50 -28 C -20 -30 10 -28 30 -22 L 38 -16 L 32 -10 L 40 -8 L 32 -2 C 28 6 20 14 5 17 C -10 18 -30 17 -55 13 C -75 9 -90 4 -100 -6 Z" fill="#1F2A3A"/>
            <g fill="#9DBDD0">
              <circle cx="-75" cy="-14" r="1.3"/><circle cx="-55" cy="-8" r="1.2"/><circle cx="-40" cy="-18" r="1.3"/><circle cx="-22" cy="-6" r="1.2"/><circle cx="-5" cy="-16" r="1.3"/><circle cx="10" cy="-8" r="1.2"/><circle cx="-65" cy="2" r="1.2"/><circle cx="-45" cy="6" r="1.3"/><circle cx="-25" cy="2" r="1.2"/><circle cx="-5" cy="8" r="1.3"/>
            </g>
            <circle cx="-95" cy="-2" r="1" fill="#0A0F18"/>
          </g>
        </svg>
      );

    case "maldives":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F8D8B0"/>
          <rect width="280" height="42" fill="#F0C098"/>
          <rect width="280" height="22" fill="#E8B088"/>
          <circle cx="225" cy="32" r="11" fill="#FAE0C0" opacity="0.85"/>
          <circle cx="225" cy="32" r="7" fill="#FAEAC8"/>
          <rect y="62" width="280" height="48" fill="#4ACABD"/>
          <rect y="65" width="280" height="40" fill="#60D0C0"/>
          <rect y="75" width="280" height="30" fill="#7ADAC8"/>
          <rect y="88" width="280" height="22" fill="#9DE5D0"/>
          <rect x="100" y="74" width="100" height="1.5" fill="#7A5040"/>
          <g transform="translate(115, 76)">
            <rect x="-9" y="0" width="1.6" height="13" fill="#5A4030"/>
            <rect x="-2" y="0" width="1.6" height="13" fill="#5A4030"/>
            <rect x="5" y="0" width="1.6" height="13" fill="#5A4030"/>
            <rect x="-7" y="-10" width="14" height="8" fill="#C8A878"/>
            <rect x="-7" y="-10" width="14" height="1.5" fill="#A88858"/>
            <rect x="-2" y="-7" width="4" height="5" fill="#1B3A5C"/>
            <polygon points="-9,-10 9,-10 0,-19" fill="#A87850"/>
            <polygon points="-9,-10 9,-10 0,-15" fill="#7A5028"/>
          </g>
          <g transform="translate(150, 80)">
            <rect x="-11" y="0" width="1.8" height="15" fill="#5A4030"/>
            <rect x="-3" y="0" width="1.8" height="15" fill="#5A4030"/>
            <rect x="7" y="0" width="1.8" height="15" fill="#5A4030"/>
            <rect x="-10" y="-12" width="20" height="10" fill="#C8A878"/>
            <rect x="-10" y="-12" width="20" height="2" fill="#A88858"/>
            <rect x="-3" y="-9" width="6" height="7" fill="#1B3A5C"/>
            <polygon points="-12,-12 12,-12 0,-23" fill="#A87850"/>
            <polygon points="-12,-12 12,-12 0,-19" fill="#7A5028"/>
          </g>
          <g transform="translate(190, 76)">
            <rect x="-9" y="0" width="1.6" height="13" fill="#5A4030"/>
            <rect x="-2" y="0" width="1.6" height="13" fill="#5A4030"/>
            <rect x="5" y="0" width="1.6" height="13" fill="#5A4030"/>
            <rect x="-7" y="-10" width="14" height="8" fill="#C8A878"/>
            <rect x="-7" y="-10" width="14" height="1.5" fill="#A88858"/>
            <rect x="-2" y="-7" width="4" height="5" fill="#1B3A5C"/>
            <polygon points="-9,-10 9,-10 0,-19" fill="#A87850"/>
            <polygon points="-9,-10 9,-10 0,-15" fill="#7A5028"/>
          </g>
        </svg>
      );

    case "salar-uyuni":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0B098"/>
          <rect width="280" height="40" fill="#E89878"/>
          <rect width="280" height="20" fill="#D88858"/>
          <g fill="white" opacity="0.55">
            <ellipse cx="55" cy="22" rx="32" ry="2"/>
            <ellipse cx="195" cy="16" rx="38" ry="2"/>
            <ellipse cx="115" cy="28" rx="22" ry="1.4"/>
            <ellipse cx="250" cy="32" rx="18" ry="1.2"/>
          </g>
          <path d="M 0 55 L 30 50 L 60 53 L 100 47 L 130 51 L 160 45 L 200 50 L 230 46 L 260 49 L 280 47 L 280 60 L 0 60 Z" fill="#7A8898" opacity="0.7"/>
          <line x1="0" y1="60" x2="280" y2="60" stroke="#A89888" strokeWidth="0.4" opacity="0.6"/>
          <rect y="60" width="280" height="50" fill="#F0B098"/>
          <rect y="62" width="280" height="22" fill="#E89878" opacity="0.85"/>
          <path d="M 0 62 L 30 67 L 60 64 L 100 70 L 130 66 L 160 72 L 200 67 L 230 71 L 260 68 L 280 70 L 280 60 L 0 60 Z" fill="#7A8898" opacity="0.45"/>
          <g transform="translate(140, 60)">
            <circle cx="0" cy="-7" r="1.7" fill="#1B3A5C"/>
            <rect x="-1.2" y="-5.5" width="2.4" height="5" fill="#1B3A5C"/>
            <rect x="-1" y="-0.5" width="0.9" height="3.5" fill="#1B3A5C"/>
            <rect x="0.1" y="-0.5" width="0.9" height="3.5" fill="#1B3A5C"/>
          </g>
          <g transform="translate(140, 60)" opacity="0.45">
            <rect x="-1" y="3" width="0.9" height="3.5" fill="#1B3A5C"/>
            <rect x="0.1" y="3" width="0.9" height="3.5" fill="#1B3A5C"/>
            <rect x="-1.2" y="6.5" width="2.4" height="5" fill="#1B3A5C"/>
            <circle cx="0" cy="13" r="1.7" fill="#1B3A5C"/>
          </g>
        </svg>
      );

    case "great-migration":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0C098"/>
          <rect width="280" height="42" fill="#E8A878"/>
          <rect width="280" height="22" fill="#D88560"/>
          <circle cx="140" cy="48" r="14" fill="#F5D8A0"/>
          <circle cx="140" cy="48" r="9" fill="#F8E0B8"/>
          <path d="M 0 65 Q 40 60 80 62 T 160 60 T 240 62 T 280 64 L 280 80 L 0 80 Z" fill="#A87858"/>
          <path d="M 0 72 Q 60 68 130 70 T 280 72 L 280 88 L 0 88 Z" fill="#8A6048"/>
          <rect y="82" width="280" height="28" fill="#D8A878"/>
          <rect y="94" width="280" height="16" fill="#B88858"/>
          <g transform="translate(28, 80)" fill="#2A1F18">
            <rect x="0" y="0" width="1.5" height="13"/>
            <ellipse cx="0.75" cy="-1" rx="9" ry="2.2"/>
            <ellipse cx="0.75" cy="-2.8" rx="6" ry="1.5"/>
          </g>
          <g transform="translate(248, 80)" fill="#2A1F18">
            <rect x="0" y="0" width="1.5" height="13"/>
            <ellipse cx="0.75" cy="-1" rx="9" ry="2.2"/>
            <ellipse cx="0.75" cy="-2.8" rx="6" ry="1.5"/>
          </g>
          <g fill="#2A1F18">
            <g transform="translate(50, 93)">
              <ellipse cx="5" cy="0" rx="5.5" ry="2"/>
              <rect x="0.5" y="0" width="1" height="5"/><rect x="2.5" y="0" width="1" height="5"/><rect x="6.5" y="0" width="1" height="5"/><rect x="8.5" y="0" width="1" height="5"/>
              <rect x="-1" y="-3" width="1.3" height="4"/>
              <ellipse cx="-0.4" cy="-4" rx="1.6" ry="1.1"/>
            </g>
            <g transform="translate(115, 96)">
              <ellipse cx="6" cy="0" rx="6.5" ry="2.4"/>
              <rect x="0.5" y="0" width="1.2" height="6"/><rect x="3" y="0" width="1.2" height="6"/><rect x="7.5" y="0" width="1.2" height="6"/><rect x="10" y="0" width="1.2" height="6"/>
              <rect x="-1.2" y="-4" width="1.6" height="5"/>
              <ellipse cx="-0.5" cy="-5" rx="1.9" ry="1.3"/>
            </g>
            <g transform="translate(188, 94)">
              <ellipse cx="5" cy="0" rx="4.8" ry="1.8"/>
              <rect x="0.5" y="0" width="0.9" height="5"/><rect x="2.5" y="0" width="0.9" height="5"/><rect x="6.2" y="0" width="0.9" height="5"/><rect x="8.2" y="0" width="0.9" height="5"/>
              <rect x="-1" y="-3" width="1.1" height="4"/>
              <ellipse cx="-0.4" cy="-3.8" rx="1.4" ry="0.95"/>
            </g>
          </g>
        </svg>
      );

    case "track-gorillas":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#A8B8B5"/>
          <rect width="280" height="28" fill="#C0CDCB"/>
          <path d="M 0 38 L 30 26 L 65 32 L 100 18 L 135 30 L 170 20 L 205 32 L 240 22 L 280 30 L 280 60 L 0 60 Z" fill="#6A7A78"/>
          <path d="M 0 50 Q 60 42 130 46 T 280 48 L 280 72 L 0 72 Z" fill="#4A5A55"/>
          <path d="M 0 68 L 15 58 L 35 64 L 55 53 L 80 62 L 105 51 L 130 58 L 155 53 L 180 62 L 205 55 L 230 62 L 255 53 L 280 60 L 280 85 L 0 85 Z" fill="#2A3A28"/>
          <g fill="#2A4528">
            <ellipse cx="20" cy="80" rx="22" ry="9"/>
            <ellipse cx="58" cy="83" rx="20" ry="8"/>
            <ellipse cx="220" cy="80" rx="18" ry="8"/>
            <ellipse cx="260" cy="83" rx="22" ry="9"/>
          </g>
          <g transform="translate(110, 55)">
            <path d="M 0 50 L 8 22 Q 18 13 30 13 Q 42 13 52 22 L 60 50 Z" fill="#1B3A5C"/>
            <ellipse cx="2" cy="36" rx="7" ry="11" fill="#1B3A5C"/>
            <ellipse cx="58" cy="36" rx="7" ry="11" fill="#1B3A5C"/>
            <ellipse cx="30" cy="28" rx="17" ry="5" fill="#5A5350" opacity="0.55"/>
            <ellipse cx="30" cy="10" rx="11" ry="10" fill="#1B3A5C"/>
            <ellipse cx="30" cy="6" rx="11" ry="3.5" fill="#0A0805"/>
            <ellipse cx="30" cy="13" rx="7" ry="6" fill="#3A2A22"/>
            <circle cx="26.5" cy="11" r="0.9" fill="#0A0805"/>
            <circle cx="33.5" cy="11" r="0.9" fill="#0A0805"/>
            <ellipse cx="28" cy="15.5" rx="0.7" ry="0.5" fill="#0A0805"/>
            <ellipse cx="32" cy="15.5" rx="0.7" ry="0.5" fill="#0A0805"/>
            <path d="M 27 18 Q 30 19.5 33 18" stroke="#0A0805" strokeWidth="0.5" fill="none" strokeLinecap="round"/>
          </g>
          <g fill="#3A5530">
            <ellipse cx="15" cy="100" rx="22" ry="9" transform="rotate(12 15 100)"/>
            <ellipse cx="263" cy="103" rx="22" ry="9" transform="rotate(-12 263 103)"/>
          </g>
        </svg>
      );

    case "skydive":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="65" fill="#8AC0D8"/>
          <rect width="280" height="22" fill="#A8D5E5"/>
          <rect width="280" height="8" fill="#C8E5F0"/>
          <g fill="white" opacity="0.85">
            <ellipse cx="25" cy="14" rx="10" ry="2.5"/>
            <ellipse cx="190" cy="18" rx="13" ry="3"/>
          </g>
          <circle cx="245" cy="22" r="9" fill="#F5DCA8" opacity="0.85"/>
          <rect y="65" width="280" height="45" fill="#3A98B0"/>
          <rect y="65" width="280" height="3" fill="#5DBAD0"/>
          <g fill="#D8B888" opacity="0.7">
            <path d="M 35 65 Q 50 60 80 62 Q 110 65 140 64 Q 170 63 200 65 L 200 70 Q 170 68 140 69 Q 110 70 80 67 Q 50 65 35 70 Z"/>
            <path d="M 215 65 Q 235 62 260 64 Q 275 65 280 65 L 280 70 Q 275 70 260 69 Q 235 67 215 70 Z"/>
          </g>
          <g transform="translate(140, 36)">
            <path d="M -32 0 Q -32 -22, 0 -26 Q 32 -22, 32 0 Z" fill="#C8404A"/>
            <path d="M -32 0 Q -32 -22, -22 -25 Q -22 -6, -16 0 Z" fill="#E8D0A8"/>
            <path d="M -16 0 Q -22 -6, -22 -25 Q -12 -27, -8 -27 Q -8 -8, -3 0 Z" fill="#C8404A"/>
            <path d="M -3 0 Q -8 -8, -8 -27 Q 0 -28, 8 -27 Q 8 -8, 3 0 Z" fill="#E8D0A8"/>
            <path d="M 3 0 Q 8 -8, 8 -27 Q 12 -27, 22 -25 Q 22 -6, 16 0 Z" fill="#C8404A"/>
            <path d="M 16 0 Q 22 -6, 22 -25 Q 32 -22, 32 0 Z" fill="#E8D0A8"/>
            <line x1="-28" y1="-2" x2="-6" y2="20" stroke="#1B3A5C" strokeWidth="0.5"/>
            <line x1="-14" y1="-1" x2="-4" y2="20" stroke="#1B3A5C" strokeWidth="0.5"/>
            <line x1="0" y1="-1" x2="0" y2="20" stroke="#1B3A5C" strokeWidth="0.5"/>
            <line x1="14" y1="-1" x2="4" y2="20" stroke="#1B3A5C" strokeWidth="0.5"/>
            <line x1="28" y1="-2" x2="6" y2="20" stroke="#1B3A5C" strokeWidth="0.5"/>
            <g transform="translate(0, 22)">
              <circle cx="0" cy="0" r="3.5" fill="#1B3A5C"/>
              <rect x="-3" y="2" width="6" height="9" rx="1" fill="#C8404A"/>
              <rect x="-5" y="3" width="2" height="6" fill="#1B3A5C" transform="rotate(-25 -4 6)"/>
              <rect x="3" y="3" width="2" height="6" fill="#1B3A5C" transform="rotate(25 4 6)"/>
              <rect x="-2" y="10" width="1.8" height="6" fill="#1B3A5C"/>
              <rect x="0.2" y="10" width="1.8" height="6" fill="#1B3A5C"/>
            </g>
          </g>
        </svg>
      );

    case "bungee-jump":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#A8C4D2"/>
          <rect width="280" height="42" fill="#B8D2DE"/>
          <rect width="280" height="15" fill="#C8DEE8"/>
          <path d="M 0 50 L 40 38 L 70 44 L 100 30 L 130 38 L 160 28 L 190 35 L 220 24 L 260 32 L 280 28 L 280 60 L 0 60 Z" fill="#6A8A75"/>
          <path d="M 0 55 L 30 50 L 60 54 L 95 46 L 130 52 L 165 44 L 200 50 L 240 42 L 280 48 L 280 65 L 0 65 Z" fill="#4A6A55"/>
          <path d="M -5 110 Q 40 80 90 76 L 200 76 Q 240 78 285 110 Z" fill="#3A5A45"/>
          <path d="M -5 110 Q 40 88 90 84 L 200 84 Q 240 86 285 110 Z" fill="#2A4A35"/>
          <rect x="0" y="55" width="280" height="6" fill="#4A3828"/>
          <rect x="0" y="58" width="280" height="3" fill="#3A2818"/>
          <g stroke="#3A2818" strokeWidth="0.7" fill="none">
            <path d="M 30 55 L 50 35 L 70 55"/>
            <line x1="40" y1="55" x2="50" y2="45"/>
            <line x1="60" y1="55" x2="50" y2="45"/>
            <path d="M 90 55 L 110 35 L 130 55"/>
            <line x1="100" y1="55" x2="110" y2="45"/>
            <line x1="120" y1="55" x2="110" y2="45"/>
            <path d="M 150 55 L 170 35 L 190 55"/>
            <line x1="160" y1="55" x2="170" y2="45"/>
            <line x1="180" y1="55" x2="170" y2="45"/>
            <path d="M 210 55 L 230 35 L 250 55"/>
            <line x1="220" y1="55" x2="230" y2="45"/>
            <line x1="240" y1="55" x2="230" y2="45"/>
          </g>
          <line x1="140" y1="58" x2="140" y2="82" stroke="#C84A4A" strokeWidth="1.2"/>
          <g transform="translate(140, 87)">
            <ellipse cx="0" cy="0" rx="3" ry="2.5" fill="#1B3A5C"/>
            <path d="M -6 1 L -3 -3 L 3 -3 L 6 1 L 5 4 L -5 4 Z" fill="#E8C868"/>
            <rect x="-3" y="3" width="6" height="8" rx="1" fill="#E89058"/>
            <rect x="-3" y="3" width="6" height="3" fill="#C8404A"/>
            <rect x="-5.5" y="2" width="2" height="5" fill="#1B3A5C" transform="rotate(35 -4.5 4.5)"/>
            <rect x="3.5" y="2" width="2" height="5" fill="#1B3A5C" transform="rotate(-35 4.5 4.5)"/>
            <rect x="-2.5" y="10" width="2" height="6" fill="#1B3A5C" transform="rotate(-15 -1.5 13)"/>
            <rect x="0.5" y="10" width="2" height="6" fill="#1B3A5C" transform="rotate(15 1.5 13)"/>
            <line x1="0" y1="-2" x2="0" y2="-5" stroke="#C84A4A" strokeWidth="1.2"/>
          </g>
        </svg>
      );

    case "greek-islands":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F8C8A0"/>
          <rect width="280" height="42" fill="#F0A080"/>
          <rect width="280" height="22" fill="#E88060"/>
          <circle cx="230" cy="32" r="11" fill="#F8E0C0" opacity="0.85"/>
          <circle cx="230" cy="32" r="7" fill="#FAEAC8"/>
          <rect y="58" width="280" height="52" fill="#3A6AA8"/>
          <path d="M 0 62 L 30 67 L 60 70 L 100 75 L 140 78 L 280 82 L 280 110 L 0 110 Z" fill="#5A4838"/>
          <path d="M 0 70 L 50 75 L 100 80 L 280 85 L 280 110 L 0 110 Z" fill="#7A5848"/>
          <g fill="white">
            <rect x="20" y="55" width="16" height="9"/><rect x="40" y="58" width="16" height="9"/><rect x="86" y="60" width="16" height="10"/><rect x="106" y="58" width="16" height="11"/><rect x="128" y="62" width="14" height="9"/><rect x="148" y="60" width="16" height="11"/>
            <rect x="30" y="68" width="20" height="12"/><rect x="54" y="70" width="18" height="11"/><rect x="80" y="72" width="18" height="11"/><rect x="102" y="70" width="18" height="13"/><rect x="126" y="72" width="18" height="11"/><rect x="148" y="73" width="16" height="10"/>
          </g>
          <g transform="translate(70, 55)">
            <rect x="-7" y="0" width="14" height="11" fill="white"/>
            <path d="M -7 0 Q -7 -7 0 -7 Q 7 -7 7 0 Z" fill="#3A6AA8"/>
            <ellipse cx="0" cy="0" rx="7" ry="1.5" fill="#3A6AA8"/>
            <rect x="-0.5" y="-10" width="1" height="3" fill="#1B3A5C"/>
            <rect x="-2" y="4" width="4" height="7" fill="#1B3A5C"/>
          </g>
          <g transform="translate(115, 58)">
            <rect x="-5" y="0" width="10" height="8" fill="white"/>
            <path d="M -5 0 Q -5 -5 0 -5 Q 5 -5 5 0 Z" fill="#3A6AA8"/>
            <ellipse cx="0" cy="0" rx="5" ry="1.2" fill="#3A6AA8"/>
            <rect x="-0.4" y="-7" width="0.8" height="2" fill="#1B3A5C"/>
          </g>
          <g fill="#3A6AA8">
            <rect x="23" y="58" width="2" height="2.5"/><rect x="28" y="58" width="2" height="2.5"/><rect x="43" y="62" width="2" height="2.5"/><rect x="48" y="62" width="2" height="2.5"/><rect x="90" y="63" width="2" height="2.5"/><rect x="95" y="63" width="2" height="2.5"/><rect x="109" y="61" width="2" height="2.5"/><rect x="115" y="61" width="2" height="2.5"/><rect x="151" y="63" width="2" height="2.5"/><rect x="156" y="63" width="2" height="2.5"/>
          </g>
        </svg>
      );

    case "tokyo-2am":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#0A0F1F"/>
          <rect x="0" y="0" width="280" height="40" fill="#15182A"/>
          <polygon points="0,15 80,40 80,110 0,110" fill="#1A1825"/>
          <polygon points="280,15 200,40 200,110 280,110" fill="#1A1825"/>
          <rect x="80" y="0" width="120" height="42" fill="#0A0F1F"/>
          <rect x="4" y="22" width="14" height="48" fill="#3A0F25"/>
          <rect x="4" y="22" width="14" height="48" fill="none" stroke="#F84088" strokeWidth="0.6"/>
          <rect x="4" y="22" width="14" height="48" fill="#F84088" opacity="0.15"/>
          <g fill="#FAB0D0">
            <rect x="8" y="28" width="6" height="3"/><rect x="8" y="34" width="6" height="3"/><rect x="8" y="40" width="6" height="3"/><rect x="8" y="46" width="6" height="3"/><rect x="8" y="52" width="6" height="3"/><rect x="8" y="58" width="6" height="3"/><rect x="8" y="64" width="6" height="3"/>
          </g>
          <rect x="22" y="38" width="32" height="7" fill="#1F1F0F"/>
          <rect x="22" y="38" width="32" height="7" fill="none" stroke="#F8E040" strokeWidth="0.6"/>
          <rect x="22" y="38" width="32" height="7" fill="#F8E040" opacity="0.25"/>
          <rect x="22" y="50" width="32" height="22" fill="#0F2A2A"/>
          <rect x="22" y="50" width="32" height="22" fill="none" stroke="#40E0E8" strokeWidth="0.6"/>
          <g fill="#80F0F8">
            <rect x="26" y="54" width="6" height="3"/><rect x="34" y="54" width="6" height="3"/><rect x="42" y="54" width="6" height="3"/><rect x="26" y="60" width="4" height="3"/><rect x="32" y="60" width="8" height="3"/><rect x="42" y="60" width="6" height="3"/>
          </g>
          <rect x="60" y="32" width="14" height="50" fill="#2A0F0F"/>
          <rect x="60" y="32" width="14" height="50" fill="none" stroke="#F84030" strokeWidth="0.6"/>
          <rect x="60" y="32" width="14" height="50" fill="#F84030" opacity="0.18"/>
          <g fill="#F8A088">
            <rect x="64" y="38" width="6" height="3"/><rect x="64" y="44" width="6" height="3"/><rect x="64" y="50" width="6" height="3"/><rect x="64" y="56" width="6" height="3"/><rect x="64" y="62" width="6" height="3"/><rect x="64" y="68" width="6" height="3"/><rect x="64" y="74" width="6" height="3"/>
          </g>
          <rect x="208" y="22" width="40" height="8" fill="#0F2A2A"/>
          <rect x="208" y="22" width="40" height="8" fill="none" stroke="#40E0E8" strokeWidth="0.6"/>
          <rect x="208" y="22" width="40" height="8" fill="#40E0E8" opacity="0.3"/>
          <rect x="214" y="35" width="14" height="45" fill="#3A0F25"/>
          <rect x="214" y="35" width="14" height="45" fill="none" stroke="#F84088" strokeWidth="0.6"/>
          <rect x="214" y="35" width="14" height="45" fill="#F84088" opacity="0.15"/>
          <rect x="248" y="32" width="16" height="48" fill="#2A1F0F"/>
          <rect x="248" y="32" width="16" height="48" fill="none" stroke="#F88040" strokeWidth="0.6"/>
          <rect x="248" y="32" width="16" height="48" fill="#F88040" opacity="0.2"/>
          <rect y="90" width="280" height="20" fill="#1A1825"/>
          <rect y="92" width="280" height="18" fill="#15182A"/>
          <g opacity="0.35">
            <ellipse cx="11" cy="94" rx="10" ry="2" fill="#F84088"/>
            <ellipse cx="38" cy="96" rx="14" ry="2.5" fill="#F8E040"/>
            <ellipse cx="221" cy="94" rx="12" ry="2" fill="#F84088"/>
            <ellipse cx="256" cy="96" rx="14" ry="2.5" fill="#F88040"/>
          </g>
          <g transform="translate(140, 95)">
            <circle cx="0" cy="-9" r="2.3" fill="#1B3A5C"/>
            <rect x="-2.5" y="-7" width="5" height="7" fill="#2A2018"/>
            <rect x="-2" y="0" width="1.8" height="8" fill="#1B3A5C"/>
            <rect x="0.2" y="0" width="1.8" height="8" fill="#1B3A5C"/>
          </g>
        </svg>
      );

    case "train-across-america":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0C098"/>
          <rect width="280" height="40" fill="#E8A878"/>
          <rect width="280" height="20" fill="#D89868"/>
          <circle cx="225" cy="22" r="9" fill="#F8DCC0" opacity="0.85"/>
          <path d="M 0 55 L 30 42 L 60 50 L 100 35 L 140 48 L 180 38 L 220 50 L 260 38 L 280 45 L 280 70 L 0 70 Z" fill="#5A6A7A" opacity="0.7"/>
          <path d="M 60 50 L 100 35 L 115 45 L 105 38 Z" fill="white" opacity="0.6"/>
          <path d="M 180 38 L 195 50 L 205 42 L 190 40 Z" fill="white" opacity="0.6"/>
          <path d="M 0 65 L 40 58 L 80 62 L 130 56 L 180 60 L 230 55 L 280 60 L 280 78 L 0 78 Z" fill="#B8A078"/>
          <path d="M 0 72 L 50 68 L 100 70 L 160 66 L 220 70 L 280 68 L 280 85 L 0 85 Z" fill="#9A8458"/>
          <g fill="#5A7838" opacity="0.6">
            <ellipse cx="30" cy="80" rx="4" ry="2"/>
            <ellipse cx="55" cy="83" rx="3" ry="1.5"/>
            <ellipse cx="180" cy="80" rx="4" ry="2"/>
            <ellipse cx="240" cy="83" rx="3" ry="1.5"/>
          </g>
          <rect y="85" width="280" height="8" fill="#5A4838"/>
          <line x1="0" y1="88" x2="280" y2="88" stroke="#A89888" strokeWidth="0.6"/>
          <line x1="0" y1="91" x2="280" y2="91" stroke="#A89888" strokeWidth="0.6"/>
          <g fill="#3A2818">
            <rect x="5" y="87" width="2" height="5"/>
            <rect x="18" y="87" width="2" height="5"/>
            <rect x="31" y="87" width="2" height="5"/>
            <rect x="252" y="87" width="2" height="5"/>
            <rect x="265" y="87" width="2" height="5"/>
            <rect x="278" y="87" width="2" height="5"/>
          </g>
          <g transform="translate(20, 76)">
            <ellipse cx="-5" cy="-12" rx="9" ry="3" fill="#E8E0D8" opacity="0.75"/>
            <ellipse cx="-14" cy="-16" rx="7" ry="3" fill="#D8D0C8" opacity="0.65"/>
            <ellipse cx="-22" cy="-19" rx="5" ry="2.5" fill="#C8C0B8" opacity="0.55"/>
            <rect x="0" y="0" width="32" height="10" fill="#2A3848"/>
            <rect x="0" y="-3" width="32" height="3" fill="#3A4858"/>
            <polygon points="32,0 42,0 42,5 38,10 32,10" fill="#1F2A38"/>
            <rect x="3" y="-7" width="4" height="4" fill="#1F2A38"/>
            <rect x="38" y="2" width="3" height="3" fill="#F8E040"/>
            <g fill="#8AC8E8">
              <rect x="2" y="3" width="2" height="3"/>
              <rect x="8" y="3" width="2" height="3"/>
              <rect x="14" y="3" width="2" height="3"/>
              <rect x="20" y="3" width="2" height="3"/>
              <rect x="26" y="3" width="2" height="3"/>
            </g>
            <circle cx="6" cy="11" r="2.2" fill="#1F1A14"/>
            <circle cx="16" cy="11" r="2.2" fill="#1F1A14"/>
            <circle cx="26" cy="11" r="2.2" fill="#1F1A14"/>
            <circle cx="36" cy="11" r="2.2" fill="#1F1A14"/>
          </g>
          <g fill="#A8B0B8">
            <rect x="70" y="78" width="38" height="8"/>
            <rect x="112" y="78" width="38" height="8"/>
            <rect x="154" y="78" width="38" height="8"/>
            <rect x="196" y="78" width="38" height="8"/>
          </g>
          <g fill="#888E96">
            <rect x="70" y="76" width="38" height="2"/>
            <rect x="112" y="76" width="38" height="2"/>
            <rect x="154" y="76" width="38" height="2"/>
            <rect x="196" y="76" width="38" height="2"/>
          </g>
          <g fill="#3A4858">
            <rect x="70" y="84" width="38" height="2"/>
            <rect x="112" y="84" width="38" height="2"/>
            <rect x="154" y="84" width="38" height="2"/>
            <rect x="196" y="84" width="38" height="2"/>
          </g>
          <g fill="#8AC8E8">
            <rect x="72" y="80" width="2.5" height="2.5"/><rect x="77" y="80" width="2.5" height="2.5"/><rect x="82" y="80" width="2.5" height="2.5"/><rect x="87" y="80" width="2.5" height="2.5"/><rect x="92" y="80" width="2.5" height="2.5"/><rect x="97" y="80" width="2.5" height="2.5"/><rect x="102" y="80" width="2.5" height="2.5"/>
            <rect x="114" y="80" width="2.5" height="2.5"/><rect x="119" y="80" width="2.5" height="2.5"/><rect x="124" y="80" width="2.5" height="2.5"/><rect x="129" y="80" width="2.5" height="2.5"/><rect x="134" y="80" width="2.5" height="2.5"/><rect x="139" y="80" width="2.5" height="2.5"/><rect x="144" y="80" width="2.5" height="2.5"/>
            <rect x="156" y="80" width="2.5" height="2.5"/><rect x="161" y="80" width="2.5" height="2.5"/><rect x="166" y="80" width="2.5" height="2.5"/><rect x="171" y="80" width="2.5" height="2.5"/><rect x="176" y="80" width="2.5" height="2.5"/><rect x="181" y="80" width="2.5" height="2.5"/><rect x="186" y="80" width="2.5" height="2.5"/>
            <rect x="198" y="80" width="2.5" height="2.5"/><rect x="203" y="80" width="2.5" height="2.5"/><rect x="208" y="80" width="2.5" height="2.5"/><rect x="213" y="80" width="2.5" height="2.5"/><rect x="218" y="80" width="2.5" height="2.5"/><rect x="223" y="80" width="2.5" height="2.5"/><rect x="228" y="80" width="2.5" height="2.5"/>
          </g>
          <g fill="#1F1A14">
            <circle cx="76" cy="87.5" r="1.6"/><circle cx="102" cy="87.5" r="1.6"/>
            <circle cx="118" cy="87.5" r="1.6"/><circle cx="144" cy="87.5" r="1.6"/>
            <circle cx="160" cy="87.5" r="1.6"/><circle cx="186" cy="87.5" r="1.6"/>
            <circle cx="202" cy="87.5" r="1.6"/><circle cx="228" cy="87.5" r="1.6"/>
            <rect x="64" y="82" width="6" height="2"/>
            <rect x="108" y="82" width="4" height="2"/>
            <rect x="150" y="82" width="4" height="2"/>
            <rect x="192" y="82" width="4" height="2"/>
          </g>
        </svg>
      );

    case "napa-valley":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F5D5A0"/>
          <rect width="280" height="40" fill="#F0C088"/>
          <rect width="280" height="22" fill="#E8A868"/>
          <circle cx="225" cy="28" r="11" fill="#F8E0B8" opacity="0.9"/>
          <circle cx="225" cy="28" r="7" fill="#FAEAC8"/>
          <g fill="white" opacity="0.5">
            <ellipse cx="60" cy="22" rx="20" ry="2.5"/>
            <ellipse cx="120" cy="18" rx="15" ry="2"/>
          </g>
          <path d="M 0 60 Q 30 45 80 50 T 160 48 T 230 52 T 280 50 L 280 75 L 0 75 Z" fill="#C8A050"/>
          <path d="M 0 65 Q 50 55 100 58 T 200 60 T 280 62 L 280 78 L 0 78 Z" fill="#A88838"/>
          <g fill="#5A4828" opacity="0.55">
            <ellipse cx="195" cy="50" rx="5" ry="6"/>
            <ellipse cx="190" cy="48" rx="3" ry="5"/>
            <ellipse cx="200" cy="48" rx="3" ry="4"/>
            <rect x="194.5" y="55" width="1" height="3"/>
          </g>
          <path d="M 0 70 L 280 78 L 280 110 L 0 110 Z" fill="#4A6838"/>
          <g stroke="#2A4218" strokeWidth="1.2" opacity="0.85">
            <line x1="0" y1="76" x2="280" y2="84"/>
            <line x1="0" y1="80" x2="280" y2="88"/>
            <line x1="0" y1="84" x2="280" y2="92"/>
            <line x1="0" y1="88" x2="280" y2="96"/>
            <line x1="0" y1="93" x2="280" y2="101"/>
            <line x1="0" y1="98" x2="280" y2="106"/>
            <line x1="0" y1="103" x2="280" y2="110"/>
          </g>
          <g stroke="#3A5828" strokeWidth="0.5" opacity="0.6">
            <line x1="0" y1="74" x2="280" y2="82"/>
            <line x1="0" y1="78" x2="280" y2="86"/>
            <line x1="0" y1="82" x2="280" y2="90"/>
            <line x1="0" y1="86" x2="280" y2="94"/>
            <line x1="0" y1="91" x2="280" y2="99"/>
            <line x1="0" y1="96" x2="280" y2="104"/>
            <line x1="0" y1="101" x2="280" y2="108"/>
          </g>
          <g fill="#2A4218" opacity="0.7">
            <circle cx="25" cy="76" r="0.6"/><circle cx="60" cy="80" r="0.6"/><circle cx="95" cy="84" r="0.6"/><circle cx="130" cy="88" r="0.6"/><circle cx="170" cy="92" r="0.6"/><circle cx="210" cy="96" r="0.6"/><circle cx="245" cy="100" r="0.6"/>
            <circle cx="40" cy="84" r="0.6"/><circle cx="80" cy="88" r="0.6"/><circle cx="115" cy="92" r="0.6"/><circle cx="155" cy="96" r="0.6"/><circle cx="195" cy="100" r="0.6"/><circle cx="235" cy="104" r="0.6"/>
          </g>
          <g transform="translate(45, 62)">
            <ellipse cx="0" cy="0" rx="11" ry="9" fill="#2A4218"/>
            <ellipse cx="-6" cy="-3" rx="5" ry="4" fill="#3A5828"/>
            <ellipse cx="5" cy="-4" rx="6" ry="5" fill="#3A5828"/>
            <ellipse cx="3" cy="3" rx="5" ry="4" fill="#2A4218"/>
            <rect x="-1" y="6" width="2" height="7" fill="#3A2820"/>
            <rect x="-1.5" y="9" width="3" height="2" fill="#3A2820"/>
          </g>
        </svg>
      );

    case "grand-canyon":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F5C098"/>
          <rect width="280" height="40" fill="#F0A878"/>
          <rect width="280" height="22" fill="#E89058"/>
          <circle cx="50" cy="22" r="9" fill="#F8E0B8" opacity="0.75"/>
          <path d="M 0 50 L 30 42 L 60 48 L 100 38 L 140 46 L 180 40 L 220 48 L 260 42 L 280 47 L 280 60 L 0 60 Z" fill="#D88858" opacity="0.7"/>
          <path d="M 0 55 L 25 50 L 60 55 L 100 48 L 140 54 L 180 50 L 220 55 L 260 50 L 280 55 L 280 70 L 0 70 Z" fill="#C87848"/>
          <path d="M 0 65 L 30 58 L 70 64 L 110 58 L 150 64 L 190 58 L 230 64 L 280 60 L 280 78 L 0 78 Z" fill="#B86838"/>
          <path d="M 0 74 L 35 68 L 75 74 L 115 68 L 155 74 L 195 68 L 235 74 L 280 70 L 280 88 L 0 88 Z" fill="#A85828"/>
          <path d="M 0 83 L 40 78 L 80 83 L 120 78 L 160 83 L 200 78 L 240 83 L 280 80 L 280 95 L 0 95 Z" fill="#984818"/>
          <path d="M 0 92 L 50 89 L 100 93 L 150 89 L 200 93 L 250 89 L 280 92 L 280 110 L 0 110 Z" fill="#783818"/>
          <g stroke="#3A2818" strokeWidth="0.4" opacity="0.5">
            <line x1="20" y1="64" x2="22" y2="68"/>
            <line x1="50" y1="62" x2="48" y2="67"/>
            <line x1="120" y1="62" x2="118" y2="67"/>
            <line x1="180" y1="62" x2="182" y2="67"/>
            <line x1="220" y1="62" x2="218" y2="67"/>
            <line x1="40" y1="74" x2="42" y2="78"/>
            <line x1="90" y1="72" x2="88" y2="77"/>
            <line x1="160" y1="72" x2="162" y2="77"/>
            <line x1="240" y1="72" x2="238" y2="77"/>
            <line x1="60" y1="82" x2="62" y2="87"/>
            <line x1="180" y1="82" x2="178" y2="87"/>
          </g>
          <path d="M 0 105 Q 60 100 130 102 Q 180 103 280 100" fill="none" stroke="#4A88A8" strokeWidth="2.5" strokeLinecap="round"/>
          <path d="M 0 105 Q 60 100 130 102 Q 180 103 280 100" fill="none" stroke="#7AB8D8" strokeWidth="1.2" strokeLinecap="round"/>
          <g fill="#7AB8D8" opacity="0.5">
            <ellipse cx="40" cy="105" rx="5" ry="0.4"/>
            <ellipse cx="120" cy="103" rx="6" ry="0.4"/>
            <ellipse cx="210" cy="102" rx="5" ry="0.4"/>
          </g>
        </svg>
      );

    case "route-66":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#A8D0E8"/>
          <rect width="280" height="40" fill="#B8DCEF"/>
          <rect width="280" height="20" fill="#C8E5F5"/>
          <g fill="white" opacity="0.65">
            <ellipse cx="50" cy="18" rx="22" ry="3"/>
            <ellipse cx="45" cy="15" rx="13" ry="2.5"/>
            <ellipse cx="200" cy="22" rx="25" ry="3.5"/>
            <ellipse cx="195" cy="18" rx="15" ry="2.8"/>
            <ellipse cx="135" cy="12" rx="18" ry="2"/>
          </g>
          <path d="M 0 60 L 35 50 L 60 55 L 75 45 L 95 50 L 110 42 L 130 48 L 280 55 L 280 70 L 0 70 Z" fill="#B86838" opacity="0.7"/>
          <path d="M 200 55 L 225 45 L 245 50 L 260 42 L 280 48 L 280 65 L 200 65 Z" fill="#A85828" opacity="0.8"/>
          <path d="M 0 65 L 60 58 L 120 62 L 180 56 L 240 60 L 280 58 L 280 75 L 0 75 Z" fill="#D8A878"/>
          <path d="M 0 73 L 60 68 L 120 72 L 180 66 L 240 70 L 280 68 L 280 85 L 0 85 Z" fill="#C89868"/>
          <g stroke="#5A4838" strokeWidth="0.6">
            <path d="M 36 66 L 36 60 M 34 63 L 38 63"/>
            <path d="M 246 68 L 246 62 M 244 65 L 248 65"/>
          </g>
          <path d="M 0 85 L 280 85 L 220 110 L 60 110 Z" fill="#3A2820"/>
          <path d="M 0 85 L 280 85 L 220 110 L 60 110 Z" fill="#1F1A14" opacity="0.5"/>
          <g fill="#F8E040">
            <polygon points="135,88 145,88 144,92 136,92"/>
            <polygon points="132,95 144,95 143,99 133,99"/>
            <polygon points="128,103 142,103 141,108 129,108"/>
          </g>
          <g transform="translate(40, 65)">
            <rect x="0" y="0" width="1.2" height="22" fill="#3A2820"/>
            <g transform="translate(0.6, 0)">
              <path d="M -14 -10 L 14 -10 L 16 -2 L 14 6 L -14 6 L -16 -2 Z" fill="white"/>
              <path d="M -14 -10 L 14 -10 L 16 -2 L 14 6 L -14 6 L -16 -2 Z" fill="none" stroke="#1F1A14" strokeWidth="0.5"/>
              <text x="0" y="-7" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="1.8" fill="#1F1A14" fontWeight="500">U.S.</text>
              <text x="0" y="-3" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="4" fill="#1F1A14" fontWeight="700" letterSpacing="0.3">ROUTE</text>
              <text x="0" y="3.5" textAnchor="middle" fontFamily="DM Sans, sans-serif" fontSize="6.5" fill="#1F1A14" fontWeight="700">66</text>
            </g>
          </g>
        </svg>
      );

    case "yellowstone":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#A8C0D8"/>
          <rect width="280" height="40" fill="#BCD0E0"/>
          <rect width="280" height="20" fill="#CCDEEC"/>
          <g fill="white" opacity="0.6">
            <ellipse cx="40" cy="16" rx="18" ry="2.5"/>
            <ellipse cx="36" cy="13" rx="11" ry="2"/>
          </g>
          <path d="M 0 50 L 30 38 L 60 45 L 100 32 L 140 42 L 180 35 L 220 45 L 260 38 L 280 42 L 280 60 L 0 60 Z" fill="#5A6A78" opacity="0.65"/>
          <path d="M 100 32 L 108 40 L 115 36 L 105 35 Z" fill="white"/>
          <path d="M 180 35 L 188 44 L 195 38 L 185 37 Z" fill="white"/>
          <path d="M 0 60 L 40 55 L 80 60 L 130 53 L 180 58 L 230 54 L 280 58 L 280 75 L 0 75 Z" fill="#4A6038"/>
          <g fill="#2A4020">
            <polygon points="20,60 23,48 26,60"/>
            <polygon points="35,62 38,50 41,62"/>
            <polygon points="50,60 53,48 56,60"/>
            <polygon points="225,60 228,49 231,60"/>
            <polygon points="240,62 243,50 246,62"/>
            <polygon points="255,60 258,48 261,60"/>
          </g>
          <path d="M 0 75 L 50 72 L 100 76 L 150 72 L 200 75 L 280 73 L 280 90 L 0 90 Z" fill="#8A7858"/>
          <g transform="translate(140, 75)">
            <ellipse cx="0" cy="20" rx="22" ry="3" fill="#5A4838"/>
            <ellipse cx="0" cy="20" rx="18" ry="2.2" fill="#7A6848"/>
            <ellipse cx="0" cy="20" rx="14" ry="1.6" fill="#9A8868"/>
            <ellipse cx="0" cy="0" rx="8" ry="3" fill="#7A8A98" opacity="0.85"/>
            <ellipse cx="0" cy="-5" rx="10" ry="4" fill="white" opacity="0.85"/>
            <ellipse cx="-3" cy="-12" rx="9" ry="5" fill="white" opacity="0.9"/>
            <ellipse cx="3" cy="-22" rx="11" ry="6" fill="white" opacity="0.9"/>
            <ellipse cx="-2" cy="-32" rx="13" ry="7" fill="#E8E0D8" opacity="0.85"/>
            <ellipse cx="3" cy="-44" rx="15" ry="8" fill="#D8D0C8" opacity="0.8"/>
            <ellipse cx="-2" cy="-55" rx="17" ry="9" fill="#C8C0B8" opacity="0.7"/>
            <ellipse cx="2" cy="-68" rx="20" ry="11" fill="#B8B0A8" opacity="0.5"/>
            <ellipse cx="-10" cy="-5" rx="4" ry="2.5" fill="white" opacity="0.7"/>
            <ellipse cx="10" cy="-8" rx="5" ry="3" fill="white" opacity="0.7"/>
          </g>
          <rect y="90" width="280" height="20" fill="#6A5848"/>
          <g fill="#3A2820" opacity="0.6">
            <ellipse cx="30" cy="98" rx="2" ry="0.5"/>
            <ellipse cx="80" cy="100" rx="2" ry="0.5"/>
            <ellipse cx="200" cy="98" rx="2" ry="0.5"/>
            <ellipse cx="250" cy="102" rx="2" ry="0.5"/>
          </g>
        </svg>
      );

    case "mount-rushmore":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#A8C0E0"/>
          <rect width="280" height="40" fill="#B8CCE8"/>
          <rect width="280" height="20" fill="#C8DCF0"/>
          <g fill="white" opacity="0.55">
            <ellipse cx="55" cy="20" rx="22" ry="3"/>
            <ellipse cx="225" cy="16" rx="20" ry="2.5"/>
          </g>
          <path d="M 0 75 L 30 60 L 60 55 L 100 35 L 140 30 L 180 38 L 220 55 L 260 65 L 280 72 L 280 110 L 0 110 Z" fill="#9A8060"/>
          <path d="M 0 78 L 30 65 L 60 62 L 100 45 L 140 42 L 180 48 L 220 62 L 260 70 L 280 76 L 280 110 L 0 110 Z" fill="#7A6048" opacity="0.7"/>
          <g transform="translate(40, 52)">
            <ellipse cx="0" cy="0" rx="13" ry="14" fill="#8A7050"/>
            <path d="M -10 -8 Q -8 -14 -2 -14 Q 3 -14 5 -10 L 6 -3 L -8 -3 Z" fill="#6A5038"/>
            <ellipse cx="-3" cy="-2" rx="3" ry="2" fill="#5A4030" opacity="0.6"/>
            <ellipse cx="3" cy="-2" rx="3" ry="2" fill="#5A4030" opacity="0.6"/>
            <ellipse cx="0" cy="3" rx="1.5" ry="1.2" fill="#5A4030"/>
            <path d="M -3 8 Q 0 10 3 8" fill="none" stroke="#5A4030" strokeWidth="0.7"/>
            <ellipse cx="0" cy="14" rx="6" ry="2" fill="#5A4030" opacity="0.4"/>
          </g>
          <g transform="translate(95, 45)">
            <ellipse cx="0" cy="0" rx="14" ry="16" fill="#8A7050"/>
            <ellipse cx="-1" cy="-9" rx="9" ry="4" fill="#5A4030"/>
            <ellipse cx="-1" cy="-11" rx="6" ry="3" fill="#5A4030"/>
            <ellipse cx="-3" cy="-3" rx="2.5" ry="1.5" fill="#5A4030" opacity="0.6"/>
            <ellipse cx="3" cy="-3" rx="2.5" ry="1.5" fill="#5A4030" opacity="0.6"/>
            <path d="M -4 8 Q 0 11 4 8" fill="none" stroke="#5A4030" strokeWidth="0.6"/>
            <ellipse cx="-7" cy="0" rx="2" ry="6" fill="#6A5038" opacity="0.6"/>
          </g>
          <g transform="translate(155, 42)">
            <ellipse cx="0" cy="0" rx="13" ry="15" fill="#8A7050"/>
            <path d="M -8 -10 L 8 -10 L 6 -3 L -6 -3 Z" fill="#5A4030"/>
            <ellipse cx="-3" cy="-1" rx="2" ry="1.5" fill="#5A4030" opacity="0.6"/>
            <ellipse cx="3" cy="-1" rx="2" ry="1.5" fill="#5A4030" opacity="0.6"/>
            <ellipse cx="0" cy="4" rx="1.4" ry="1" fill="#5A4030"/>
            <path d="M -3 8 Q 0 10 3 8" fill="none" stroke="#5A4030" strokeWidth="0.6"/>
            <ellipse cx="-7" cy="0" rx="2" ry="5" fill="#6A5038" opacity="0.5"/>
          </g>
          <g transform="translate(215, 52)">
            <ellipse cx="0" cy="0" rx="13" ry="14" fill="#8A7050"/>
            <path d="M -9 -10 Q -3 -14 5 -12 L 6 -3 L -7 -3 Z" fill="#5A4030"/>
            <ellipse cx="-3" cy="-1" rx="2" ry="1.5" fill="#5A4030" opacity="0.6"/>
            <ellipse cx="3" cy="-1" rx="2" ry="1.5" fill="#5A4030" opacity="0.6"/>
            <path d="M -5 6 Q 0 9 5 6" fill="#5A4030"/>
            <path d="M -5 6 Q 0 12 5 6 Q 3 14 -3 14 Z" fill="#6A5038"/>
            <ellipse cx="-7" cy="-1" rx="2" ry="5" fill="#6A5038" opacity="0.5"/>
          </g>
          <g stroke="#5A4030" strokeWidth="0.4" opacity="0.5">
            <line x1="20" y1="68" x2="25" y2="72"/>
            <line x1="70" y1="60" x2="73" y2="64"/>
            <line x1="130" y1="58" x2="133" y2="62"/>
            <line x1="185" y1="56" x2="188" y2="60"/>
            <line x1="250" y1="68" x2="253" y2="72"/>
          </g>
          <rect y="88" width="280" height="22" fill="#2A4218"/>
          <g fill="#1A3010">
            <polygon points="20,88 24,72 28,88"/>
            <polygon points="35,90 39,75 43,90"/>
            <polygon points="240,88 244,73 248,88"/>
            <polygon points="255,90 259,75 263,90"/>
            <polygon points="125,88 128,78 131,88"/>
            <polygon points="180,88 183,77 186,88"/>
          </g>
        </svg>
      );

    case "hawaii-volcano":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#3A1828"/>
          <rect width="280" height="55" fill="#5A2030"/>
          <rect width="280" height="30" fill="#7A3030"/>
          <rect width="280" height="15" fill="#A04030"/>
          <g fill="#1F0A0A" opacity="0.5">
            <ellipse cx="50" cy="20" rx="25" ry="3"/>
            <ellipse cx="220" cy="14" rx="30" ry="2.5"/>
            <ellipse cx="140" cy="10" rx="20" ry="2"/>
          </g>
          <path d="M 0 55 L 30 48 L 70 52 L 130 35 L 170 42 L 220 48 L 280 50 L 280 75 L 0 75 Z" fill="#1F0F18"/>
          <path d="M 0 60 L 40 56 L 90 60 L 150 50 L 200 56 L 280 60 L 280 80 L 0 80 Z" fill="#0F0508"/>
          <g transform="translate(150, 50)" fill="#F84030">
            <ellipse cx="0" cy="-25" rx="6" ry="3"/>
            <ellipse cx="-3" cy="-30" rx="3" ry="2"/>
            <ellipse cx="2" cy="-32" rx="4" ry="2"/>
            <ellipse cx="-1" cy="-37" rx="2.5" ry="1.5"/>
          </g>
          <g transform="translate(150, 50)" fill="#F88040" opacity="0.85">
            <ellipse cx="0" cy="-25" rx="4" ry="2"/>
            <ellipse cx="-3" cy="-30" rx="2" ry="1.4"/>
            <ellipse cx="2" cy="-32" rx="2.5" ry="1.3"/>
          </g>
          <path d="M 145 50 Q 142 60 138 70 Q 134 80 130 90 Q 128 95 130 100" fill="none" stroke="#F84030" strokeWidth="2.5" strokeLinecap="round" opacity="0.9"/>
          <path d="M 152 50 Q 156 60 162 70 Q 168 78 175 84 Q 180 88 182 92" fill="none" stroke="#F84030" strokeWidth="2.2" strokeLinecap="round" opacity="0.9"/>
          <path d="M 148 50 Q 146 60 144 72 Q 142 84 144 95" fill="none" stroke="#F88040" strokeWidth="1.4" strokeLinecap="round" opacity="0.8"/>
          <path d="M 154 50 Q 158 62 166 76 Q 172 84 178 90" fill="none" stroke="#F88040" strokeWidth="1.2" strokeLinecap="round" opacity="0.8"/>
          <rect y="75" width="280" height="35" fill="#0A1828"/>
          <rect y="78" width="280" height="32" fill="#1A2838"/>
          <g fill="white" opacity="0.7">
            <ellipse cx="125" cy="92" rx="20" ry="4"/>
            <ellipse cx="180" cy="90" rx="22" ry="4"/>
            <ellipse cx="135" cy="84" rx="18" ry="3"/>
            <ellipse cx="170" cy="82" rx="15" ry="2.5"/>
          </g>
          <g fill="#D8D0C8" opacity="0.55">
            <ellipse cx="148" cy="78" rx="12" ry="3"/>
            <ellipse cx="160" cy="74" rx="10" ry="2.5"/>
            <ellipse cx="155" cy="68" rx="8" ry="2"/>
            <ellipse cx="170" cy="62" rx="6" ry="2"/>
            <ellipse cx="148" cy="56" rx="5" ry="1.5"/>
          </g>
          <g fill="#F88040" opacity="0.6">
            <ellipse cx="135" cy="98" rx="15" ry="1"/>
            <ellipse cx="180" cy="97" rx="18" ry="0.8"/>
          </g>
          <g stroke="white" strokeWidth="0.4" opacity="0.5">
            <line x1="20" y1="92" x2="50" y2="92"/>
            <line x1="210" y1="94" x2="240" y2="94"/>
            <line x1="40" y1="100" x2="80" y2="100"/>
            <line x1="200" y1="100" x2="240" y2="100"/>
            <line x1="0" y1="105" x2="280" y2="105"/>
          </g>
          <g transform="translate(40, 75)" fill="#1F1A14">
            <rect x="-0.8" y="0" width="1.5" height="-18"/>
            <g fill="#3A5030">
              <ellipse cx="0" cy="-18" rx="6" ry="1.2" transform="rotate(-25 0 -18)"/>
              <ellipse cx="0" cy="-18" rx="6" ry="1.2" transform="rotate(25 0 -18)"/>
              <ellipse cx="0" cy="-19" rx="5" ry="1" transform="rotate(-55 0 -19)"/>
              <ellipse cx="0" cy="-19" rx="5" ry="1" transform="rotate(55 0 -19)"/>
              <ellipse cx="0" cy="-17" rx="6" ry="1.2" transform="rotate(0 0 -17)"/>
            </g>
          </g>
        </svg>
      );

    case "mardi-gras":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#3A2848"/>
          <rect width="280" height="55" fill="#4A3058"/>
          <rect width="280" height="30" fill="#5A3868"/>
          <g fill="#FAE0A0" opacity="0.75">
            <circle cx="40" cy="14" r="0.6"/>
            <circle cx="80" cy="10" r="0.5"/>
            <circle cx="130" cy="12" r="0.6"/>
            <circle cx="200" cy="9" r="0.5"/>
            <circle cx="245" cy="14" r="0.5"/>
            <circle cx="170" cy="18" r="0.4"/>
            <circle cx="60" cy="22" r="0.4"/>
          </g>
          <rect x="0" y="28" width="280" height="55" fill="#9A7858"/>
          <rect x="0" y="28" width="280" height="3" fill="#7A5838"/>
          <g fill="#7A5838">
            <rect x="40" y="32" width="3" height="50"/>
            <rect x="100" y="32" width="3" height="50"/>
            <rect x="180" y="32" width="3" height="50"/>
            <rect x="240" y="32" width="3" height="50"/>
          </g>
          <g fill="#5A3858">
            <rect x="15" y="38" width="18" height="20" rx="1"/>
            <rect x="50" y="38" width="18" height="20" rx="1"/>
            <rect x="75" y="38" width="18" height="20" rx="1"/>
            <rect x="110" y="38" width="18" height="20" rx="1"/>
            <rect x="135" y="38" width="18" height="20" rx="1"/>
            <rect x="160" y="38" width="18" height="20" rx="1"/>
            <rect x="190" y="38" width="18" height="20" rx="1"/>
            <rect x="215" y="38" width="18" height="20" rx="1"/>
            <rect x="248" y="38" width="18" height="20" rx="1"/>
          </g>
          <g fill="#FAE0A0" opacity="0.85">
            <rect x="17" y="40" width="14" height="16"/>
            <rect x="52" y="40" width="14" height="16"/>
            <rect x="77" y="40" width="14" height="16"/>
            <rect x="112" y="40" width="14" height="16"/>
            <rect x="137" y="40" width="14" height="16"/>
            <rect x="162" y="40" width="14" height="16"/>
            <rect x="192" y="40" width="14" height="16"/>
            <rect x="217" y="40" width="14" height="16"/>
            <rect x="250" y="40" width="14" height="16"/>
          </g>
          <g stroke="#1F1A14" strokeWidth="0.4" fill="none" opacity="0.6">
            <line x1="24" y1="40" x2="24" y2="56"/>
            <line x1="59" y1="40" x2="59" y2="56"/>
            <line x1="84" y1="40" x2="84" y2="56"/>
            <line x1="119" y1="40" x2="119" y2="56"/>
            <line x1="144" y1="40" x2="144" y2="56"/>
            <line x1="169" y1="40" x2="169" y2="56"/>
            <line x1="199" y1="40" x2="199" y2="56"/>
            <line x1="224" y1="40" x2="224" y2="56"/>
            <line x1="257" y1="40" x2="257" y2="56"/>
          </g>
          <rect x="0" y="58" width="280" height="3" fill="#5A3818"/>
          <g stroke="#3A2818" strokeWidth="0.4" fill="none">
            <path d="M 5 60 L 5 78 M 12 60 Q 12 65 18 65 Q 24 65 24 60 M 28 60 L 28 78 M 35 60 Q 35 65 28 70"/>
            <path d="M 45 60 L 45 78 M 52 60 Q 52 65 58 65 Q 64 65 64 60 M 68 60 L 68 78 M 75 60 Q 75 65 68 70"/>
            <path d="M 85 60 L 85 78 M 92 60 Q 92 65 98 65 Q 104 65 104 60 M 108 60 L 108 78 M 115 60 Q 115 65 108 70"/>
            <path d="M 125 60 L 125 78 M 132 60 Q 132 65 138 65 Q 144 65 144 60 M 148 60 L 148 78 M 155 60 Q 155 65 148 70"/>
            <path d="M 165 60 L 165 78 M 172 60 Q 172 65 178 65 Q 184 65 184 60 M 188 60 L 188 78 M 195 60 Q 195 65 188 70"/>
            <path d="M 205 60 L 205 78 M 212 60 Q 212 65 218 65 Q 224 65 224 60 M 228 60 L 228 78 M 235 60 Q 235 65 228 70"/>
            <path d="M 245 60 L 245 78 M 252 60 Q 252 65 258 65 Q 264 65 264 60 M 268 60 L 268 78 M 275 60 Q 275 65 268 70"/>
          </g>
          <g transform="translate(60, 88)">
            <path d="M -18 0 Q -22 -8 -10 -8 Q -4 -10 0 -6 Q 4 -10 10 -8 Q 22 -8 18 0 Q 14 6 0 4 Q -14 6 -18 0 Z" fill="#5028A0"/>
            <ellipse cx="-9" cy="-2" rx="5" ry="3" fill="#1F1A14"/>
            <ellipse cx="9" cy="-2" rx="5" ry="3" fill="#1F1A14"/>
            <path d="M -2 4 Q 0 6 2 4" fill="none" stroke="#3A1F18" strokeWidth="0.5"/>
            <g fill="#F0D040">
              <circle cx="0" cy="-7" r="1.2"/>
              <path d="M -3 -10 L -2 -7 L 1 -10 L 0 -6 L 3 -10 L 2 -7 L 5 -10" stroke="#F0D040" strokeWidth="0.5" fill="none"/>
            </g>
          </g>
          <g transform="translate(140, 92)">
            <path d="M -18 0 Q -22 -8 -10 -8 Q -4 -10 0 -6 Q 4 -10 10 -8 Q 22 -8 18 0 Q 14 6 0 4 Q -14 6 -18 0 Z" fill="#3A8038"/>
            <ellipse cx="-9" cy="-2" rx="5" ry="3" fill="#1F1A14"/>
            <ellipse cx="9" cy="-2" rx="5" ry="3" fill="#1F1A14"/>
            <g fill="#F0D040">
              <path d="M -3 -10 L -2 -7 L 1 -10 L 0 -6 L 3 -10 L 2 -7 L 5 -10" stroke="#F0D040" strokeWidth="0.5" fill="none"/>
            </g>
          </g>
          <g transform="translate(220, 90)">
            <path d="M -18 0 Q -22 -8 -10 -8 Q -4 -10 0 -6 Q 4 -10 10 -8 Q 22 -8 18 0 Q 14 6 0 4 Q -14 6 -18 0 Z" fill="#F0D040"/>
            <ellipse cx="-9" cy="-2" rx="5" ry="3" fill="#1F1A14"/>
            <ellipse cx="9" cy="-2" rx="5" ry="3" fill="#1F1A14"/>
            <path d="M -3 -10 L -2 -7 L 1 -10 L 0 -6 L 3 -10 L 2 -7 L 5 -10" stroke="#5028A0" strokeWidth="0.5" fill="none"/>
          </g>
          <g fill="#F0D040">
            <circle cx="30" cy="100" r="1.2"/>
            <circle cx="38" cy="103" r="1"/>
            <circle cx="100" cy="102" r="1.2"/>
            <circle cx="108" cy="105" r="1"/>
            <circle cx="175" cy="100" r="1.2"/>
            <circle cx="250" cy="103" r="1.2"/>
            <circle cx="258" cy="100" r="1"/>
          </g>
          <g fill="#5028A0">
            <circle cx="25" cy="106" r="1"/>
            <circle cx="95" cy="106" r="1"/>
            <circle cx="115" cy="100" r="1.2"/>
            <circle cx="180" cy="106" r="1.2"/>
            <circle cx="255" cy="106" r="1"/>
          </g>
          <g fill="#3A8038">
            <circle cx="44" cy="107" r="1"/>
            <circle cx="112" cy="108" r="1.2"/>
            <circle cx="190" cy="105" r="1.2"/>
            <circle cx="265" cy="107" r="1"/>
          </g>
        </svg>
      );

    case "great-barrier-reef":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#0A4A6A"/>
          <rect width="280" height="55" fill="#2A8AAC"/>
          <rect width="280" height="25" fill="#5DBAD0"/>
          <rect width="280" height="9" fill="#9DDAE8"/>
          <path d="M 0 5 Q 20 9 40 5 T 80 5 T 120 5 T 160 5 T 200 5 T 240 5 T 280 5" fill="none" stroke="white" strokeWidth="0.5" opacity="0.5"/>
          <polygon points="55,0 82,0 105,85 85,85" fill="white" opacity="0.22"/>
          <polygon points="120,0 148,0 168,85 142,85" fill="white" opacity="0.2"/>
          <polygon points="175,0 202,0 225,85 200,85" fill="white" opacity="0.17"/>
          <polygon points="235,0 262,0 278,80 256,80" fill="white" opacity="0.13"/>
          <polygon points="12,0 35,0 50,75 30,75" fill="white" opacity="0.15"/>
          <g transform="translate(248, 28)">
            <ellipse cx="0" cy="0" rx="2.2" ry="4.5" fill="#1F1A14"/>
            <circle cx="0" cy="-4.5" r="2.4" fill="#1F1A14"/>
            <rect x="0.5" y="-2.8" width="1.2" height="2.8" fill="#3A2A22"/>
            <ellipse cx="-2.2" cy="5.5" rx="2.5" ry="1.1" fill="#1F1A14" transform="rotate(-30 -2.2 5.5)"/>
            <ellipse cx="2.2" cy="5.5" rx="2.5" ry="1.1" fill="#1F1A14" transform="rotate(30 2.2 5.5)"/>
            <line x1="-2.5" y1="0" x2="-5.5" y2="2" stroke="#1F1A14" strokeWidth="1"/>
            <line x1="2.5" y1="0" x2="5" y2="-3" stroke="#1F1A14" strokeWidth="1"/>
            <circle cx="2" cy="-9" r="0.7" fill="white" opacity="0.75"/>
            <circle cx="3" cy="-12.5" r="0.5" fill="white" opacity="0.65"/>
            <circle cx="2.5" cy="-15.5" r="0.4" fill="white" opacity="0.55"/>
            <circle cx="3.5" cy="-18" r="0.3" fill="white" opacity="0.45"/>
          </g>
          <g transform="translate(140, 55)">
            <path d="M -65 -2 Q -55 -10 -25 -12 Q -12 -12 -3 -8 L -1 -10 L 0 -11 L 1 -10 L 3 -8 Q 12 -12 25 -12 Q 55 -10 65 -2 Q 65 3 18 5 Q 12 8 8 18 Q 5 28 3 32 L -3 32 Q -5 28 -8 18 Q -12 8 -18 5 Q -65 3 -65 -2 Z" fill="#1A2838"/>
            <path d="M -3 -10 Q -5 -16 -3 -18 Q -1 -16 -1 -11 Z" fill="#1A2838"/>
            <path d="M 3 -10 Q 5 -16 3 -18 Q 1 -16 1 -11 Z" fill="#1A2838"/>
            <ellipse cx="0" cy="-1" rx="11" ry="3.5" fill="#3A4A5A" opacity="0.75"/>
            <ellipse cx="-32" cy="-2" rx="2.5" ry="0.8" fill="#3A4A5A" opacity="0.4"/>
            <ellipse cx="32" cy="-2" rx="2.5" ry="0.8" fill="#3A4A5A" opacity="0.4"/>
          </g>
          <g transform="translate(78, 78)">
            <ellipse cx="0" cy="0" rx="9.5" ry="6" fill="#3A4830"/>
            <ellipse cx="0" cy="-0.5" rx="6.8" ry="4" fill="#4A5840"/>
            <line x1="-5" y1="0" x2="5" y2="0" stroke="#2A3820" strokeWidth="0.6"/>
            <line x1="0" y1="-3" x2="0" y2="3" stroke="#2A3820" strokeWidth="0.6"/>
            <line x1="-3" y1="-2" x2="-3" y2="2" stroke="#2A3820" strokeWidth="0.4"/>
            <line x1="3" y1="-2" x2="3" y2="2" stroke="#2A3820" strokeWidth="0.4"/>
            <ellipse cx="-9.5" cy="-0.5" rx="2.8" ry="2" fill="#3A4830"/>
            <ellipse cx="-4.5" cy="-4" rx="4" ry="1.7" fill="#3A4830" transform="rotate(-25 -4.5 -4)"/>
            <ellipse cx="-4.5" cy="4" rx="4" ry="1.7" fill="#3A4830" transform="rotate(25 -4.5 4)"/>
            <ellipse cx="5.5" cy="-3.2" rx="2.8" ry="1.3" fill="#3A4830" transform="rotate(20 5.5 -3.2)"/>
            <ellipse cx="5.5" cy="3.2" rx="2.8" ry="1.3" fill="#3A4830" transform="rotate(-20 5.5 3.2)"/>
          </g>
          <g fill="#F5C878">
            <ellipse cx="200" cy="35" rx="3" ry="1.5"/>
            <polygon points="197,35 195,33 195,37"/>
            <ellipse cx="210" cy="32" rx="2.6" ry="1.3"/>
            <polygon points="207.4,32 205.8,30.5 205.8,33.5"/>
            <ellipse cx="205" cy="42" rx="2.8" ry="1.4"/>
            <polygon points="202.2,42 200.6,40.5 200.6,43.5"/>
            <ellipse cx="216" cy="38" rx="2.5" ry="1.3"/>
            <polygon points="213.5,38 212,36.5 212,39.5"/>
            <ellipse cx="222" cy="42" rx="2.5" ry="1.3"/>
            <polygon points="219.5,42 218,40.5 218,43.5"/>
            <ellipse cx="212" cy="46" rx="2.3" ry="1.2"/>
            <polygon points="209.7,46 208.2,44.8 208.2,47.2"/>
          </g>
          <g transform="translate(115, 92)">
            <ellipse cx="0" cy="0" rx="5.5" ry="3" fill="#F58838"/>
            <polygon points="-5.5,0 -8.5,-2.5 -8.5,2.5" fill="#F58838"/>
            <polygon points="0,-3 2,-5 0,-2" fill="#F58838"/>
            <polygon points="0,3 2,5 0,2" fill="#F58838"/>
            <ellipse cx="-2.5" cy="0" rx="0.9" ry="2.8" fill="white"/>
            <ellipse cx="1" cy="0" rx="0.7" ry="2.8" fill="white"/>
            <circle cx="3" cy="-0.5" r="0.5" fill="#1A0F08"/>
          </g>
          <g fill="white">
            <circle cx="62" cy="55" r="1.5" opacity="0.7"/>
            <circle cx="60" cy="63" r="1" opacity="0.6"/>
            <circle cx="64" cy="70" r="0.7" opacity="0.5"/>
            <circle cx="220" cy="55" r="1.2" opacity="0.7"/>
            <circle cx="223" cy="65" r="0.7" opacity="0.55"/>
            <circle cx="170" cy="80" r="1.3" opacity="0.6"/>
            <circle cx="172" cy="88" r="0.7" opacity="0.5"/>
          </g>
          <g transform="translate(0, 88)">
            <ellipse cx="18" cy="10" rx="16" ry="9" fill="#D86A8A"/>
            <ellipse cx="18" cy="10" rx="11" ry="6" fill="#C85A7A"/>
            <ellipse cx="15" cy="6" rx="3" ry="1.5" fill="#E889A0"/>
            <g transform="translate(45, 0)" stroke="#E89058" strokeWidth="1.6" fill="none">
              <path d="M 0 20 C 0 8, -5 0, -10 -3"/>
              <path d="M 0 20 C 0 10, -2 -2, -5 -6"/>
              <path d="M 0 20 C 0 12, 2 -2, 5 -6"/>
              <path d="M 0 20 C 0 8, 5 0, 10 -3"/>
              <path d="M 0 20 C 0 14, 3 4, 7 0"/>
              <path d="M 0 20 C 0 14, -3 4, -7 0"/>
            </g>
            <ellipse cx="45" cy="20" rx="3" ry="1.7" fill="#D87C40"/>
            <g transform="translate(75, 2)" fill="#9858A8">
              <rect x="0" y="0" width="3" height="16" rx="1"/>
              <rect x="-5" y="2" width="3" height="14" rx="1"/>
              <rect x="5" y="3" width="3" height="13" rx="1"/>
              <rect x="-9" y="6" width="3" height="10" rx="1"/>
              <rect x="9" y="5" width="3" height="11" rx="1"/>
              <circle cx="1.5" cy="0" r="1.7"/>
              <circle cx="-3.5" cy="2" r="1.5"/>
              <circle cx="6.5" cy="3" r="1.5"/>
              <circle cx="-7.5" cy="6" r="1.3"/>
              <circle cx="10.5" cy="5" r="1.3"/>
            </g>
            <g transform="translate(105, 6)" stroke="#C85A8A" strokeWidth="1.8" strokeLinecap="round" fill="none">
              <ellipse cx="0" cy="10" rx="10" ry="3.5" fill="#C85A8A"/>
              <line x1="-8" y1="9" x2="-12" y2="-1"/>
              <line x1="-6" y1="7" x2="-8" y2="-3"/>
              <line x1="-3" y1="6" x2="-3" y2="-4"/>
              <line x1="0" y1="6" x2="0" y2="-5"/>
              <line x1="3" y1="6" x2="3" y2="-4"/>
              <line x1="6" y1="7" x2="8" y2="-3"/>
              <line x1="8" y1="9" x2="12" y2="-1"/>
            </g>
            <g transform="translate(140, 4)" fill="#F5A878">
              <rect x="0" y="0" width="2" height="14"/>
              <rect x="-4" y="-2" width="2" height="14"/>
              <rect x="4" y="-2" width="2" height="14"/>
              <rect x="-6" y="2" width="2" height="10"/>
              <rect x="6" y="2" width="2" height="10"/>
              <circle cx="1" cy="-1" r="1.6"/>
              <circle cx="-3" cy="-3" r="1.4"/>
              <circle cx="5" cy="-3" r="1.4"/>
            </g>
            <ellipse cx="170" cy="11" rx="10" ry="5.5" fill="#E89058"/>
            <ellipse cx="170" cy="11" rx="6" ry="3" fill="#D87C40"/>
            <ellipse cx="195" cy="13" rx="10" ry="5" fill="#5A8A85"/>
            <ellipse cx="195" cy="13" rx="6" ry="3" fill="#4A7A75"/>
            <g transform="translate(220, 2)" fill="#A85A78">
              <rect x="0" y="0" width="2" height="13"/>
              <rect x="-3" y="-1" width="2" height="11"/>
              <rect x="3" y="-2" width="2" height="13"/>
              <rect x="-5" y="2" width="2" height="9"/>
              <rect x="5" y="1" width="2" height="10"/>
              <circle cx="1" cy="-1" r="1.5"/>
              <circle cx="-2" cy="-2" r="1.3"/>
              <circle cx="4" cy="-3" r="1.3"/>
            </g>
            <ellipse cx="250" cy="12" rx="11" ry="5.5" fill="#E8A878"/>
            <ellipse cx="250" cy="12" rx="7" ry="3" fill="#D89868"/>
            <ellipse cx="272" cy="13" rx="10" ry="5" fill="#D86A8A"/>
          </g>
          <rect y="108" width="280" height="3" fill="#8A6048"/>
        </svg>
      );

    case "everest-base-camp":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#D8E2E8"/>
          <rect width="280" height="35" fill="#E8EEF0"/>
          <rect width="280" height="12" fill="#F2F5F7"/>
          <path d="M -10 110 L 35 50 L 60 65 L 90 30 L 130 8 L 175 35 L 210 55 L 250 25 L 290 60 L 290 110 Z" fill="#3A4858"/>
          <path d="M 60 65 L 90 30 L 105 50 Z" fill="#5A6878"/>
          <path d="M 130 8 L 175 35 L 158 50 Z" fill="#5A6878"/>
          <path d="M 210 55 L 250 25 L 270 50 Z" fill="#5A6878"/>
          <path d="M 90 30 L 88 36 L 96 38 L 92 44 L 102 46 L 110 50 L 88 50 L 78 45 L 84 38 Z" fill="white"/>
          <path d="M 130 8 L 126 16 L 134 20 L 130 26 L 142 28 L 150 35 L 120 36 L 110 30 L 118 22 L 125 14 Z" fill="white"/>
          <path d="M 175 35 L 172 41 L 180 43 L 176 49 L 184 50 L 168 50 L 165 44 L 170 38 Z" fill="white"/>
          <path d="M 250 25 L 247 32 L 255 34 L 250 40 L 260 42 L 268 46 L 244 46 L 236 40 L 242 32 Z" fill="white"/>
          <path d="M 0 75 Q 50 70 100 72 T 200 70 T 280 73 L 280 92 L 0 92 Z" fill="#B0BAC2"/>
          <path d="M 0 82 Q 60 78 130 80 T 280 82 L 280 100 L 0 100 Z" fill="#909AA4"/>
          <rect y="92" width="280" height="18" fill="#7A8A95"/>
          <g transform="translate(100, 90)">
            <polygon points="0,0 20,0 10,-12" fill="#E89058"/>
            <polygon points="0,0 20,0 10,-12" fill="none" stroke="#3A2820" strokeWidth="0.4"/>
            <rect x="8" y="-3" width="4" height="3" fill="#3A2820"/>
          </g>
          <g transform="translate(125, 92)">
            <polygon points="0,0 22,0 11,-13" fill="#C84A4A"/>
            <polygon points="0,0 22,0 11,-13" fill="none" stroke="#3A2820" strokeWidth="0.4"/>
            <rect x="9" y="-3" width="4" height="3" fill="#3A2820"/>
          </g>
          <g transform="translate(155, 90)">
            <polygon points="0,0 20,0 10,-12" fill="#E8C868"/>
            <polygon points="0,0 20,0 10,-12" fill="none" stroke="#3A2820" strokeWidth="0.4"/>
            <rect x="8" y="-3" width="4" height="3" fill="#3A2820"/>
          </g>
          <g transform="translate(40, 60)">
            <path d="M 0 0 Q 12 -4 24 0 Q 36 4 50 -2" stroke="#3A2820" strokeWidth="0.4" fill="none" strokeDasharray="0.5 1.2"/>
            <rect x="-1.5" y="-12" width="2" height="14" fill="#3A2820"/>
            <rect x="-1.5" y="-12" width="3.5" height="3" fill="#C84A4A"/>
            <rect x="-1.5" y="-9" width="3.5" height="3" fill="#E8E8E8"/>
            <rect x="-1.5" y="-6" width="3.5" height="3" fill="#3A6AA8"/>
            <rect x="-1.5" y="-3" width="3.5" height="3" fill="#E8C868"/>
            <rect x="-1.5" y="0" width="3.5" height="3" fill="#5A9A5A"/>
            <rect x="22.5" y="-10" width="3.5" height="3" fill="#C84A4A"/>
            <rect x="22.5" y="-7" width="3.5" height="3" fill="#E8E8E8"/>
            <rect x="22.5" y="-4" width="3.5" height="3" fill="#3A6AA8"/>
            <rect x="22.5" y="-1" width="3.5" height="3" fill="#E8C868"/>
            <rect x="44" y="-8" width="3.5" height="3" fill="#C84A4A"/>
            <rect x="44" y="-5" width="3.5" height="3" fill="#E8E8E8"/>
            <rect x="44" y="-2" width="3.5" height="3" fill="#3A6AA8"/>
            <rect x="48.5" y="-2" width="2" height="6" fill="#3A2820"/>
          </g>
          <g fill="white" opacity="0.85">
            <circle cx="200" cy="85" r="0.6"/>
            <circle cx="220" cy="90" r="0.5"/>
            <circle cx="240" cy="88" r="0.6"/>
            <circle cx="60" cy="88" r="0.5"/>
            <circle cx="80" cy="92" r="0.5"/>
          </g>
        </svg>
      );

    case "zip-line":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#B8D2C0"/>
  <rect width="280" height="35" fill="#C8DECB"/>
  <ellipse cx="140" cy="42" rx="180" ry="8" fill="#E0E8DC" opacity="0.7"/>
  <ellipse cx="60" cy="58" rx="100" ry="5" fill="#E0E8DC" opacity="0.55"/>
  <ellipse cx="220" cy="62" rx="100" ry="5" fill="#E0E8DC" opacity="0.55"/>
  <path d="M 0 50 L 25 38 L 60 44 L 100 32 L 140 40 L 180 30 L 220 38 L 260 28 L 280 32 L 280 60 L 0 60 Z" fill="#5A7A55"/>
  <path d="M 0 58 Q 40 50 90 54 T 200 50 T 280 56 L 280 75 L 0 75 Z" fill="#4A6A45"/>
  <g fill="#3A5A35">
    <ellipse cx="15" cy="78" rx="22" ry="14"/>
    <ellipse cx="50" cy="82" rx="20" ry="13"/>
    <ellipse cx="85" cy="78" rx="22" ry="14"/>
    <ellipse cx="120" cy="83" rx="18" ry="12"/>
    <ellipse cx="155" cy="78" rx="22" ry="14"/>
    <ellipse cx="190" cy="84" rx="20" ry="13"/>
    <ellipse cx="225" cy="78" rx="22" ry="14"/>
    <ellipse cx="265" cy="83" rx="20" ry="13"/>
  </g>
  <g fill="#2A4A28">
    <ellipse cx="30" cy="92" rx="22" ry="14"/>
    <ellipse cx="70" cy="96" rx="20" ry="13"/>
    <ellipse cx="110" cy="94" rx="22" ry="14"/>
    <ellipse cx="150" cy="98" rx="20" ry="13"/>
    <ellipse cx="190" cy="94" rx="22" ry="14"/>
    <ellipse cx="230" cy="98" rx="20" ry="13"/>
    <ellipse cx="265" cy="94" rx="22" ry="14"/>
  </g>
  <g transform="translate(10, 45)">
    <rect x="0" y="0" width="3" height="50" fill="#5A3820"/>
    <rect x="-2" y="-2" width="7" height="3" fill="#3A2818"/>
    <rect x="-2" y="-2" width="7" height="6" fill="#5A3820"/>
    <path d="M -3 4 L 6 4 L 4 0 L -1 0 Z" fill="#5A3820"/>
  </g>
  <g transform="translate(263, 35)">
    <rect x="0" y="0" width="3" height="55" fill="#5A3820"/>
    <rect x="-2" y="-2" width="7" height="3" fill="#3A2818"/>
    <rect x="-2" y="-2" width="7" height="6" fill="#5A3820"/>
    <path d="M -3 4 L 6 4 L 4 0 L -1 0 Z" fill="#5A3820"/>
  </g>
  <line x1="10" y1="46" x2="263" y2="38" stroke="#1F1A14" strokeWidth="1.2"/>
  <line x1="10" y1="47" x2="263" y2="39" stroke="#3A332E" strokeWidth="0.5" opacity="0.7"/>
  <g transform="translate(140, 42)">
    <rect x="-3" y="-2" width="6" height="3.5" fill="#3A332E" rx="0.5"/>
    <rect x="-2" y="-3" width="4" height="2" fill="#5A5046"/>
    <circle cx="-1.5" cy="-2.5" r="0.5" fill="#1F1A14"/>
    <circle cx="1.5" cy="-2.5" r="0.5" fill="#1F1A14"/>
    <line x1="-2.5" y1="1.5" x2="-3" y2="6" stroke="#3A332E" strokeWidth="0.6"/>
    <line x1="2.5" y1="1.5" x2="3" y2="6" stroke="#3A332E" strokeWidth="0.6"/>
    <circle cx="0" cy="8" r="3" fill="#3A2820"/>
    <path d="M -5 7 L -3 5 L 3 5 L 5 7 L 5 9 L -5 9 Z" fill="#E8C868"/>
    <rect x="-3.5" y="9" width="7" height="9" rx="1" fill="#C84A4A"/>
    <rect x="-3.5" y="9" width="7" height="3" fill="#3A6AA8"/>
    <rect x="-6" y="10" width="3" height="6" rx="0.5" fill="#3A2820" transform="rotate(20 -4.5 13)"/>
    <rect x="3" y="10" width="3" height="6" rx="0.5" fill="#3A2820" transform="rotate(-20 4.5 13)"/>
    <rect x="-3" y="17" width="2.4" height="7" fill="#3A2820"/>
    <rect x="0.6" y="17" width="2.4" height="7" fill="#3A2820"/>
    <line x1="0" y1="-3" x2="0" y2="-1" stroke="#1F1A14" strokeWidth="0.4"/>
  </g>
  <g fill="#E8C868">
    <ellipse cx="60" cy="75" rx="2" ry="1"/>
    <ellipse cx="180" cy="77" rx="2" ry="1"/>
    <ellipse cx="245" cy="75" rx="2" ry="1"/>
  </g>
  <g fill="#C84A4A">
    <ellipse cx="35" cy="76" rx="1.5" ry="0.8"/>
    <ellipse cx="100" cy="78" rx="1.5" ry="0.8"/>
    <ellipse cx="210" cy="76" rx="1.5" ry="0.8"/>
  </g>
        </svg>
      );

    case "sandboard-namib":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#E8B078"/>
  <rect width="280" height="40" fill="#F0C898"/>
  <rect width="280" height="15" fill="#F5D8B0"/>
  <circle cx="50" cy="22" r="11" fill="#F8E0B8"/>
  <circle cx="50" cy="22" r="7" fill="#FAEAC8"/>
  <path d="M 0 55 Q 30 45 60 48 T 130 42 T 200 38 T 280 35 L 280 70 L 0 70 Z" fill="#B85A28"/>
  <path d="M 0 55 Q 30 45 60 48 T 130 42 T 200 38 T 280 35" fill="none" stroke="#8A3818" strokeWidth="0.5"/>
  <path d="M 200 38 Q 240 30 280 35 L 280 65 Q 240 50 200 55 Z" fill="#8A3818" opacity="0.6"/>
  <path d="M -10 110 Q 40 70 100 62 Q 160 56 220 60 Q 260 64 290 75 L 290 110 Z" fill="#C8602A"/>
  <path d="M -10 110 Q 40 80 100 72 Q 160 66 220 70 Q 260 74 290 85 L 290 110 Z" fill="#9A4818"/>
  <path d="M -10 110 Q 40 92 100 86 Q 160 82 220 86 Q 260 90 290 98 L 290 110 Z" fill="#722E10"/>
  <g transform="translate(135, 70)">
    <ellipse cx="0" cy="13" rx="12" ry="2" fill="#3A1F08" opacity="0.5"/>
    <path d="M -10 8 Q 0 6 12 9 L 11 12 Q 0 10 -9 11 Z" fill="#3A2818"/>
    <path d="M -10 8 Q 0 6 12 9" fill="none" stroke="#5A3828" strokeWidth="0.4"/>
    <line x1="-6" y1="8.5" x2="-6" y2="11" stroke="#5A3828" strokeWidth="0.3"/>
    <line x1="6" y1="8" x2="6" y2="11" stroke="#5A3828" strokeWidth="0.3"/>
    <rect x="-3" y="3" width="2.5" height="6" fill="#1F1A14" transform="rotate(-12 -1.5 6)"/>
    <rect x="1" y="3" width="2.5" height="6" fill="#1F1A14" transform="rotate(8 2 6)"/>
    <rect x="-2.5" y="-6" width="5" height="10" rx="1" fill="#E8C868"/>
    <rect x="-2.5" y="-6" width="5" height="3" fill="#C84A4A"/>
    <rect x="-5" y="-5" width="3" height="6" rx="0.5" fill="#E8C868" transform="rotate(-30 -3.5 -2)"/>
    <rect x="2" y="-5" width="3" height="6" rx="0.5" fill="#E8C868" transform="rotate(30 3.5 -2)"/>
    <circle cx="0" cy="-9" r="3" fill="#3A2820"/>
    <ellipse cx="0" cy="-11" rx="3.5" ry="1.4" fill="#1F1A14"/>
  </g>
  <path d="M 100 90 Q 120 86 140 88" stroke="#F5D8B0" strokeWidth="1.5" fill="none" opacity="0.6" strokeLinecap="round"/>
  <path d="M 95 95 Q 115 92 135 94" stroke="#F5D8B0" strokeWidth="1" fill="none" opacity="0.4" strokeLinecap="round"/>
  <g fill="#5A2818" opacity="0.55">
    <ellipse cx="200" cy="100" rx="1" ry="0.4"/>
    <ellipse cx="220" cy="103" rx="1" ry="0.4"/>
    <ellipse cx="240" cy="98" rx="1" ry="0.4"/>
    <ellipse cx="260" cy="105" rx="1" ry="0.4"/>
    <ellipse cx="40" cy="100" rx="1" ry="0.4"/>
    <ellipse cx="60" cy="104" rx="1" ry="0.4"/>
    <ellipse cx="20" cy="102" rx="1" ry="0.4"/>
  </g>
        </svg>
      );

    case "surf-nazare":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#C8DEE8"/>
  <rect width="280" height="30" fill="#D8E8F0"/>
  <g fill="white" opacity="0.7">
    <ellipse cx="40" cy="14" rx="14" ry="3"/>
    <ellipse cx="36" cy="11" rx="8" ry="2.5"/>
    <ellipse cx="220" cy="18" rx="16" ry="3.5"/>
    <ellipse cx="216" cy="14" rx="9" ry="2.8"/>
  </g>
  <path d="M 0 35 C 30 25, 70 20, 110 30 C 145 38, 175 32, 200 22 C 215 16, 225 12, 240 8 C 250 8, 258 14, 258 28 C 258 38, 250 48, 235 55 L 220 60 C 220 50, 215 42, 205 38 C 195 35, 180 38, 165 45 C 145 55, 120 62, 90 65 C 60 68, 30 65, 0 60 Z" fill="#1A4858"/>
  <path d="M 240 8 C 250 8, 258 14, 258 28 C 258 38, 250 48, 235 55 C 240 45, 245 32, 245 22 C 245 14, 242 10, 240 8 Z" fill="#0F3445"/>
  <path d="M 15 38 C 45 30, 80 28, 115 35 C 145 41, 175 36, 195 28 C 175 38, 145 47, 115 50 C 80 53, 45 50, 15 45 Z" fill="#3A6878" opacity="0.6"/>
  <g fill="white">
    <ellipse cx="200" cy="22" rx="5" ry="1.5"/>
    <ellipse cx="190" cy="26" rx="6" ry="1.7"/>
    <ellipse cx="175" cy="34" rx="7" ry="1.8"/>
    <ellipse cx="155" cy="43" rx="8" ry="1.8"/>
    <ellipse cx="130" cy="52" rx="9" ry="1.8"/>
    <ellipse cx="220" cy="55" rx="10" ry="2.5"/>
    <ellipse cx="230" cy="48" rx="8" ry="2"/>
    <ellipse cx="235" cy="40" rx="6" ry="1.5"/>
    <circle cx="245" cy="32" r="1.2"/>
    <circle cx="250" cy="38" r="1"/>
    <circle cx="255" cy="44" r="0.8"/>
    <circle cx="225" cy="25" r="0.8"/>
    <circle cx="215" cy="30" r="0.8"/>
    <circle cx="205" cy="35" r="0.6"/>
  </g>
  <rect y="70" width="280" height="40" fill="#3A6878"/>
  <rect y="70" width="280" height="3" fill="#5A8898"/>
  <rect y="70" width="280" height="1" fill="#9DBDC8" opacity="0.7"/>
  <g fill="white">
    <ellipse cx="40" cy="78" rx="14" ry="1.5" opacity="0.8"/>
    <ellipse cx="100" cy="82" rx="20" ry="1.7" opacity="0.7"/>
    <ellipse cx="180" cy="80" rx="18" ry="1.6" opacity="0.7"/>
    <ellipse cx="250" cy="85" rx="16" ry="1.5" opacity="0.65"/>
  </g>
  <g stroke="#5A8898" strokeWidth="0.6" opacity="0.5">
    <line x1="25" y1="92" x2="55" y2="92"/>
    <line x1="115" y1="95" x2="145" y2="95"/>
    <line x1="195" y1="98" x2="225" y2="98"/>
    <line x1="60" y1="100" x2="90" y2="100"/>
    <line x1="170" y1="103" x2="200" y2="103"/>
  </g>
  <g transform="translate(180, 56)">
    <ellipse cx="0" cy="0" rx="9" ry="1.4" fill="#E8E8E0" transform="rotate(-12)"/>
    <ellipse cx="0" cy="0" rx="9" ry="1.4" fill="none" stroke="#3A2820" strokeWidth="0.3" transform="rotate(-12)"/>
    <g transform="translate(0, -2) rotate(-12)">
      <rect x="-1.5" y="-3" width="3" height="5" fill="#3A2820"/>
      <circle cx="0" cy="-5" r="1.6" fill="#3A2820"/>
      <rect x="-3" y="-2" width="2" height="3" fill="#C84A4A" transform="rotate(-20 -2 -1)"/>
      <rect x="1" y="-2" width="2" height="3" fill="#C84A4A" transform="rotate(20 2 -1)"/>
      <rect x="-1.2" y="2" width="1" height="3" fill="#3A2820"/>
      <rect x="0.2" y="2" width="1" height="3" fill="#3A2820"/>
    </g>
  </g>
        </svg>
      );

    case "solar-eclipse":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#0A0F25"/>
  <rect width="280" height="70" fill="#1A1830"/>
  <rect width="280" height="55" fill="#2A1F38"/>
  <rect y="70" width="280" height="20" fill="#3A2828"/>
  <rect y="85" width="280" height="25" fill="#5A3838"/>
  <g fill="white" opacity="0.7">
    <circle cx="30" cy="10" r="0.5"/>
    <circle cx="62" cy="22" r="0.4"/>
    <circle cx="200" cy="14" r="0.5"/>
    <circle cx="240" cy="24" r="0.5"/>
    <circle cx="20" cy="38" r="0.4"/>
    <circle cx="248" cy="42" r="0.4"/>
    <circle cx="80" cy="8" r="0.4"/>
    <circle cx="218" cy="9" r="0.4"/>
    <circle cx="55" cy="48" r="0.4"/>
    <circle cx="225" cy="55" r="0.4"/>
    <circle cx="100" cy="20" r="0.35"/>
    <circle cx="180" cy="35" r="0.35"/>
  </g>
  <g transform="translate(140, 42)">
    <circle cx="0" cy="0" r="32" fill="white" opacity="0.06"/>
    <circle cx="0" cy="0" r="27" fill="white" opacity="0.1"/>
    <circle cx="0" cy="0" r="22" fill="#F8F0D8" opacity="0.18"/>
    <circle cx="0" cy="0" r="19" fill="#F8F0D8" opacity="0.32"/>
    <g fill="#F8F0D8" opacity="0.45">
      <ellipse cx="0" cy="-22" rx="2" ry="10"/>
      <ellipse cx="0" cy="22" rx="2" ry="10"/>
      <ellipse cx="-22" cy="0" rx="10" ry="2"/>
      <ellipse cx="22" cy="0" rx="10" ry="2"/>
      <ellipse cx="-15" cy="-15" rx="2" ry="7" transform="rotate(45 -15 -15)"/>
      <ellipse cx="15" cy="15" rx="2" ry="7" transform="rotate(45 15 15)"/>
      <ellipse cx="-15" cy="15" rx="2" ry="7" transform="rotate(-45 -15 15)"/>
      <ellipse cx="15" cy="-15" rx="2" ry="7" transform="rotate(-45 15 -15)"/>
    </g>
    <circle cx="0" cy="0" r="15.5" fill="#F8E5C8" opacity="0.85"/>
    <circle cx="0" cy="0" r="14.5" fill="white" opacity="0.95"/>
    <circle cx="0" cy="0" r="13" fill="#0A0F25"/>
    <circle cx="11" cy="-7" r="2.2" fill="white"/>
    <circle cx="11" cy="-7" r="3.5" fill="white" opacity="0.55"/>
    <circle cx="11" cy="-7" r="5" fill="white" opacity="0.3"/>
  </g>
  <ellipse cx="140" cy="78" rx="200" ry="3" fill="#C84030" opacity="0.5"/>
  <ellipse cx="140" cy="80" rx="240" ry="2" fill="#E89858" opacity="0.45"/>
  <path d="M 0 85 L 30 80 L 60 84 L 100 76 L 140 82 L 180 75 L 220 82 L 260 78 L 280 82 L 280 95 L 0 95 Z" fill="#1F1A30"/>
  <g fill="#0A0815">
    <g transform="translate(55, 100)">
      <circle cx="0" cy="-7" r="2"/>
      <rect x="-2" y="-5" width="4" height="6"/>
      <rect x="-1.5" y="1" width="1.5" height="7"/>
      <rect x="0" y="1" width="1.5" height="7"/>
    </g>
    <g transform="translate(75, 102)">
      <circle cx="0" cy="-6" r="1.8"/>
      <rect x="-1.8" y="-4" width="3.5" height="5"/>
      <rect x="-1.3" y="1" width="1.3" height="6"/>
      <rect x="0" y="1" width="1.3" height="6"/>
    </g>
    <g transform="translate(210, 100)">
      <circle cx="0" cy="-7" r="2"/>
      <rect x="-2" y="-5" width="4" height="6"/>
      <rect x="-1.5" y="1" width="1.5" height="7"/>
      <rect x="0" y="1" width="1.5" height="7"/>
    </g>
    <g transform="translate(230, 103)">
      <circle cx="0" cy="-6" r="1.8"/>
      <rect x="-1.8" y="-4" width="3.5" height="5"/>
      <rect x="-1.3" y="1" width="1.3" height="6"/>
      <rect x="0" y="1" width="1.3" height="6"/>
    </g>
  </g>
        </svg>
      );

    case "seven-continents":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#EAD8C0"/>
  <rect width="280" height="110" fill="#A8CCDC" opacity="0.35"/>
  <g stroke="#8A7858" strokeDasharray="2 2" opacity="0.4" fill="none">
    <line x1="0" y1="40" x2="280" y2="40" strokeWidth="0.3"/>
    <line x1="0" y1="55" x2="280" y2="55" strokeWidth="0.5"/>
    <line x1="0" y1="72" x2="280" y2="72" strokeWidth="0.3"/>
  </g>
  <g stroke="#8A7858" strokeDasharray="1 3" opacity="0.3" fill="none">
    <line x1="40" y1="10" x2="40" y2="100" strokeWidth="0.3"/>
    <line x1="100" y1="10" x2="100" y2="100" strokeWidth="0.3"/>
    <line x1="160" y1="10" x2="160" y2="100" strokeWidth="0.3"/>
    <line x1="220" y1="10" x2="220" y2="100" strokeWidth="0.3"/>
  </g>
  <path d="M 18 22 L 32 18 L 45 22 L 58 26 L 70 28 L 80 32 L 82 40 L 76 44 L 68 47 L 60 50 L 50 48 L 40 42 L 30 38 L 22 32 L 18 26 Z" fill="#A8C0A8"/>
  <path d="M 18 22 L 32 18 L 45 22 L 58 26 L 70 28 L 80 32 L 82 40 L 76 44 L 68 47 L 60 50 L 50 48 L 40 42 L 30 38 L 22 32 L 18 26 Z" fill="none" stroke="#5A6850" strokeWidth="0.4"/>
  <path d="M 60 56 L 72 58 L 78 65 L 76 75 L 72 85 L 66 92 L 60 94 L 54 88 L 52 78 L 54 68 L 56 60 Z" fill="#C8A878"/>
  <path d="M 60 56 L 72 58 L 78 65 L 76 75 L 72 85 L 66 92 L 60 94 L 54 88 L 52 78 L 54 68 L 56 60 Z" fill="none" stroke="#7A6850" strokeWidth="0.4"/>
  <path d="M 120 22 L 130 18 L 142 20 L 150 25 L 152 32 L 146 36 L 138 38 L 128 36 L 122 30 Z" fill="#D8B898"/>
  <path d="M 120 22 L 130 18 L 142 20 L 150 25 L 152 32 L 146 36 L 138 38 L 128 36 L 122 30 Z" fill="none" stroke="#8A6850" strokeWidth="0.4"/>
  <path d="M 128 40 L 148 38 L 158 45 L 162 58 L 158 70 L 152 80 L 145 86 L 138 82 L 132 72 L 128 60 L 126 50 Z" fill="#E8C898"/>
  <path d="M 128 40 L 148 38 L 158 45 L 162 58 L 158 70 L 152 80 L 145 86 L 138 82 L 132 72 L 128 60 L 126 50 Z" fill="none" stroke="#9A7850" strokeWidth="0.4"/>
  <path d="M 152 25 L 175 18 L 200 16 L 225 20 L 240 26 L 245 36 L 240 44 L 225 50 L 205 50 L 190 46 L 175 42 L 162 36 L 155 30 Z" fill="#C8B8D0"/>
  <path d="M 152 25 L 175 18 L 200 16 L 225 20 L 240 26 L 245 36 L 240 44 L 225 50 L 205 50 L 190 46 L 175 42 L 162 36 L 155 30 Z" fill="none" stroke="#7A6890" strokeWidth="0.4"/>
  <path d="M 218 64 L 232 62 L 246 65 L 252 70 L 250 76 L 240 80 L 228 80 L 218 76 L 215 70 Z" fill="#D8A878"/>
  <path d="M 218 64 L 232 62 L 246 65 L 252 70 L 250 76 L 240 80 L 228 80 L 218 76 L 215 70 Z" fill="none" stroke="#8A6850" strokeWidth="0.4"/>
  <path d="M 10 96 L 50 94 L 90 97 L 130 95 L 170 97 L 210 94 L 260 96 L 270 100 L 270 104 L 10 104 Z" fill="#E8E0D0"/>
  <path d="M 10 96 L 50 94 L 90 97 L 130 95 L 170 97 L 210 94 L 260 96 L 270 100 L 270 104 L 10 104 Z" fill="none" stroke="#8A7858" strokeWidth="0.4"/>
  <g fontFamily="DM Sans, sans-serif" fontSize="3.2" fill="#5C4A2E" letterSpacing="0.5" fontWeight="500" opacity="0.85">
    <text x="48" y="38" textAnchor="middle">N · AMERICA</text>
    <text x="64" y="78" textAnchor="middle">S · AMERICA</text>
    <text x="135" y="30" textAnchor="middle">EUROPE</text>
    <text x="145" y="62" textAnchor="middle">AFRICA</text>
    <text x="198" y="34" textAnchor="middle">ASIA</text>
    <text x="234" y="74" textAnchor="middle">OCEANIA</text>
    <text x="140" y="102" textAnchor="middle">ANTARCTICA</text>
  </g>
  <g stroke="#8B3A2F" strokeWidth="0.4" strokeDasharray="1.5 1.5" fill="none" opacity="0.5">
    <path d="M 50 35 Q 90 25 130 28"/>
    <path d="M 145 30 Q 175 32 200 32"/>
    <path d="M 200 32 Q 215 50 232 68"/>
    <path d="M 65 75 Q 100 80 130 70"/>
    <path d="M 145 75 Q 165 78 175 80"/>
  </g>
  <g transform="translate(245, 92)" opacity="0.7">
    <circle cx="0" cy="0" r="7" fill="#EAD8C0"/>
    <circle cx="0" cy="0" r="7" fill="none" stroke="#8B3A2F" strokeWidth="0.5"/>
    <polygon points="0,-6 1.3,-1.3 1.3,1.3 0,6 -1.3,1.3 -1.3,-1.3" fill="#8B3A2F" opacity="0.75"/>
    <polygon points="-6,0 -1.3,-1.3 1.3,-1.3 6,0 1.3,1.3 -1.3,1.3" fill="#8B3A2F" opacity="0.4"/>
  </g>
        </svg>
      );

    case "paris-mornings":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0DCC0"/>
  <rect width="280" height="40" fill="#F5E5D0"/>
  <circle cx="40" cy="22" r="9" fill="#F8E5B0" opacity="0.7"/>
  <g transform="translate(220, 35)" fill="#7A6850" opacity="0.65">
    <polygon points="-3,40 -4,40 -1,8 1,8 4,40 3,40"/>
    <polygon points="-4,40 -9,40 -6,32 -4,32"/>
    <polygon points="4,40 9,40 6,32 4,32"/>
    <rect x="-0.4" y="0" width="0.8" height="8"/>
    <rect x="-3" y="22" width="6" height="0.5"/>
    <rect x="-4" y="14" width="8" height="0.5"/>
  </g>
  <rect x="0" y="32" width="280" height="50" fill="#E8D8B8"/>
  <polygon points="0,32 25,22 255,22 280,32" fill="#5A4838"/>
  <rect y="33" width="280" height="1.5" fill="#A89878"/>
  <rect y="50" width="280" height="1.5" fill="#A89878"/>
  <g fill="#3A2A1F">
    <path d="M 20 56 L 20 46 Q 25 41 30 46 L 30 56 Z"/>
    <path d="M 45 56 L 45 46 Q 50 41 55 46 L 55 56 Z"/>
    <path d="M 70 56 L 70 46 Q 75 41 80 46 L 80 56 Z"/>
    <path d="M 175 56 L 175 46 Q 180 41 185 46 L 185 56 Z"/>
    <path d="M 200 56 L 200 46 Q 205 41 210 46 L 210 56 Z"/>
    <path d="M 245 56 L 245 46 Q 250 41 255 46 L 255 56 Z"/>
    <rect x="20" y="56" width="10" height="22"/>
    <rect x="45" y="56" width="10" height="22"/>
    <rect x="70" y="56" width="10" height="22"/>
    <rect x="175" y="56" width="10" height="22"/>
    <rect x="200" y="56" width="10" height="22"/>
    <rect x="245" y="56" width="10" height="22"/>
  </g>
  <g stroke="#3A2A1F" strokeWidth="0.5">
    <line x1="18" y1="78" x2="32" y2="78"/>
    <line x1="43" y1="78" x2="57" y2="78"/>
    <line x1="68" y1="78" x2="82" y2="78"/>
    <line x1="173" y1="78" x2="187" y2="78"/>
    <line x1="198" y1="78" x2="212" y2="78"/>
    <line x1="243" y1="78" x2="257" y2="78"/>
  </g>
  <rect x="95" y="60" width="65" height="2" fill="#8B3A2F"/>
  <polygon points="95,62 160,62 158,76 97,76" fill="#C84030"/>
  <g fill="#E8D0B0">
    <polygon points="103,62 108,62 107,76 103,76"/>
    <polygon points="118,62 123,62 122,76 118,76"/>
    <polygon points="133,62 138,62 137,76 133,76"/>
    <polygon points="148,62 153,62 152,76 148,76"/>
  </g>
  <rect x="98" y="76" width="59" height="6" fill="#3A2A1F"/>
  <text x="127" y="71" fontFamily="serif" fontSize="4.5" fontStyle="italic" fill="#E8D0B0" textAnchor="middle">Le Café</text>
  <rect y="82" width="280" height="28" fill="#A89880"/>
  <line y1="82" x2="280" y2="82" stroke="#7A6850" strokeWidth="0.5"/>
  <g transform="translate(50, 95)">
    <ellipse cx="0" cy="0" rx="14" ry="4" fill="#D8C8A8"/>
    <ellipse cx="0" cy="-0.5" rx="14" ry="3.5" fill="#E8D8B8"/>
    <rect x="-1.2" y="0" width="2.4" height="10" fill="#3A2A1F"/>
    <ellipse cx="0" cy="10" rx="5" ry="1.2" fill="#3A2A1F"/>
    <g transform="translate(-5, -3.5)">
      <rect x="0" y="0" width="4.5" height="3.5" fill="#EAD8C0"/>
      <rect x="0" y="0" width="4.5" height="0.8" fill="#5A3828"/>
      <path d="M 4.5 1 Q 6.5 1.5 6.5 2.3 Q 6.5 2.8 4.5 2.8" fill="none" stroke="#EAD8C0" strokeWidth="0.5"/>
      <rect x="-0.8" y="3.5" width="6" height="0.5" fill="#A89880"/>
      <ellipse cx="2" cy="0.4" rx="1.8" ry="0.3" fill="#1A0A05" opacity="0.5"/>
    </g>
    <g transform="translate(4, -2.5)">
      <path d="M 0 0 Q 1 -3 4 -2.5 Q 6 0 5 2.5 Q 2 3.5 0 0" fill="#D8A878"/>
      <path d="M 1.5 0 Q 2.5 -1.8 3.8 -1.2 Q 4.5 0.5 3.8 1.8 Q 2.5 2.2 1.5 0" fill="#B88858"/>
    </g>
  </g>
  <g transform="translate(82, 95)">
    <rect x="-3" y="-5" width="6" height="2" fill="#8A6848"/>
    <rect x="-3" y="-3" width="6" height="7" fill="#A88858"/>
    <line x1="-3" y1="-5" x2="-3" y2="6" stroke="#8A6848" strokeWidth="0.4"/>
    <line x1="3" y1="-5" x2="3" y2="6" stroke="#8A6848" strokeWidth="0.4"/>
    <line x1="-3" y1="-1" x2="3" y2="-1" stroke="#8A6848" strokeWidth="0.3"/>
    <line x1="-3" y1="2" x2="3" y2="2" stroke="#8A6848" strokeWidth="0.3"/>
    <rect x="-3.3" y="4" width="0.8" height="4" fill="#8A6848"/>
    <rect x="2.5" y="4" width="0.8" height="4" fill="#8A6848"/>
  </g>
        </svg>
      );

    case "backpack-europe":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#E8D8C0"/>
  <rect width="280" height="40" fill="#F0E0C8"/>
  <rect width="280" height="20" fill="#F5E8D2"/>
  <circle cx="232" cy="22" r="9" fill="#F8E5B0" opacity="0.65"/>
  <g fill="#B8A898" opacity="0.5">
    <g transform="translate(45, 45)">
      <polygon points="-2,28 -3,28 -1,2 1,2 3,28 2,28"/>
      <polygon points="-3,28 -6,28 -4,22 -3,22"/>
      <polygon points="3,28 6,28 4,22 3,22"/>
      <rect x="-0.3" y="-3" width="0.6" height="5"/>
      <rect x="-2" y="18" width="4" height="0.4"/>
      <rect x="-2.5" y="11" width="5" height="0.4"/>
    </g>
    <g transform="translate(95, 50)">
      <rect x="-3" y="0" width="6" height="23"/>
      <rect x="-4" y="-3" width="8" height="3"/>
      <polygon points="-4,-3 4,-3 0,-9"/>
      <rect x="-0.4" y="-12" width="0.8" height="3"/>
      <circle cx="0" cy="-1" r="1.6" fill="#A89878"/>
    </g>
  </g>
  <g fill="#9A8878" opacity="0.7">
    <g transform="translate(185, 60)">
      <ellipse cx="0" cy="12" rx="22" ry="2.5"/>
      <path d="M -22 12 L -22 -2 Q -22 -10 -10 -12 L 10 -12 Q 22 -10 22 -2 L 22 12 Z"/>
      <polygon points="14,-12 18,-7 21,-12 22,-9 22,-5 19,-5 17,-2 13,-7"/>
      <g fill="#7A6858">
        <ellipse cx="-14" cy="-3" rx="2" ry="3"/>
        <ellipse cx="-7" cy="-4" rx="2" ry="3.5"/>
        <ellipse cx="0" cy="-4" rx="2" ry="3.5"/>
        <ellipse cx="7" cy="-4" rx="2" ry="3.5"/>
        <ellipse cx="14" cy="-3" rx="2" ry="3"/>
        <ellipse cx="-14" cy="4" rx="2" ry="3"/>
        <ellipse cx="-7" cy="3" rx="2" ry="3.5"/>
        <ellipse cx="0" cy="3" rx="2" ry="3.5"/>
        <ellipse cx="7" cy="3" rx="2" ry="3.5"/>
        <ellipse cx="14" cy="4" rx="2" ry="3"/>
      </g>
    </g>
    <g transform="translate(245, 62)">
      <rect x="-12" y="0" width="24" height="14"/>
      <rect x="-13" y="-3" width="26" height="3"/>
      <g fill="#7A6858">
        <rect x="-11" y="2" width="2" height="11"/>
        <rect x="-6" y="2" width="2" height="11"/>
        <rect x="-1" y="2" width="2" height="11"/>
        <rect x="4" y="2" width="2" height="11"/>
        <rect x="9" y="2" width="2" height="11"/>
      </g>
      <g fill="#6A5A48">
        <rect x="-3" y="-7" width="2" height="4"/>
        <rect x="-3" y="-7" width="2" height="2"/>
        <rect x="1" y="-7" width="2" height="4"/>
      </g>
    </g>
  </g>
  <rect y="76" width="280" height="34" fill="#9A8878"/>
  <rect y="88" width="280" height="22" fill="#7A6858"/>
  <g fill="#5A4838" opacity="0.5">
    <ellipse cx="40" cy="98" rx="3" ry="0.7"/>
    <ellipse cx="80" cy="102" rx="3" ry="0.7"/>
    <ellipse cx="220" cy="100" rx="3" ry="0.7"/>
    <ellipse cx="255" cy="104" rx="3" ry="0.7"/>
  </g>
  <g transform="translate(140, 84)">
    <rect x="-6" y="-3" width="11" height="3" fill="#C84030"/>
    <rect x="-6" y="0" width="11" height="14" fill="#5A4030"/>
    <rect x="-6" y="0" width="11" height="2" fill="#3A2820"/>
    <line x1="-2.5" y1="0" x2="-2.5" y2="14" stroke="#3A2820" strokeWidth="0.4"/>
    <line x1="1.5" y1="0" x2="1.5" y2="14" stroke="#3A2820" strokeWidth="0.4"/>
    <rect x="-5" y="8" width="9" height="2.5" fill="#3A2820"/>
    <rect x="-4" y="14" width="9" height="11" fill="#3A4A5A"/>
    <circle cx="0.5" cy="-5" r="3" fill="#D8B898"/>
    <ellipse cx="0.5" cy="-8" rx="4" ry="0.7" fill="#5A4030"/>
    <rect x="-1.5" y="-10" width="4" height="2.5" fill="#5A4030"/>
    <rect x="-3" y="25" width="2.5" height="10" fill="#1F1A14"/>
    <rect x="0.5" y="25" width="2.5" height="10" fill="#1F1A14"/>
    <rect x="-3.5" y="34" width="3.5" height="2" fill="#0A0805"/>
    <rect x="0.5" y="34" width="3.5" height="2" fill="#0A0805"/>
  </g>
        </svg>
      );

    case "dogsled":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#A8C0D0"/>
  <rect width="280" height="40" fill="#C0D0DC"/>
  <rect width="280" height="20" fill="#D8E0E8"/>
  <path d="M 0 25 Q 70 18 140 22 T 280 18" fill="none" stroke="#5DCAA5" strokeWidth="5" opacity="0.3" strokeLinecap="round"/>
  <path d="M 0 25 Q 70 18 140 22 T 280 18" fill="none" stroke="#9FE1CB" strokeWidth="1.6" opacity="0.55" strokeLinecap="round"/>
  <path d="M 0 36 Q 80 30 160 33 T 280 31" fill="none" stroke="#7F77DD" strokeWidth="4" opacity="0.25" strokeLinecap="round"/>
  <path d="M 0 60 L 30 40 L 60 52 L 100 35 L 140 50 L 180 38 L 220 52 L 260 42 L 280 50 L 280 80 L 0 80 Z" fill="#7A8AA0" opacity="0.65"/>
  <path d="M 100 35 L 110 45 L 120 40 L 105 38 Z" fill="white"/>
  <path d="M 180 38 L 190 48 L 200 42 L 185 40 Z" fill="white"/>
  <path d="M 0 70 L 40 55 L 80 65 L 130 50 L 180 60 L 220 52 L 280 60 L 280 90 L 0 90 Z" fill="#6A7A88"/>
  <rect y="75" width="280" height="35" fill="#E8E5E0"/>
  <rect y="80" width="280" height="30" fill="#F0EDE5"/>
  <rect y="86" width="280" height="24" fill="#F5F2EA"/>
  <g fill="#1A2820">
    <polygon points="15,75 17,65 19,75"/>
    <polygon points="18,80 21,68 24,80"/>
    <polygon points="35,78 38,68 41,78"/>
    <polygon points="258,75 260,65 262,75"/>
    <polygon points="263,78 266,68 269,78"/>
  </g>
  <g transform="translate(225, 92)">
    <rect x="0" y="2" width="22" height="1.5" fill="#3A2820"/>
    <rect x="0" y="5" width="22" height="1.5" fill="#3A2820"/>
    <rect x="3" y="-6" width="16" height="6" fill="#5A4030"/>
    <line x1="3" y1="-3" x2="19" y2="-3" stroke="#3A2820" strokeWidth="0.3"/>
    <rect x="3" y="-8" width="16" height="2" fill="#3A2820"/>
    <rect x="9" y="-15" width="2" height="9" fill="#3A2820"/>
    <rect x="14" y="-15" width="2" height="9" fill="#3A2820"/>
    <ellipse cx="12" cy="-18" rx="3" ry="3.5" fill="#3A4A5A"/>
    <ellipse cx="12" cy="-20" rx="3" ry="1" fill="#1F1A14"/>
    <circle cx="12" cy="-17" r="1.2" fill="#D8B898"/>
  </g>
  <line x1="125" y1="93" x2="225" y2="93" stroke="#3A2820" strokeWidth="0.4"/>
  <line x1="125" y1="95" x2="225" y2="95" stroke="#3A2820" strokeWidth="0.4"/>
  <g transform="translate(195, 92)" fill="#3A2820">
    <ellipse cx="0" cy="0" rx="8" ry="3"/>
    <ellipse cx="7" cy="-1" rx="3" ry="2.5"/>
    <polygon points="9,-3 8,-5.5 11,-4"/>
    <polygon points="6,-3 5,-5.5 8,-4"/>
    <rect x="-4" y="2" width="1.4" height="3.5"/>
    <rect x="-1" y="2" width="1.4" height="3.5"/>
    <rect x="2" y="2" width="1.4" height="3.5"/>
    <rect x="5" y="2" width="1.4" height="3.5"/>
    <path d="M -8 -1 Q -10 -3 -10 0" stroke="#3A2820" strokeWidth="1.2" fill="none"/>
    <ellipse cx="-5" cy="0" rx="3" ry="1" fill="white" opacity="0.5"/>
    <circle cx="8" cy="-1" r="0.3" fill="white"/>
  </g>
  <g transform="translate(170, 93)" fill="#5A4838">
    <ellipse cx="0" cy="0" rx="8" ry="3"/>
    <ellipse cx="7" cy="-1" rx="3" ry="2.5"/>
    <polygon points="9,-3 8,-5.5 11,-4"/>
    <polygon points="6,-3 5,-5.5 8,-4"/>
    <rect x="-4" y="2" width="1.4" height="3.5"/>
    <rect x="-1" y="2" width="1.4" height="3.5"/>
    <rect x="2" y="2" width="1.4" height="3.5"/>
    <rect x="5" y="2" width="1.4" height="3.5"/>
    <path d="M -8 -1 Q -10 -3 -10 0" stroke="#5A4838" strokeWidth="1.2" fill="none"/>
    <ellipse cx="-3" cy="0" rx="4" ry="0.8" fill="white" opacity="0.4"/>
    <circle cx="8" cy="-1" r="0.3" fill="white"/>
  </g>
  <g transform="translate(145, 92)" fill="#3A2820">
    <ellipse cx="0" cy="0" rx="8" ry="3"/>
    <ellipse cx="7" cy="-1" rx="3" ry="2.5"/>
    <polygon points="9,-3 8,-5.5 11,-4"/>
    <polygon points="6,-3 5,-5.5 8,-4"/>
    <rect x="-4" y="2" width="1.4" height="3.5"/>
    <rect x="-1" y="2" width="1.4" height="3.5"/>
    <rect x="2" y="2" width="1.4" height="3.5"/>
    <rect x="5" y="2" width="1.4" height="3.5"/>
    <path d="M -8 -1 Q -10 -3 -10 0" stroke="#3A2820" strokeWidth="1.2" fill="none"/>
    <ellipse cx="-4" cy="0" rx="3" ry="1" fill="white" opacity="0.5"/>
    <circle cx="8" cy="-1" r="0.3" fill="white"/>
  </g>
  <g transform="translate(120, 93)" fill="#5A4838">
    <ellipse cx="0" cy="0" rx="8" ry="3"/>
    <ellipse cx="7" cy="-1" rx="3" ry="2.5"/>
    <polygon points="9,-3 8,-5.5 11,-4"/>
    <polygon points="6,-3 5,-5.5 8,-4"/>
    <rect x="-4" y="2" width="1.4" height="3.5"/>
    <rect x="-1" y="2" width="1.4" height="3.5"/>
    <rect x="2" y="2" width="1.4" height="3.5"/>
    <rect x="5" y="2" width="1.4" height="3.5"/>
    <ellipse cx="-3" cy="0" rx="4" ry="0.8" fill="white" opacity="0.4"/>
    <circle cx="8" cy="-1" r="0.3" fill="white"/>
  </g>
  <g stroke="#D0CDC0" strokeWidth="0.6" opacity="0.7">
    <line x1="248" y1="100" x2="280" y2="100"/>
    <line x1="248" y1="103" x2="280" y2="103"/>
  </g>
  <g fill="white" opacity="0.7">
    <circle cx="100" cy="92" r="0.6"/>
    <circle cx="110" cy="98" r="0.5"/>
    <circle cx="60" cy="100" r="0.6"/>
    <circle cx="75" cy="103" r="0.5"/>
    <circle cx="45" cy="105" r="0.6"/>
    <circle cx="270" cy="92" r="0.6"/>
    <circle cx="255" cy="96" r="0.5"/>
  </g>
        </svg>
      );

    case "castle-stay":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#2A3858"/>
  <rect width="280" height="50" fill="#3A4868"/>
  <rect width="280" height="25" fill="#5A5878"/>
  <g fill="white" opacity="0.85">
    <circle cx="30" cy="12" r="0.6"/>
    <circle cx="60" cy="10" r="0.5"/>
    <circle cx="110" cy="8" r="0.6"/>
    <circle cx="180" cy="14" r="0.5"/>
    <circle cx="240" cy="10" r="0.6"/>
    <circle cx="270" cy="16" r="0.5"/>
    <circle cx="90" cy="20" r="0.5"/>
    <circle cx="200" cy="22" r="0.5"/>
    <circle cx="155" cy="24" r="0.4"/>
  </g>
  <circle cx="220" cy="24" r="7" fill="#F5E8C8" opacity="0.9"/>
  <circle cx="218" cy="22" r="5" fill="#2A3858"/>
  <path d="M 0 60 L 30 50 L 60 56 L 100 45 L 140 52 L 180 42 L 220 50 L 260 45 L 280 50 L 280 80 L 0 80 Z" fill="#1F2848" opacity="0.85"/>
  <g transform="translate(140, 50)">
    <rect x="-32" y="20" width="64" height="35" fill="#9898A2"/>
    <g fill="#9898A2">
      <rect x="-32" y="15" width="4" height="5"/>
      <rect x="-25" y="15" width="4" height="5"/>
      <rect x="-18" y="15" width="4" height="5"/>
      <rect x="-11" y="15" width="4" height="5"/>
      <rect x="-4" y="15" width="4" height="5"/>
      <rect x="3" y="15" width="4" height="5"/>
      <rect x="10" y="15" width="4" height="5"/>
      <rect x="17" y="15" width="4" height="5"/>
      <rect x="24" y="15" width="4" height="5"/>
    </g>
    <rect x="-40" y="0" width="11" height="55" fill="#A8A8B0"/>
    <g fill="#A8A8B0">
      <rect x="-40" y="-5" width="3" height="5"/>
      <rect x="-36" y="-5" width="3" height="5"/>
      <rect x="-32" y="-5" width="3" height="5"/>
    </g>
    <polygon points="-41,0 -28,0 -34.5,-10" fill="#5A3020"/>
    <line x1="-34.5" y1="-12" x2="-34.5" y2="-10" stroke="#3A2418" strokeWidth="0.6"/>
    <rect x="29" y="0" width="11" height="55" fill="#A8A8B0"/>
    <g fill="#A8A8B0">
      <rect x="29" y="-5" width="3" height="5"/>
      <rect x="33" y="-5" width="3" height="5"/>
      <rect x="37" y="-5" width="3" height="5"/>
    </g>
    <polygon points="28,0 41,0 34.5,-10" fill="#5A3020"/>
    <line x1="34.5" y1="-12" x2="34.5" y2="-10" stroke="#3A2418" strokeWidth="0.6"/>
    <rect x="-7" y="-12" width="14" height="32" fill="#9898A2"/>
    <g fill="#9898A2">
      <rect x="-7" y="-17" width="3" height="5"/>
      <rect x="-3" y="-17" width="3" height="5"/>
      <rect x="1" y="-17" width="3" height="5"/>
      <rect x="5" y="-17" width="3" height="5"/>
    </g>
    <polygon points="-8,-12 8,-12 0,-24" fill="#5A3020"/>
    <line x1="0" y1="-26" x2="0" y2="-24" stroke="#3A2418" strokeWidth="0.6"/>
    <line x1="0" y1="-26" x2="0" y2="-30" stroke="#3A2820" strokeWidth="0.5"/>
    <polygon points="0,-30 6,-28.5 0,-27" fill="#C84030"/>
    <g fill="#F0D880">
      <rect x="-28" y="28" width="3" height="6"/>
      <rect x="-18" y="28" width="3" height="6"/>
      <rect x="-8" y="28" width="3" height="6"/>
      <rect x="18" y="28" width="3" height="6"/>
      <rect x="-23" y="42" width="3" height="6"/>
      <rect x="-13" y="42" width="3" height="6"/>
      <rect x="13" y="42" width="3" height="6"/>
      <rect x="23" y="42" width="3" height="6"/>
      <rect x="-36" y="10" width="2" height="4"/>
      <rect x="-36" y="22" width="2" height="4"/>
      <rect x="-36" y="35" width="2" height="4"/>
      <rect x="33" y="10" width="2" height="4"/>
      <rect x="33" y="22" width="2" height="4"/>
      <rect x="33" y="35" width="2" height="4"/>
      <rect x="-2" y="0" width="2" height="4"/>
      <rect x="0.5" y="0" width="2" height="4"/>
      <rect x="-1.5" y="10" width="3" height="5"/>
    </g>
    <path d="M -5 55 L -5 47 Q -5 43 0 43 Q 5 43 5 47 L 5 55 Z" fill="#1F1A14"/>
    <rect x="-5" y="54" width="10" height="2" fill="#3A2820"/>
    <rect x="-1" y="46" width="0.4" height="6" fill="#F0D880" opacity="0.7"/>
    <rect x="0.6" y="46" width="0.4" height="6" fill="#F0D880" opacity="0.7"/>
  </g>
  <rect y="88" width="280" height="22" fill="#2A3858"/>
  <rect y="92" width="280" height="18" fill="#3A4868"/>
  <g stroke="#5A6488" strokeWidth="0.3" opacity="0.5">
    <line x1="0" y1="96" x2="280" y2="96"/>
    <line x1="0" y1="100" x2="280" y2="100"/>
    <line x1="0" y1="105" x2="280" y2="105"/>
  </g>
  <g opacity="0.35">
    <rect x="100" y="92" width="80" height="16" fill="#9898A2"/>
    <rect x="103" y="92" width="6" height="14" fill="#A8A8B0"/>
    <rect x="170" y="92" width="6" height="14" fill="#A8A8B0"/>
  </g>
  <g fill="#F0D880" opacity="0.45">
    <rect x="112" y="93" width="2" height="3"/>
    <rect x="122" y="93" width="2" height="3"/>
    <rect x="158" y="93" width="2" height="3"/>
    <rect x="168" y="93" width="2" height="3"/>
  </g>
        </svg>
      );

    case "burning-man":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#3A1F3F"/>
  <rect width="280" height="65" fill="#7A2F4F"/>
  <rect width="280" height="40" fill="#C84830"/>
  <rect width="280" height="22" fill="#E89058"/>
  <rect width="280" height="10" fill="#F0C098"/>
  <path d="M 0 62 L 30 52 L 60 58 L 100 47 L 140 54 L 180 44 L 220 52 L 260 47 L 280 52 L 280 75 L 0 75 Z" fill="#1F1A1A" opacity="0.85"/>
  <rect y="68" width="280" height="42" fill="#5A4838"/>
  <rect y="75" width="280" height="35" fill="#4A3828"/>
  <rect y="86" width="280" height="24" fill="#3A2818"/>
  <ellipse cx="140" cy="68" rx="180" ry="3" fill="#8A6848" opacity="0.4"/>
  <g transform="translate(140, 78)">
    <rect x="-12" y="0" width="24" height="3" fill="#3A2818"/>
    <rect x="-3" y="-13" width="2.5" height="13" fill="#5A3020"/>
    <rect x="0.5" y="-13" width="2.5" height="13" fill="#5A3020"/>
    <polygon points="-8,-13 8,-13 6,-28 -6,-28" fill="#6A4030"/>
    <polygon points="-8,-13 8,-13 0,-18" fill="#5A3828"/>
    <rect x="-7" y="-24" width="14" height="1.5" fill="#3A1F18"/>
    <rect x="-5" y="-19" width="10" height="1.5" fill="#3A1F18"/>
    <polygon points="-2,-22 2,-22 0,-17" fill="#F8C038" opacity="0.6"/>
    <g transform="rotate(-35 -6 -28)">
      <rect x="-9" y="-29" width="3" height="13" fill="#6A4030"/>
      <polygon points="-9,-29 -6,-29 -7.5,-31" fill="#5A3020"/>
    </g>
    <g transform="rotate(35 6 -28)">
      <rect x="6" y="-29" width="3" height="13" fill="#6A4030"/>
      <polygon points="6,-29 9,-29 7.5,-31" fill="#5A3020"/>
    </g>
    <rect x="-3.5" y="-36" width="7" height="8" fill="#5A3020"/>
    <rect x="-2.5" y="-34" width="5" height="1" fill="#3A1F18"/>
    <rect x="-1.5" y="-32" width="3" height="0.8" fill="#3A1F18"/>
    <g fill="#F8E040">
      <path d="M -10 0 Q -8 -10 -5 -6 Q -2 -14 0 -10 Q 3 -16 5 -10 Q 8 -14 10 -6 Q 11 -10 13 0 Z"/>
    </g>
    <g fill="#F8C038">
      <path d="M -8 0 Q -6 -7 -3 -5 Q 0 -10 3 -5 Q 6 -7 8 0 Z"/>
    </g>
    <g fill="#E89058" opacity="0.85">
      <path d="M -5 0 Q -3 -4 -1 -3 Q 0 -7 1 -3 Q 3 -4 5 0 Z"/>
    </g>
    <g fill="#F8E8C8" opacity="0.7">
      <ellipse cx="0" cy="-2" rx="2" ry="1"/>
    </g>
  </g>
  <g fill="#1F1A14">
    <g transform="translate(50, 100)"><circle cx="0" cy="-3" r="1.2"/><rect x="-1" y="-2" width="2" height="4"/><rect x="-0.8" y="2" width="0.7" height="3"/><rect x="0.1" y="2" width="0.7" height="3"/></g>
    <g transform="translate(70, 102)"><circle cx="0" cy="-3" r="1"/><rect x="-0.8" y="-2" width="1.6" height="3.5"/><rect x="-0.6" y="1.5" width="0.5" height="2.5"/><rect x="0.1" y="1.5" width="0.5" height="2.5"/></g>
    <g transform="translate(90, 100)"><circle cx="0" cy="-3" r="1.2"/><rect x="-1" y="-2" width="2" height="4"/><rect x="-0.8" y="2" width="0.7" height="3"/><rect x="0.1" y="2" width="0.7" height="3"/></g>
    <g transform="translate(195, 101)"><circle cx="0" cy="-3" r="1.1"/><rect x="-0.9" y="-2" width="1.8" height="3.5"/><rect x="-0.7" y="1.5" width="0.6" height="2.5"/><rect x="0.2" y="1.5" width="0.6" height="2.5"/></g>
    <g transform="translate(215, 100)"><circle cx="0" cy="-3" r="1.2"/><rect x="-1" y="-2" width="2" height="4"/><rect x="-0.8" y="2" width="0.7" height="3"/><rect x="0.1" y="2" width="0.7" height="3"/></g>
    <g transform="translate(235, 103)"><circle cx="0" cy="-3" r="1"/><rect x="-0.8" y="-2" width="1.6" height="3.5"/><rect x="-0.6" y="1.5" width="0.5" height="2.5"/><rect x="0.1" y="1.5" width="0.5" height="2.5"/></g>
  </g>
  <g fill="#A88058" opacity="0.5">
    <circle cx="40" cy="82" r="0.4"/><circle cx="60" cy="86" r="0.4"/><circle cx="110" cy="84" r="0.4"/><circle cx="180" cy="83" r="0.4"/><circle cx="230" cy="87" r="0.4"/><circle cx="265" cy="85" r="0.4"/>
  </g>
        </svg>
      );

    case "world-cup":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#1A2848"/>
  <rect width="280" height="40" fill="#2A3858"/>
  <g fill="#F8E8C8">
    <circle cx="50" cy="14" r="1.8"/><circle cx="100" cy="11" r="1.8"/><circle cx="180" cy="11" r="1.8"/><circle cx="230" cy="14" r="1.8"/>
  </g>
  <g opacity="0.18">
    <polygon points="50,16 25,45 75,45" fill="#F8E8C8"/>
    <polygon points="100,13 75,45 125,45" fill="#F8E8C8"/>
    <polygon points="180,13 155,45 205,45" fill="#F8E8C8"/>
    <polygon points="230,16 205,45 255,45" fill="#F8E8C8"/>
  </g>
  <path d="M 0 38 Q 140 28 280 38 L 280 75 Q 140 65 0 75 Z" fill="#5A4A6A"/>
  <path d="M 0 42 Q 140 32 280 42 L 280 65 Q 140 55 0 65 Z" fill="#1A1A2A"/>
  <g fill="#F0C868" opacity="0.7">
    <circle cx="20" cy="48" r="0.45"/><circle cx="32" cy="50" r="0.4"/><circle cx="44" cy="47" r="0.45"/><circle cx="56" cy="49" r="0.4"/><circle cx="68" cy="46" r="0.45"/><circle cx="80" cy="48" r="0.4"/><circle cx="92" cy="47" r="0.45"/><circle cx="104" cy="49" r="0.4"/><circle cx="116" cy="46" r="0.45"/><circle cx="128" cy="48" r="0.4"/><circle cx="140" cy="47" r="0.45"/><circle cx="152" cy="49" r="0.4"/><circle cx="164" cy="46" r="0.45"/><circle cx="176" cy="48" r="0.4"/><circle cx="188" cy="47" r="0.45"/><circle cx="200" cy="49" r="0.4"/><circle cx="212" cy="46" r="0.45"/><circle cx="224" cy="48" r="0.4"/><circle cx="236" cy="47" r="0.45"/><circle cx="248" cy="49" r="0.4"/><circle cx="260" cy="46" r="0.45"/>
  </g>
  <g fill="#C84030" opacity="0.65">
    <circle cx="26" cy="55" r="0.45"/><circle cx="38" cy="57" r="0.4"/><circle cx="50" cy="54" r="0.45"/><circle cx="62" cy="56" r="0.4"/><circle cx="74" cy="53" r="0.45"/><circle cx="86" cy="55" r="0.4"/><circle cx="98" cy="54" r="0.45"/><circle cx="110" cy="56" r="0.4"/><circle cx="122" cy="53" r="0.45"/><circle cx="134" cy="55" r="0.4"/><circle cx="146" cy="54" r="0.45"/><circle cx="158" cy="56" r="0.4"/><circle cx="170" cy="53" r="0.45"/><circle cx="182" cy="55" r="0.4"/><circle cx="194" cy="54" r="0.45"/><circle cx="206" cy="56" r="0.4"/><circle cx="218" cy="53" r="0.45"/><circle cx="230" cy="55" r="0.4"/><circle cx="242" cy="54" r="0.45"/><circle cx="254" cy="56" r="0.4"/>
  </g>
  <g fill="white" opacity="0.6">
    <circle cx="30" cy="61" r="0.4"/><circle cx="42" cy="63" r="0.4"/><circle cx="54" cy="60" r="0.4"/><circle cx="66" cy="62" r="0.4"/><circle cx="78" cy="59" r="0.4"/><circle cx="90" cy="61" r="0.4"/><circle cx="102" cy="60" r="0.4"/><circle cx="114" cy="62" r="0.4"/><circle cx="126" cy="59" r="0.4"/><circle cx="138" cy="61" r="0.4"/><circle cx="150" cy="60" r="0.4"/><circle cx="162" cy="62" r="0.4"/><circle cx="174" cy="59" r="0.4"/><circle cx="186" cy="61" r="0.4"/><circle cx="198" cy="60" r="0.4"/><circle cx="210" cy="62" r="0.4"/><circle cx="222" cy="59" r="0.4"/><circle cx="234" cy="61" r="0.4"/><circle cx="246" cy="60" r="0.4"/><circle cx="258" cy="62" r="0.4"/>
  </g>
  <rect y="70" width="280" height="40" fill="#3A8A3A"/>
  <rect y="70" width="280" height="3" fill="#5AA85A" opacity="0.5"/>
  <rect y="80" width="280" height="3" fill="#2A7A2A" opacity="0.5"/>
  <line x1="140" y1="70" x2="140" y2="110" stroke="white" strokeWidth="0.6" opacity="0.85"/>
  <circle cx="140" cy="88" r="9" fill="none" stroke="white" strokeWidth="0.5" opacity="0.85"/>
  <circle cx="140" cy="88" r="0.7" fill="white" opacity="0.85"/>
  <rect x="0" y="83" width="22" height="20" fill="none" stroke="white" strokeWidth="0.5" opacity="0.85"/>
  <rect x="258" y="83" width="22" height="20" fill="none" stroke="white" strokeWidth="0.5" opacity="0.85"/>
  <rect x="0" y="88" width="8" height="10" fill="none" stroke="white" strokeWidth="0.5" opacity="0.85"/>
  <rect x="272" y="88" width="8" height="10" fill="none" stroke="white" strokeWidth="0.5" opacity="0.85"/>
  <g transform="translate(195, 82)">
    <circle cx="0" cy="0" r="5" fill="white"/>
    <polygon points="0,-3 2.5,-1.5 2,1.5 -2,1.5 -2.5,-1.5" fill="#1F1A14"/>
    <line x1="0" y1="-5" x2="0" y2="-3" stroke="#1F1A14" strokeWidth="0.6"/>
    <line x1="2.5" y1="-1.5" x2="4.5" y2="-2.5" stroke="#1F1A14" strokeWidth="0.5"/>
    <line x1="2" y1="1.5" x2="4" y2="3" stroke="#1F1A14" strokeWidth="0.5"/>
    <line x1="-2" y1="1.5" x2="-4" y2="3" stroke="#1F1A14" strokeWidth="0.5"/>
    <line x1="-2.5" y1="-1.5" x2="-4.5" y2="-2.5" stroke="#1F1A14" strokeWidth="0.5"/>
    <ellipse cx="0" cy="9" rx="4" ry="0.8" fill="#1F1A14" opacity="0.35"/>
  </g>
  <g transform="translate(120, 95)">
    <circle cx="0" cy="-4" r="1.4" fill="#D8B898"/>
    <rect x="-1.5" y="-2.8" width="3" height="4" fill="#C84030"/>
    <rect x="-1.5" y="-2.8" width="3" height="1.5" fill="#E84030"/>
    <rect x="-1.3" y="1.2" width="1" height="3.5" fill="white"/>
    <rect x="0.3" y="1.2" width="1" height="3.5" fill="white"/>
    <rect x="-1.5" y="4.5" width="1.2" height="1.2" fill="#1F1A14"/>
    <rect x="0.3" y="4.5" width="1.2" height="1.2" fill="#1F1A14"/>
  </g>
  <g transform="translate(165, 92)">
    <circle cx="0" cy="-4" r="1.4" fill="#D8B898"/>
    <rect x="-1.5" y="-2.8" width="3" height="4" fill="white"/>
    <rect x="-1.5" y="-2.8" width="3" height="1.5" fill="#F0F0F0"/>
    <rect x="-1.3" y="1.2" width="1" height="3.5" fill="#3A6AA8"/>
    <rect x="0.3" y="1.2" width="1" height="3.5" fill="#3A6AA8"/>
    <rect x="-1.5" y="4.5" width="1.2" height="1.2" fill="#1F1A14"/>
    <rect x="0.3" y="4.5" width="1.2" height="1.2" fill="#1F1A14"/>
  </g>
        </svg>
      );

    case "the-olympics":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0C098"/>
  <rect width="280" height="42" fill="#E8B888"/>
  <rect width="280" height="20" fill="#D8A878"/>
  <circle cx="50" cy="22" r="9" fill="#F8DCC0" opacity="0.75"/>
  <path d="M 0 60 Q 50 52 100 55 T 200 50 T 280 55 L 280 80 L 0 80 Z" fill="#A8806A" opacity="0.55"/>
  <path d="M 20 80 L 20 65 Q 20 55 40 53 L 240 53 Q 260 55 260 65 L 260 80 Z" fill="#E8C898"/>
  <g fill="#3A2820">
    <rect x="40" y="63" width="3" height="17"/><rect x="55" y="63" width="3" height="17"/><rect x="70" y="63" width="3" height="17"/><rect x="85" y="63" width="3" height="17"/><rect x="100" y="63" width="3" height="17"/><rect x="180" y="63" width="3" height="17"/><rect x="195" y="63" width="3" height="17"/><rect x="210" y="63" width="3" height="17"/><rect x="225" y="63" width="3" height="17"/><rect x="240" y="63" width="3" height="17"/>
  </g>
  <line x1="20" y1="58" x2="260" y2="58" stroke="#A88858" strokeWidth="0.8"/>
  <g transform="translate(140, 30)">
    <circle cx="-22" cy="0" r="4.5" fill="none" stroke="#3A6AA8" strokeWidth="1.6"/>
    <circle cx="-11" cy="3" r="4.5" fill="none" stroke="#F8E040" strokeWidth="1.6"/>
    <circle cx="0" cy="0" r="4.5" fill="none" stroke="#1F1A14" strokeWidth="1.6"/>
    <circle cx="11" cy="3" r="4.5" fill="none" stroke="#3A7A3A" strokeWidth="1.6"/>
    <circle cx="22" cy="0" r="4.5" fill="none" stroke="#C84030" strokeWidth="1.6"/>
  </g>
  <g transform="translate(140, 78)">
    <ellipse cx="0" cy="20" rx="22" ry="3" fill="#3A2818"/>
    <rect x="-18" y="14" width="36" height="6" fill="#7A5040"/>
    <polygon points="-18,14 18,14 14,6 -14,6" fill="#8A6048"/>
    <rect x="-3" y="-8" width="6" height="14" fill="#7A5040"/>
    <rect x="-3" y="-8" width="6" height="1.5" fill="#5A4030"/>
    <path d="M -10 -8 L 10 -8 L 8 -15 L -8 -15 Z" fill="#5A4030"/>
    <ellipse cx="0" cy="-15" rx="8" ry="2" fill="#3A2818"/>
    <ellipse cx="0" cy="-15" rx="6" ry="1.2" fill="#1F1A14"/>
    <path d="M -6 -15 Q -4 -28 -1 -23 Q 0 -32 1 -23 Q 4 -28 6 -15 Z" fill="#F8E040"/>
    <path d="M -4 -15 Q -2 -23 0 -20 Q 2 -25 2 -22 Q 4 -23 4 -15 Z" fill="#F8C038"/>
    <path d="M -2 -15 Q 0 -20 0 -18 Q 1 -22 2 -18 Q 2 -20 2 -15 Z" fill="#E89058"/>
    <ellipse cx="0" cy="-17" rx="1.5" ry="2" fill="#F8E8C8" opacity="0.85"/>
  </g>
  <g transform="translate(80, 91)" fill="#1F1A14">
    <circle cx="0" cy="-7" r="2"/>
    <rect x="-1.5" y="-5.5" width="3" height="6" fill="#C84030"/>
    <rect x="-1.5" y="0.5" width="1.3" height="6"/>
    <rect x="0.2" y="0.5" width="1.3" height="6"/>
    <line x1="2" y1="-3" x2="7" y2="-9" stroke="#1F1A14" strokeWidth="0.8"/>
    <rect x="6" y="-11" width="2.5" height="3"/>
    <path d="M 6 -11 Q 7.2 -15 8.5 -11 Z" fill="#F8C038"/>
    <path d="M 6.5 -11 Q 7.2 -13 8 -11 Z" fill="#F8E8C8"/>
  </g>
  <rect y="85" width="280" height="25" fill="#8A6848"/>
  <g fill="#7A5840">
    <ellipse cx="50" cy="92" rx="6" ry="1.5"/>
    <ellipse cx="220" cy="94" rx="8" ry="1.5"/>
  </g>
        </svg>
      );

    case "love-abroad":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0B098"/>
  <rect width="280" height="42" fill="#E89878"/>
  <rect width="280" height="20" fill="#D88858"/>
  <circle cx="220" cy="32" r="11" fill="#F8DCC0" opacity="0.85"/>
  <g fill="#5A4838" opacity="0.7">
    <rect x="30" y="50" width="20" height="20"/>
    <polygon points="30,50 50,50 40,42"/>
    <rect x="56" y="32" width="9" height="38"/>
    <polygon points="56,32 65,32 60.5,25"/>
    <rect x="60" y="22" width="1" height="3"/>
    <rect x="56" y="40" width="9" height="2" fill="#3A2818"/>
    <rect x="74" y="55" width="28" height="15"/>
    <polygon points="74,55 102,55 96,48 80,48"/>
    <rect x="108" y="50" width="22" height="20"/>
    <ellipse cx="119" cy="50" rx="11" ry="6"/>
    <rect x="118" y="42" width="2" height="3"/>
    <rect x="136" y="52" width="18" height="18"/>
    <polygon points="136,52 154,52 145,46"/>
    <rect x="160" y="56" width="14" height="14"/>
    <rect x="180" y="58" width="38" height="3"/>
    <path d="M 188 61 Q 199 65 210 61 L 210 70 L 188 70 Z" fill="#5A4838"/>
    <rect x="220" y="54" width="20" height="16"/>
    <polygon points="220,54 240,54 230,47"/>
    <rect x="245" y="58" width="15" height="12"/>
  </g>
  <g fill="#F0D8B0" opacity="0.5">
    <rect x="38" y="58" width="2" height="3"/><rect x="42" y="58" width="2" height="3"/><rect x="38" y="64" width="2" height="3"/><rect x="42" y="64" width="2" height="3"/>
    <rect x="78" y="60" width="2" height="3"/><rect x="82" y="60" width="2" height="3"/><rect x="86" y="60" width="2" height="3"/><rect x="90" y="60" width="2" height="3"/><rect x="94" y="60" width="2" height="3"/>
    <rect x="113" y="58" width="2" height="3"/><rect x="117" y="58" width="2" height="3"/><rect x="121" y="58" width="2" height="3"/><rect x="125" y="58" width="2" height="3"/>
    <rect x="113" y="64" width="2" height="3"/><rect x="121" y="64" width="2" height="3"/>
    <rect x="140" y="58" width="2" height="3"/><rect x="144" y="58" width="2" height="3"/><rect x="148" y="58" width="2" height="3"/>
    <rect x="164" y="62" width="2" height="3"/><rect x="168" y="62" width="2" height="3"/>
    <rect x="224" y="58" width="2" height="3"/><rect x="228" y="58" width="2" height="3"/><rect x="232" y="58" width="2" height="3"/><rect x="236" y="58" width="2" height="3"/>
    <rect x="248" y="62" width="2" height="3"/><rect x="252" y="62" width="2" height="3"/><rect x="256" y="62" width="2" height="3"/>
  </g>
  <rect y="68" width="280" height="42" fill="#5A4858"/>
  <rect y="70" width="280" height="40" fill="#7A6878"/>
  <g fill="#F0B098" opacity="0.35">
    <ellipse cx="50" cy="75" rx="20" ry="1"/>
    <ellipse cx="120" cy="78" rx="15" ry="0.8"/>
    <ellipse cx="220" cy="80" rx="20" ry="0.8"/>
  </g>
  <g stroke="#9A8898" strokeWidth="0.3" opacity="0.55">
    <line x1="0" y1="84" x2="280" y2="84"/>
    <line x1="0" y1="90" x2="280" y2="90"/>
    <line x1="0" y1="98" x2="280" y2="98"/>
    <line x1="0" y1="105" x2="280" y2="105"/>
  </g>
  <g transform="translate(140, 92)">
    <path d="M -50 0 Q -52 5 -45 7 L 38 7 Q 50 5 52 0 Q 52 -2 47 -3 L -37 -3 Q -48 -3 -50 0 Z" fill="#1F1A14"/>
    <path d="M -50 0 L -58 -8 L -55 -2 L -60 -5 L -55 -1 L -52 1 Z" fill="#1F1A14"/>
    <path d="M 52 0 Q 56 -2 55 -5 L 53 -3" stroke="#1F1A14" strokeWidth="1" fill="none"/>
    <rect x="-18" y="-3" width="34" height="2" fill="#5A3020"/>
    <g transform="translate(-7, -5)">
      <circle cx="0" cy="-3.5" r="1.8" fill="#D8B898"/>
      <rect x="-1.5" y="-2" width="3" height="3" fill="#C84030"/>
      <rect x="-1.5" y="-2" width="3" height="0.6" fill="#1F1A14"/>
      <ellipse cx="0" cy="-6" rx="2" ry="0.7" fill="#3A2820"/>
    </g>
    <g transform="translate(7, -5)">
      <circle cx="0" cy="-3.5" r="1.8" fill="#D8B898"/>
      <rect x="-1.5" y="-2" width="3" height="3" fill="#3A4A5A"/>
      <rect x="-1.5" y="-2" width="3" height="0.6" fill="#1F1A14"/>
      <ellipse cx="0" cy="-6" rx="2" ry="0.7" fill="#5A3020"/>
    </g>
    <g transform="translate(35, -2)">
      <circle cx="0" cy="-12" r="2" fill="#D8B898"/>
      <rect x="-2.2" y="-10" width="4.4" height="7" fill="white"/>
      <rect x="-2.2" y="-7" width="4.4" height="1" fill="#1F1A14"/>
      <rect x="-2.2" y="-8.5" width="4.4" height="0.6" fill="#1F1A14"/>
      <rect x="-1.7" y="-3" width="1.5" height="4" fill="#1F1A14"/>
      <rect x="0.2" y="-3" width="1.5" height="4" fill="#1F1A14"/>
      <line x1="2.5" y1="-9" x2="14" y2="2" stroke="#5A4030" strokeWidth="0.7"/>
      <ellipse cx="15" cy="3" rx="2.5" ry="0.8" fill="#5A4030"/>
    </g>
  </g>
  <g transform="translate(60, 22)" fill="#C84030" opacity="0.7">
    <path d="M 0 4 L -3 1 Q -5 -2 -3 -3 Q -1 -3 0 -1 Q 1 -3 3 -3 Q 5 -2 3 1 Z"/>
  </g>
        </svg>
      );

    case "take-a-cruise":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0B098"/>
  <rect width="280" height="42" fill="#E89878"/>
  <rect width="280" height="22" fill="#D88858"/>
  <circle cx="230" cy="48" r="13" fill="#F8DCA0" opacity="0.9"/>
  <circle cx="230" cy="48" r="8" fill="#FAEAC8"/>
  <g fill="white" opacity="0.55">
    <ellipse cx="40" cy="18" rx="20" ry="3.5"/>
    <ellipse cx="35" cy="15" rx="11" ry="3"/>
  </g>
  <rect y="62" width="280" height="48" fill="#3A6AA8"/>
  <rect y="62" width="280" height="2" fill="#5A8AC8" opacity="0.7"/>
  <g fill="#F8DCA0" opacity="0.55">
    <ellipse cx="230" cy="68" rx="20" ry="1"/>
    <ellipse cx="230" cy="74" rx="18" ry="0.9"/>
    <ellipse cx="230" cy="80" rx="14" ry="0.7"/>
    <ellipse cx="230" cy="86" rx="11" ry="0.6"/>
    <ellipse cx="230" cy="92" rx="9" ry="0.5"/>
    <ellipse cx="230" cy="98" rx="7" ry="0.4"/>
  </g>
  <g stroke="#5A8AC8" strokeWidth="0.4" opacity="0.55">
    <line x1="0" y1="76" x2="40" y2="76"/>
    <line x1="0" y1="84" x2="55" y2="84"/>
    <line x1="50" y1="92" x2="150" y2="92"/>
    <line x1="0" y1="100" x2="280" y2="100"/>
    <line x1="180" y1="78" x2="220" y2="78"/>
  </g>
  <g transform="translate(105, 58)">
    <path d="M -88 12 L -82 22 L 84 22 L 90 12 Z" fill="white"/>
    <rect x="-82" y="2" width="166" height="10" fill="white"/>
    <rect x="-82" y="9" width="166" height="2" fill="#1F1A14"/>
    <rect x="-78" y="0" width="156" height="2" fill="#A88858"/>
    <g fill="#F8E5C8">
      <rect x="-72" y="0" width="6" height="2"/><rect x="-60" y="0" width="6" height="2"/><rect x="-48" y="0" width="6" height="2"/><rect x="-36" y="0" width="6" height="2"/><rect x="-24" y="0" width="6" height="2"/><rect x="-12" y="0" width="6" height="2"/><rect x="0" y="0" width="6" height="2"/><rect x="12" y="0" width="6" height="2"/><rect x="24" y="0" width="6" height="2"/><rect x="36" y="0" width="6" height="2"/><rect x="48" y="0" width="6" height="2"/><rect x="60" y="0" width="6" height="2"/><rect x="72" y="0" width="6" height="2"/>
    </g>
    <g fill="#3A6AA8">
      <rect x="-78" y="5" width="2" height="2.5"/><rect x="-72" y="5" width="2" height="2.5"/><rect x="-66" y="5" width="2" height="2.5"/><rect x="-60" y="5" width="2" height="2.5"/><rect x="-54" y="5" width="2" height="2.5"/><rect x="-48" y="5" width="2" height="2.5"/><rect x="-42" y="5" width="2" height="2.5"/><rect x="-36" y="5" width="2" height="2.5"/><rect x="-30" y="5" width="2" height="2.5"/><rect x="-24" y="5" width="2" height="2.5"/><rect x="-18" y="5" width="2" height="2.5"/><rect x="-12" y="5" width="2" height="2.5"/><rect x="-6" y="5" width="2" height="2.5"/><rect x="0" y="5" width="2" height="2.5"/><rect x="6" y="5" width="2" height="2.5"/><rect x="12" y="5" width="2" height="2.5"/><rect x="18" y="5" width="2" height="2.5"/><rect x="24" y="5" width="2" height="2.5"/><rect x="30" y="5" width="2" height="2.5"/><rect x="36" y="5" width="2" height="2.5"/><rect x="42" y="5" width="2" height="2.5"/><rect x="48" y="5" width="2" height="2.5"/><rect x="54" y="5" width="2" height="2.5"/><rect x="60" y="5" width="2" height="2.5"/><rect x="66" y="5" width="2" height="2.5"/><rect x="72" y="5" width="2" height="2.5"/><rect x="78" y="5" width="2" height="2.5"/>
    </g>
    <rect x="-76" y="-8" width="152" height="8" fill="white"/>
    <g fill="#3A6AA8">
      <rect x="-72" y="-5" width="2" height="3"/><rect x="-66" y="-5" width="2" height="3"/><rect x="-60" y="-5" width="2" height="3"/><rect x="-54" y="-5" width="2" height="3"/><rect x="-48" y="-5" width="2" height="3"/><rect x="-42" y="-5" width="2" height="3"/><rect x="-36" y="-5" width="2" height="3"/><rect x="-30" y="-5" width="2" height="3"/><rect x="-24" y="-5" width="2" height="3"/><rect x="-18" y="-5" width="2" height="3"/><rect x="-12" y="-5" width="2" height="3"/><rect x="-6" y="-5" width="2" height="3"/><rect x="0" y="-5" width="2" height="3"/><rect x="6" y="-5" width="2" height="3"/><rect x="12" y="-5" width="2" height="3"/><rect x="18" y="-5" width="2" height="3"/><rect x="24" y="-5" width="2" height="3"/><rect x="30" y="-5" width="2" height="3"/><rect x="36" y="-5" width="2" height="3"/><rect x="42" y="-5" width="2" height="3"/><rect x="48" y="-5" width="2" height="3"/><rect x="54" y="-5" width="2" height="3"/><rect x="60" y="-5" width="2" height="3"/><rect x="66" y="-5" width="2" height="3"/><rect x="72" y="-5" width="2" height="3"/>
    </g>
    <rect x="-68" y="-15" width="136" height="7" fill="white"/>
    <g fill="#3A6AA8">
      <rect x="-64" y="-13" width="2" height="3"/><rect x="-58" y="-13" width="2" height="3"/><rect x="-52" y="-13" width="2" height="3"/><rect x="-46" y="-13" width="2" height="3"/><rect x="-40" y="-13" width="2" height="3"/><rect x="-34" y="-13" width="2" height="3"/><rect x="-28" y="-13" width="2" height="3"/><rect x="-22" y="-13" width="2" height="3"/><rect x="-16" y="-13" width="2" height="3"/><rect x="-10" y="-13" width="2" height="3"/><rect x="-4" y="-13" width="2" height="3"/><rect x="2" y="-13" width="2" height="3"/><rect x="8" y="-13" width="2" height="3"/><rect x="14" y="-13" width="2" height="3"/><rect x="20" y="-13" width="2" height="3"/><rect x="26" y="-13" width="2" height="3"/><rect x="32" y="-13" width="2" height="3"/><rect x="38" y="-13" width="2" height="3"/><rect x="44" y="-13" width="2" height="3"/><rect x="50" y="-13" width="2" height="3"/><rect x="56" y="-13" width="2" height="3"/><rect x="62" y="-13" width="2" height="3"/>
    </g>
    <rect x="-58" y="-22" width="116" height="7" fill="white"/>
    <g fill="#3A6AA8">
      <rect x="-54" y="-20" width="2" height="3"/><rect x="-48" y="-20" width="2" height="3"/><rect x="-42" y="-20" width="2" height="3"/><rect x="-36" y="-20" width="2" height="3"/><rect x="-30" y="-20" width="2" height="3"/><rect x="-24" y="-20" width="2" height="3"/><rect x="-18" y="-20" width="2" height="3"/><rect x="-12" y="-20" width="2" height="3"/><rect x="-6" y="-20" width="2" height="3"/><rect x="0" y="-20" width="2" height="3"/><rect x="6" y="-20" width="2" height="3"/><rect x="12" y="-20" width="2" height="3"/><rect x="18" y="-20" width="2" height="3"/><rect x="24" y="-20" width="2" height="3"/><rect x="30" y="-20" width="2" height="3"/><rect x="36" y="-20" width="2" height="3"/><rect x="42" y="-20" width="2" height="3"/><rect x="48" y="-20" width="2" height="3"/><rect x="54" y="-20" width="2" height="3"/>
    </g>
    <rect x="40" y="-28" width="22" height="6" fill="white"/>
    <g fill="#3A6AA8">
      <rect x="42" y="-26" width="2" height="2.5"/><rect x="46" y="-26" width="2" height="2.5"/><rect x="50" y="-26" width="2" height="2.5"/><rect x="54" y="-26" width="2" height="2.5"/><rect x="58" y="-26" width="2" height="2.5"/>
    </g>
    <rect x="-14" y="-36" width="14" height="14" fill="#3A2820"/>
    <rect x="-14" y="-38" width="14" height="3" fill="#1F1A14"/>
    <rect x="-16" y="-40" width="18" height="3" fill="#3A2820"/>
    <circle cx="-7" cy="-30" r="2.2" fill="#C84030"/>
    <line x1="14" y1="-36" x2="14" y2="-44" stroke="#1F1A14" strokeWidth="0.5"/>
    <polygon points="14,-44 21,-42 14,-40" fill="#C84030"/>
  </g>
  <g fill="white" opacity="0.75">
    <ellipse cx="20" cy="82" rx="14" ry="1.5"/>
    <ellipse cx="30" cy="86" rx="18" ry="1.5"/>
    <ellipse cx="38" cy="90" rx="14" ry="1.2"/>
  </g>
        </svg>
      );

    case "petra":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#8B4A3C"/>
          <polygon points="115,0 165,0 162,42 118,42" fill="#F0C898"/>
          <polygon points="115,0 165,0 158,30 122,30" fill="#F5D8A8"/>
          <polygon points="0,0 115,0 122,30 118,55 108,80 95,110 0,110" fill="#A06050"/>
          <polygon points="0,0 75,0 90,32 75,65 50,95 0,110" fill="#8B4A3C"/>
          <polygon points="0,0 40,0 55,28 35,60 0,90" fill="#6E382C"/>
          <polygon points="165,0 280,0 280,110 195,108 180,80 168,55 162,30" fill="#A06050"/>
          <polygon points="205,0 280,0 280,110 230,90 200,60 195,30" fill="#8B4A3C"/>
          <polygon points="240,0 280,0 280,90 250,60 245,30" fill="#6E382C"/>
          <rect x="113" y="40" width="54" height="58" fill="#C88070"/>
          <rect x="113" y="40" width="54" height="6" fill="#A06050"/>
          <polygon points="113,46 167,46 162,38 118,38" fill="#A06050"/>
          <rect x="119" y="58" width="3.5" height="40" fill="#9A5A4C"/>
          <rect x="126" y="58" width="3.5" height="40" fill="#9A5A4C"/>
          <rect x="151" y="58" width="3.5" height="40" fill="#9A5A4C"/>
          <rect x="158" y="58" width="3.5" height="40" fill="#9A5A4C"/>
          <rect x="119" y="55" width="42" height="3" fill="#8B4A3C"/>
          <rect x="119" y="98" width="42" height="3" fill="#8B4A3C"/>
          <rect x="136" y="65" width="9" height="35" fill="#3A1F18"/>
          <polygon points="136,65 145,65 140.5,60" fill="#3A1F18"/>
          <circle cx="140.5" cy="34" r="3.5" fill="#A06050"/>
          <rect x="139.5" y="30" width="2" height="2" fill="#A06050"/>
          <rect x="0" y="100" width="280" height="10" fill="#8A5040"/>
          <g transform="translate(98, 92)">
            <rect x="0" y="0" width="1.6" height="6" fill="#3A1F18"/>
            <rect x="-0.3" y="3" width="2.2" height="3" fill="#3A1F18"/>
            <circle cx="0.8" cy="-1" r="1" fill="#3A1F18"/>
          </g>
          <g transform="translate(180, 94)">
            <ellipse cx="4" cy="2" rx="4.5" ry="1.5" fill="#3A1F18"/>
            <polygon points="2,2 4,-0.5 6,2" fill="#3A1F18"/>
            <rect x="1" y="2" width="0.8" height="4" fill="#3A1F18"/>
            <rect x="2.8" y="2" width="0.8" height="4" fill="#3A1F18"/>
            <rect x="5.5" y="2" width="0.8" height="4" fill="#3A1F18"/>
            <rect x="7.3" y="2" width="0.8" height="4" fill="#3A1F18"/>
            <rect x="-0.3" y="-0.5" width="1" height="3.5" fill="#3A1F18"/>
            <ellipse cx="0.2" cy="-1.3" rx="1" ry="0.8" fill="#3A1F18"/>
          </g>
        </svg>
      );

    case "machu-picchu":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F2D9C7"/>
          <rect width="280" height="30" fill="#EDC8B0"/>
          <circle cx="170" cy="38" r="12" fill="#F7E4CE"/>
          <circle cx="170" cy="38" r="7" fill="#F5D2B0"/>
          <path d="M 0 55 Q 50 48 90 52 T 180 47 T 280 53 L 280 75 L 0 75 Z" fill="#B5A8A0"/>
          <path d="M 0 65 L 25 58 L 55 62 L 90 53 L 130 60 L 160 55 L 195 62 L 230 57 L 280 62 L 280 80 L 0 80 Z" fill="#8A9991"/>
          <path d="M 100 80 L 142 28 L 158 6 L 174 28 L 205 70 L 225 80 Z" fill="#5C6F66"/>
          <path d="M 158 6 L 174 28 L 158 36 Z" fill="#4A5C54"/>
          <ellipse cx="50" cy="55" rx="55" ry="2" fill="#F2D9C7" opacity="0.45"/>
          <ellipse cx="230" cy="60" rx="45" ry="1.5" fill="#F2D9C7" opacity="0.4"/>
          <rect x="0" y="72" width="280" height="6" fill="#7A9355"/>
          <rect x="0" y="78" width="280" height="1" fill="#8A7B6A"/>
          <rect x="0" y="79" width="280" height="8" fill="#6A8246"/>
          <rect x="0" y="87" width="280" height="1" fill="#8A7B6A"/>
          <rect x="0" y="88" width="280" height="22" fill="#5A7138"/>
          <g transform="translate(115, 64)">
            <rect x="0" y="0" width="6" height="7" fill="#A89A88"/>
            <polygon points="0,0 6,0 3,-3" fill="#5C5046"/>
            <rect x="8" y="-2" width="8" height="9" fill="#B8AA98"/>
            <polygon points="8,-2 16,-2 12,-5" fill="#5C5046"/>
            <rect x="18" y="-1" width="7" height="8" fill="#A89A88"/>
            <polygon points="18,-1 25,-1 21.5,-4" fill="#5C5046"/>
            <rect x="27" y="-3" width="9" height="10" fill="#B8AA98"/>
            <polygon points="27,-3 36,-3 31.5,-6.5" fill="#5C5046"/>
            <rect x="38" y="-1" width="7" height="8" fill="#A89A88"/>
            <polygon points="38,-1 45,-1 41.5,-4" fill="#5C5046"/>
            <rect x="47" y="0" width="6" height="7" fill="#A89A88"/>
            <polygon points="47,0 53,0 50,-3" fill="#5C5046"/>
            <rect x="2" y="9" width="5" height="4" fill="#8A7B6A"/>
            <polygon points="2,9 7,9 4.5,7" fill="#5C5046"/>
            <rect x="10" y="9" width="6" height="4" fill="#8A7B6A"/>
            <polygon points="10,9 16,9 13,7" fill="#5C5046"/>
            <rect x="20" y="10" width="5" height="3" fill="#8A7B6A"/>
            <polygon points="20,10 25,10 22.5,8" fill="#5C5046"/>
            <rect x="29" y="9" width="6" height="4" fill="#8A7B6A"/>
            <polygon points="29,9 35,9 32,7" fill="#5C5046"/>
            <rect x="40" y="10" width="5" height="3" fill="#8A7B6A"/>
            <polygon points="40,10 45,10 42.5,8" fill="#5C5046"/>
          </g>
          <g stroke="#8A7B6A" strokeWidth="0.4" opacity="0.5">
            <line x1="60" y1="72" x2="60" y2="78"/>
            <line x1="220" y1="72" x2="220" y2="78"/>
            <line x1="40" y1="79" x2="40" y2="87"/>
            <line x1="240" y1="79" x2="240" y2="87"/>
          </g>
          <g transform="translate(35, 100)">
            <ellipse cx="6" cy="0" rx="6" ry="2.3" fill="#F0E2C8"/>
            <rect x="1.5" y="-0.5" width="1.1" height="5.5" fill="#F0E2C8"/>
            <rect x="4" y="-0.5" width="1.1" height="5.5" fill="#F0E2C8"/>
            <rect x="8" y="-0.5" width="1.1" height="5.5" fill="#F0E2C8"/>
            <rect x="10.5" y="-0.5" width="1.1" height="5.5" fill="#F0E2C8"/>
            <rect x="-1" y="-7" width="1.7" height="7.5" fill="#F0E2C8"/>
            <ellipse cx="-0.2" cy="-7.8" rx="1.7" ry="1.3" fill="#F0E2C8"/>
            <rect x="-1.2" y="-9.5" width="0.7" height="2" fill="#F0E2C8"/>
            <rect x="0.2" y="-9.5" width="0.7" height="2" fill="#F0E2C8"/>
          </g>
          <g transform="translate(225, 102)">
            <ellipse cx="5" cy="0" rx="5" ry="2" fill="#F0E2C8"/>
            <rect x="1.5" y="-0.5" width="0.9" height="5" fill="#F0E2C8"/>
            <rect x="3.5" y="-0.5" width="0.9" height="5" fill="#F0E2C8"/>
            <rect x="7" y="-0.5" width="0.9" height="5" fill="#F0E2C8"/>
            <rect x="9" y="-0.5" width="0.9" height="5" fill="#F0E2C8"/>
            <rect x="-0.8" y="-6" width="1.5" height="6.5" fill="#F0E2C8"/>
            <ellipse cx="-0.1" cy="-6.8" rx="1.5" ry="1.1" fill="#F0E2C8"/>
          </g>
        </svg>
      );

    case "camino-santiago":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0D098"/>
          <rect width="280" height="40" fill="#E8C078"/>
          <rect width="280" height="20" fill="#E0B068"/>
          <circle cx="60" cy="22" r="9" fill="#F8E0B0" opacity="0.75"/>
          <path d="M 0 55 Q 60 48 120 52 T 240 50 T 280 52 L 280 75 L 0 75 Z" fill="#7A8068" opacity="0.65"/>
          <path d="M 0 65 Q 50 58 100 62 T 200 60 T 280 65 L 280 85 L 0 85 Z" fill="#688058"/>
          <g transform="translate(225, 50)" opacity="0.75">
            <rect x="-15" y="0" width="30" height="20" fill="#5A4838"/>
            <rect x="-15" y="-15" width="8" height="35" fill="#5A4838"/>
            <rect x="7" y="-15" width="8" height="35" fill="#5A4838"/>
            <polygon points="-15,-15 -7,-15 -11,-22" fill="#5A4838"/>
            <polygon points="7,-15 15,-15 11,-22" fill="#5A4838"/>
            <line x1="-11" y1="-22" x2="-11" y2="-26" stroke="#5A4838" strokeWidth="0.5"/>
            <line x1="11" y1="-22" x2="11" y2="-26" stroke="#5A4838" strokeWidth="0.5"/>
            <ellipse cx="0" cy="0" rx="7" ry="3" fill="#5A4838"/>
            <rect x="-7" y="0" width="14" height="12" fill="#5A4838"/>
            <line x1="0" y1="-3" x2="0" y2="-8" stroke="#5A4838" strokeWidth="0.6"/>
            <line x1="-1" y1="-6" x2="1" y2="-6" stroke="#5A4838" strokeWidth="0.6"/>
            <rect x="-3" y="14" width="2" height="6" fill="#3A2820"/>
            <rect x="-7" y="6" width="1.5" height="3" fill="#3A2820"/>
            <rect x="5.5" y="6" width="1.5" height="3" fill="#3A2820"/>
          </g>
          <path d="M 0 75 Q 40 70 80 75 T 160 80 T 280 78 L 280 110 L 0 110 Z" fill="#7A8858"/>
          <path d="M 100 110 Q 130 100 150 85 Q 175 76 220 70 Q 245 67 280 65" fill="none" stroke="#C8A878" strokeWidth="6"/>
          <path d="M 100 110 Q 130 100 150 85 Q 175 76 220 70 Q 245 67 280 65" fill="none" stroke="#D8B888" strokeWidth="3.5"/>
          <g transform="translate(140, 78)">
            <ellipse cx="-2" cy="-6" rx="4" ry="3" fill="#5A4030"/>
            <rect x="-3" y="-5" width="5" height="9" fill="#3A2820"/>
            <circle cx="-0.5" cy="-9" r="2" fill="#D8B898"/>
            <ellipse cx="-0.5" cy="-11" rx="3.2" ry="1" fill="#5A4030"/>
            <rect x="-2.5" y="-13" width="4" height="2.5" fill="#5A4030"/>
            <rect x="-2" y="4" width="2" height="8" fill="#3A2820"/>
            <rect x="0.5" y="4" width="2" height="8" fill="#3A2820"/>
            <line x1="3" y1="-7" x2="6" y2="12" stroke="#5A4030" strokeWidth="0.9"/>
            <circle cx="3" cy="-8" r="0.8" fill="#5A4030"/>
          </g>
          <g transform="translate(40, 95)">
            <rect x="-3" y="0" width="6" height="14" fill="#9A8868"/>
            <rect x="-3.5" y="0" width="7" height="2" fill="#7A6850"/>
            <rect x="-4" y="2" width="8" height="6" fill="#F8E040"/>
            <rect x="-4" y="2" width="8" height="6" fill="none" stroke="#5A4030" strokeWidth="0.3"/>
            <g fill="#3A2820">
              <path d="M -2 4 Q -2 3 -1 3 L 1 3 Q 2 3 2 4 L 2 6.5 L -2 6.5 Z"/>
              <line x1="-1.3" y1="3.5" x2="-1.3" y2="6.5" stroke="#F8E040" strokeWidth="0.3"/>
              <line x1="-0.4" y1="3.5" x2="-0.4" y2="6.5" stroke="#F8E040" strokeWidth="0.3"/>
              <line x1="0.5" y1="3.5" x2="0.5" y2="6.5" stroke="#F8E040" strokeWidth="0.3"/>
              <line x1="1.3" y1="3.5" x2="1.3" y2="6.5" stroke="#F8E040" strokeWidth="0.3"/>
            </g>
          </g>
          <g fill="#5A6838" opacity="0.6">
            <ellipse cx="20" cy="85" rx="4" ry="2"/>
            <ellipse cx="80" cy="92" rx="3" ry="1.5"/>
            <ellipse cx="180" cy="88" rx="4" ry="2"/>
            <ellipse cx="260" cy="95" rx="3" ry="1.5"/>
          </g>
        </svg>
      );

    case "angkor-wat":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F8C098"/>
          <rect width="280" height="42" fill="#F0A878"/>
          <rect width="280" height="22" fill="#E89068"/>
          <circle cx="140" cy="32" r="10" fill="#FAE0C0" opacity="0.85"/>
          <circle cx="140" cy="32" r="6" fill="#FAEAC8"/>
          <g fill="#3A4838" opacity="0.4">
            <ellipse cx="20" cy="60" rx="20" ry="3"/>
            <ellipse cx="260" cy="60" rx="20" ry="3"/>
          </g>
          <g transform="translate(140, 56)">
            <rect x="-50" y="0" width="100" height="6" fill="#3A2820"/>
            <rect x="-55" y="6" width="110" height="3" fill="#1F1A14"/>
            <g transform="translate(-36, 0)" fill="#3A2820">
              <rect x="-5" y="-12" width="10" height="12"/>
              <path d="M -5 -12 L 5 -12 L 3 -16 L -3 -16 Z"/>
              <path d="M -3 -16 L 3 -16 L 0 -20 Z"/>
              <rect x="-0.4" y="-22" width="0.8" height="2"/>
            </g>
            <g transform="translate(-18, 0)" fill="#3A2820">
              <rect x="-6" y="-15" width="12" height="15"/>
              <path d="M -6 -15 L 6 -15 L 4 -19 L -4 -19 Z"/>
              <path d="M -4 -19 L 4 -19 L 0 -24 Z"/>
              <rect x="-0.5" y="-26" width="1" height="2"/>
            </g>
            <g transform="translate(0, 0)" fill="#3A2820">
              <rect x="-8" y="-22" width="16" height="22"/>
              <path d="M -8 -22 L 8 -22 L 6 -27 L -6 -27 Z"/>
              <path d="M -6 -27 L 6 -27 L 0 -34 Z"/>
              <rect x="-0.5" y="-37" width="1" height="3"/>
            </g>
            <g transform="translate(18, 0)" fill="#3A2820">
              <rect x="-6" y="-15" width="12" height="15"/>
              <path d="M -6 -15 L 6 -15 L 4 -19 L -4 -19 Z"/>
              <path d="M -4 -19 L 4 -19 L 0 -24 Z"/>
              <rect x="-0.5" y="-26" width="1" height="2"/>
            </g>
            <g transform="translate(36, 0)" fill="#3A2820">
              <rect x="-5" y="-12" width="10" height="12"/>
              <path d="M -5 -12 L 5 -12 L 3 -16 L -3 -16 Z"/>
              <path d="M -3 -16 L 3 -16 L 0 -20 Z"/>
              <rect x="-0.4" y="-22" width="0.8" height="2"/>
            </g>
          </g>
          <rect y="65" width="280" height="45" fill="#3A4858"/>
          <rect y="68" width="280" height="42" fill="#5A6878"/>
          <g opacity="0.55">
            <rect x="85" y="65" width="110" height="22" fill="#3A2820"/>
            <g transform="translate(140, 65)" fill="#3A2820">
              <rect x="-8" y="0" width="16" height="22"/>
            </g>
          </g>
          <g stroke="#7A8898" strokeWidth="0.4" opacity="0.6">
            <line x1="0" y1="80" x2="50" y2="80"/>
            <line x1="200" y1="82" x2="280" y2="82"/>
            <line x1="50" y1="90" x2="120" y2="90"/>
            <line x1="180" y1="92" x2="240" y2="92"/>
            <line x1="0" y1="100" x2="280" y2="100"/>
            <line x1="60" y1="103" x2="130" y2="103"/>
          </g>
          <g fill="#3A5828" opacity="0.7">
            <ellipse cx="20" cy="100" rx="6" ry="1.5"/>
            <ellipse cx="50" cy="103" rx="5" ry="1.2"/>
            <ellipse cx="220" cy="102" rx="6" ry="1.5"/>
            <ellipse cx="255" cy="105" rx="5" ry="1.2"/>
          </g>
          <g fill="#F0A0B0" opacity="0.85">
            <circle cx="22" cy="99" r="1"/>
            <circle cx="222" cy="101" r="1"/>
            <circle cx="52" cy="102" r="0.8"/>
          </g>
        </svg>
      );

    case "cappadocia":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F8D0B8"/>
          <rect width="280" height="42" fill="#F0B8A0"/>
          <rect width="280" height="22" fill="#E8A088"/>
          <circle cx="225" cy="32" r="14" fill="#FAE0C0" opacity="0.55"/>
          <circle cx="225" cy="32" r="8" fill="#FAEAC8" opacity="0.85"/>
          <path d="M 0 55 Q 60 48 120 52 T 200 50 T 280 53 L 280 70 L 0 70 Z" fill="#9A8478" opacity="0.5"/>
          <g transform="translate(60, 35)">
            <ellipse cx="0" cy="0" rx="11" ry="13" fill="#C84030"/>
            <path d="M -11 0 Q -11 -13 0 -13 Q 11 -13 11 0" fill="#E85040"/>
            <ellipse cx="0" cy="0" rx="11" ry="13" fill="none" stroke="#8B2818" strokeWidth="0.4"/>
            <ellipse cx="0" cy="-2" rx="11" ry="2" fill="#F8E040" opacity="0.75"/>
            <ellipse cx="0" cy="6" rx="9" ry="2" fill="#F8E040" opacity="0.75"/>
            <line x1="-5" y1="13" x2="-2" y2="18" stroke="#3A2820" strokeWidth="0.4"/>
            <line x1="5" y1="13" x2="2" y2="18" stroke="#3A2820" strokeWidth="0.4"/>
            <line x1="0" y1="13" x2="0" y2="18" stroke="#3A2820" strokeWidth="0.4"/>
            <rect x="-3" y="18" width="6" height="3" fill="#5A4030"/>
          </g>
          <g transform="translate(100, 22)">
            <ellipse cx="0" cy="0" rx="8" ry="10" fill="#3A6AA8"/>
            <path d="M -8 0 Q -8 -10 0 -10 Q 8 -10 8 0" fill="#5A8AC8"/>
            <ellipse cx="0" cy="0" rx="8" ry="10" fill="none" stroke="#2A4878" strokeWidth="0.4"/>
            <ellipse cx="0" cy="0" rx="8" ry="1.5" fill="#F8E040" opacity="0.75"/>
            <line x1="-3" y1="10" x2="-1" y2="14" stroke="#3A2820" strokeWidth="0.3"/>
            <line x1="3" y1="10" x2="1" y2="14" stroke="#3A2820" strokeWidth="0.3"/>
            <rect x="-2" y="14" width="4" height="2" fill="#5A4030"/>
          </g>
          <g transform="translate(150, 38)">
            <ellipse cx="0" cy="0" rx="9" ry="11" fill="#F8C038"/>
            <path d="M -9 0 Q -9 -11 0 -11 Q 9 -11 9 0" fill="#FADA68"/>
            <ellipse cx="0" cy="0" rx="9" ry="11" fill="none" stroke="#A88018" strokeWidth="0.4"/>
            <ellipse cx="0" cy="-3" rx="9" ry="2" fill="#C84030" opacity="0.7"/>
            <ellipse cx="0" cy="5" rx="7" ry="2" fill="#C84030" opacity="0.7"/>
            <line x1="-4" y1="11" x2="-1.5" y2="15" stroke="#3A2820" strokeWidth="0.3"/>
            <line x1="4" y1="11" x2="1.5" y2="15" stroke="#3A2820" strokeWidth="0.3"/>
            <rect x="-3" y="15" width="6" height="2.5" fill="#5A4030"/>
          </g>
          <g transform="translate(190, 16)">
            <ellipse cx="0" cy="0" rx="6" ry="7" fill="#3A7A3A" opacity="0.9"/>
            <ellipse cx="0" cy="-2" rx="6" ry="1.2" fill="#F8E040" opacity="0.7"/>
            <rect x="-1.5" y="9" width="3" height="1.5" fill="#5A4030"/>
          </g>
          <g transform="translate(115, 48)">
            <ellipse cx="0" cy="0" rx="7" ry="8" fill="#C84080"/>
            <path d="M -7 0 Q -7 -8 0 -8 Q 7 -8 7 0" fill="#E860A0"/>
            <ellipse cx="0" cy="0" rx="7" ry="8" fill="none" stroke="#882858" strokeWidth="0.3"/>
            <ellipse cx="0" cy="0" rx="7" ry="1.2" fill="white" opacity="0.6"/>
            <line x1="-3" y1="8" x2="-1" y2="12" stroke="#3A2820" strokeWidth="0.3"/>
            <line x1="3" y1="8" x2="1" y2="12" stroke="#3A2820" strokeWidth="0.3"/>
            <rect x="-2" y="12" width="4" height="2" fill="#5A4030"/>
          </g>
          <g transform="translate(35, 18)">
            <ellipse cx="0" cy="0" rx="5" ry="6" fill="#F0A040" opacity="0.9"/>
            <rect x="-1" y="7" width="2" height="1.5" fill="#5A4030"/>
          </g>
          <g transform="translate(245, 48)">
            <ellipse cx="0" cy="0" rx="8" ry="9" fill="#783858"/>
            <path d="M -8 0 Q -8 -9 0 -9 Q 8 -9 8 0" fill="#985878"/>
            <ellipse cx="0" cy="0" rx="8" ry="9" fill="none" stroke="#582038" strokeWidth="0.3"/>
            <ellipse cx="0" cy="-3" rx="8" ry="1.5" fill="#F8E040" opacity="0.75"/>
            <ellipse cx="0" cy="3" rx="7" ry="1.5" fill="#F8E040" opacity="0.75"/>
            <line x1="-3" y1="9" x2="-1" y2="13" stroke="#3A2820" strokeWidth="0.3"/>
            <line x1="3" y1="9" x2="1" y2="13" stroke="#3A2820" strokeWidth="0.3"/>
            <rect x="-2" y="13" width="4" height="2" fill="#5A4030"/>
          </g>
          <g fill="#C8A878">
            <path d="M 18 75 L 16 100 L 28 100 L 26 75 Q 26 70 22 70 Q 18 70 18 75 Z"/>
            <ellipse cx="22" cy="70" rx="6" ry="3" fill="#A88858"/>
            <path d="M 48 80 L 46 100 L 60 100 L 58 80 Q 58 73 53 73 Q 48 73 48 80 Z"/>
            <ellipse cx="53" cy="73" rx="7" ry="3" fill="#A88858"/>
            <path d="M 78 82 L 76 100 L 88 100 L 86 82 Q 86 76 82 76 Q 78 76 78 82 Z"/>
            <ellipse cx="82" cy="76" rx="5.5" ry="2.5" fill="#A88858"/>
            <path d="M 128 78 L 126 100 L 138 100 L 136 78 Q 136 72 132 72 Q 128 72 128 78 Z"/>
            <ellipse cx="132" cy="72" rx="5.5" ry="2.5" fill="#A88858"/>
            <path d="M 175 78 L 173 100 L 183 100 L 181 78 Q 181 72 178 72 Q 175 72 175 78 Z"/>
            <ellipse cx="178" cy="72" rx="5" ry="2.5" fill="#A88858"/>
            <path d="M 210 80 L 208 100 L 220 100 L 218 80 Q 218 74 214 74 Q 210 74 210 80 Z"/>
            <ellipse cx="214" cy="74" rx="5.5" ry="2.5" fill="#A88858"/>
            <path d="M 245 82 L 243 100 L 253 100 L 251 82 Q 251 77 248 77 Q 245 77 245 82 Z"/>
            <ellipse cx="248" cy="77" rx="5" ry="2.5" fill="#A88858"/>
          </g>
          <g fill="#3A2820">
            <ellipse cx="22" cy="86" rx="2" ry="3"/>
            <ellipse cx="53" cy="90" rx="2" ry="3"/>
            <ellipse cx="82" cy="91" rx="1.5" ry="2.5"/>
            <ellipse cx="132" cy="87" rx="2" ry="3"/>
            <ellipse cx="178" cy="88" rx="1.8" ry="2.5"/>
            <ellipse cx="214" cy="90" rx="2" ry="3"/>
            <ellipse cx="248" cy="91" rx="1.8" ry="2.5"/>
          </g>
          <rect y="100" width="280" height="10" fill="#9A7858"/>
        </svg>
      );

    case "bull-running":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F0C098"/>
          <rect width="280" height="20" fill="#E8B088"/>
          <path d="M 0 0 L 80 0 L 80 90 L 0 90 Z" fill="#A88858"/>
          <path d="M 80 5 L 110 8 L 110 80 L 80 90 Z" fill="#C8A878"/>
          <path d="M 110 8 L 130 10 L 130 70 L 110 80 Z" fill="#D8B898"/>
          <path d="M 200 0 L 280 0 L 280 90 L 200 90 Z" fill="#A88858"/>
          <path d="M 170 5 L 200 0 L 200 90 L 170 80 Z" fill="#C8A878"/>
          <path d="M 150 8 L 170 5 L 170 80 L 150 70 Z" fill="#D8B898"/>
          <g fill="#3A2820">
            <rect x="10" y="20" width="6" height="10"/>
            <rect x="30" y="20" width="6" height="10"/>
            <rect x="50" y="22" width="6" height="10"/>
            <rect x="10" y="40" width="6" height="10"/>
            <rect x="30" y="40" width="6" height="10"/>
            <rect x="50" y="42" width="6" height="10"/>
            <rect x="10" y="60" width="6" height="10"/>
            <rect x="30" y="60" width="6" height="10"/>
            <rect x="50" y="62" width="6" height="10"/>
            <rect x="216" y="20" width="6" height="10"/>
            <rect x="236" y="20" width="6" height="10"/>
            <rect x="258" y="22" width="6" height="10"/>
            <rect x="216" y="40" width="6" height="10"/>
            <rect x="236" y="40" width="6" height="10"/>
            <rect x="258" y="42" width="6" height="10"/>
            <rect x="216" y="60" width="6" height="10"/>
            <rect x="236" y="60" width="6" height="10"/>
            <rect x="258" y="62" width="6" height="10"/>
          </g>
          <g fill="#5A4030">
            <rect x="8" y="32" width="20" height="2"/>
            <rect x="28" y="32" width="20" height="2"/>
            <rect x="48" y="34" width="14" height="2"/>
            <rect x="8" y="52" width="20" height="2"/>
            <rect x="28" y="52" width="20" height="2"/>
            <rect x="48" y="54" width="14" height="2"/>
            <rect x="216" y="32" width="14" height="2"/>
            <rect x="236" y="32" width="20" height="2"/>
            <rect x="256" y="34" width="14" height="2"/>
            <rect x="216" y="52" width="14" height="2"/>
            <rect x="236" y="52" width="20" height="2"/>
            <rect x="256" y="54" width="14" height="2"/>
          </g>
          <g stroke="#5A4030" strokeWidth="0.5" fill="none">
            <line x1="8" y1="32" x2="8" y2="48"/>
            <line x1="28" y1="32" x2="28" y2="48"/>
            <line x1="48" y1="34" x2="48" y2="48"/>
            <line x1="62" y1="34" x2="62" y2="48"/>
            <line x1="216" y1="32" x2="216" y2="48"/>
            <line x1="230" y1="32" x2="230" y2="48"/>
            <line x1="256" y1="34" x2="256" y2="48"/>
            <line x1="270" y1="34" x2="270" y2="48"/>
          </g>
          <path d="M 130 90 L 150 90 L 200 110 L 80 110 Z" fill="#A88858"/>
          <path d="M 140 90 L 145 90 L 175 110 L 105 110 Z" fill="#8A6838"/>
          <g fill="#5A4838" opacity="0.5">
            <ellipse cx="100" cy="105" rx="2" ry="0.6"/>
            <ellipse cx="130" cy="103" rx="2" ry="0.6"/>
            <ellipse cx="160" cy="100" rx="1.8" ry="0.5"/>
            <ellipse cx="120" cy="108" rx="2" ry="0.6"/>
            <ellipse cx="155" cy="108" rx="2" ry="0.6"/>
          </g>
          <g transform="translate(140, 80)">
            <ellipse cx="0" cy="0" rx="12" ry="6" fill="#1F1A14"/>
            <ellipse cx="-8" cy="-1" rx="6" ry="5" fill="#1F1A14"/>
            <ellipse cx="11" cy="-1" rx="5" ry="4" fill="#1F1A14"/>
            <path d="M 13 -4 Q 16 -7 18 -5" stroke="#1F1A14" strokeWidth="1" fill="none"/>
            <path d="M 11 -4 Q 8 -7 6 -5" stroke="#1F1A14" strokeWidth="1" fill="none"/>
            <rect x="-9" y="5" width="2" height="6" fill="#1F1A14"/>
            <rect x="-4" y="5" width="2" height="6" fill="#1F1A14"/>
            <rect x="4" y="5" width="2" height="6" fill="#1F1A14"/>
            <rect x="8" y="5" width="2" height="6" fill="#1F1A14"/>
            <path d="M -12 -2 Q -14 -1 -13 2" stroke="#1F1A14" strokeWidth="1" fill="none"/>
          </g>
          <g transform="translate(115, 88)">
            <circle cx="0" cy="-7" r="2" fill="#D8B898"/>
            <rect x="-2" y="-5" width="4" height="6" fill="white"/>
            <path d="M -2 -2 L 2 -1 L 2 0 L -2 -1 Z" fill="#C84030"/>
            <rect x="-1.5" y="1" width="1.5" height="6" fill="white"/>
            <rect x="0" y="1" width="1.5" height="6" fill="white"/>
          </g>
          <g transform="translate(125, 91)">
            <circle cx="0" cy="-7" r="2" fill="#D8B898"/>
            <rect x="-2" y="-5" width="4" height="6" fill="white"/>
            <path d="M -2 -2 L 2 -1 L 2 0 L -2 -1 Z" fill="#C84030"/>
            <rect x="-1.5" y="1" width="1.5" height="6" fill="white"/>
            <rect x="0" y="1" width="1.5" height="6" fill="white"/>
          </g>
          <g transform="translate(170, 91)">
            <circle cx="0" cy="-7" r="2" fill="#D8B898"/>
            <rect x="-2" y="-5" width="4" height="6" fill="white"/>
            <path d="M -2 -2 L 2 -1 L 2 0 L -2 -1 Z" fill="#C84030"/>
            <rect x="-1.5" y="1" width="1.5" height="6" fill="white"/>
            <rect x="0" y="1" width="1.5" height="6" fill="white"/>
          </g>
          <g transform="translate(180, 88)">
            <circle cx="0" cy="-7" r="2" fill="#D8B898"/>
            <rect x="-2" y="-5" width="4" height="6" fill="white"/>
            <path d="M -2 -2 L 2 -1 L 2 0 L -2 -1 Z" fill="#C84030"/>
            <rect x="-1.5" y="1" width="1.5" height="6" fill="white"/>
            <rect x="0" y="1" width="1.5" height="6" fill="white"/>
          </g>
        </svg>
      );

    case "great-wall":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#E0D8C8"/>
          <rect width="280" height="40" fill="#D8C8B8"/>
          <rect width="280" height="20" fill="#C8B8A8"/>
          <circle cx="60" cy="22" r="9" fill="#F8E0B8" opacity="0.7"/>
          <path d="M 0 60 L 30 50 L 60 55 L 100 45 L 140 52 L 180 48 L 220 54 L 260 48 L 280 52 L 280 70 L 0 70 Z" fill="#A8A898" opacity="0.5"/>
          <path d="M 0 65 L 40 55 L 80 62 L 130 50 L 180 58 L 230 52 L 280 60 L 280 80 L 0 80 Z" fill="#888878" opacity="0.7"/>
          <path d="M 0 75 L 50 65 L 100 72 L 160 60 L 220 70 L 280 65 L 280 95 L 0 95 Z" fill="#5A6A58"/>
          <path d="M 0 90 L 60 78 L 130 88 L 210 75 L 280 85 L 280 110 L 0 110 Z" fill="#3A4838"/>
          <path d="M 280 92 Q 230 80 200 78 Q 170 75 140 70 Q 110 67 90 65 Q 60 63 30 55" stroke="#9A8870" strokeWidth="4.5" fill="none"/>
          <path d="M 280 92 Q 230 80 200 78 Q 170 75 140 70 Q 110 67 90 65 Q 60 63 30 55" stroke="#7A6850" strokeWidth="2.5" fill="none"/>
          <g fill="#5A4838">
            <rect x="265" y="86" width="2" height="3"/>
            <rect x="258" y="86" width="2" height="3"/>
            <rect x="251" y="86" width="2" height="3"/>
            <rect x="244" y="84" width="2" height="3"/>
            <rect x="237" y="83" width="2" height="3"/>
            <rect x="230" y="82" width="2" height="3"/>
            <rect x="220" y="80" width="2" height="3"/>
            <rect x="210" y="78" width="2" height="3"/>
            <rect x="200" y="76" width="2" height="3"/>
            <rect x="185" y="74" width="1.8" height="2.5"/>
            <rect x="170" y="72" width="1.8" height="2.5"/>
            <rect x="160" y="70" width="1.6" height="2.5"/>
            <rect x="150" y="68" width="1.6" height="2.5"/>
            <rect x="140" y="67" width="1.6" height="2.5"/>
            <rect x="120" y="65" width="1.4" height="2"/>
            <rect x="110" y="64" width="1.4" height="2"/>
            <rect x="100" y="63" width="1.2" height="2"/>
            <rect x="85" y="62" width="1.2" height="2"/>
            <rect x="70" y="60" width="1.2" height="2"/>
            <rect x="55" y="58" width="1" height="1.8"/>
            <rect x="40" y="55" width="1" height="1.8"/>
          </g>
          <g transform="translate(245, 80)" fill="#5A4838">
            <rect x="-5" y="-3" width="10" height="9"/>
            <rect x="-6" y="-5" width="12" height="3"/>
            <rect x="-6" y="-7" width="2" height="3"/>
            <rect x="-3" y="-7" width="2" height="3"/>
            <rect x="0" y="-7" width="2" height="3"/>
            <rect x="3" y="-7" width="2" height="3"/>
            <rect x="-2" y="0" width="1.5" height="3" fill="#1F1A14"/>
            <rect x="0.5" y="0" width="1.5" height="3" fill="#1F1A14"/>
          </g>
          <g transform="translate(165, 70)" fill="#5A4838">
            <rect x="-4" y="-2" width="8" height="7"/>
            <rect x="-5" y="-4" width="10" height="2"/>
            <rect x="-5" y="-6" width="1.5" height="2"/>
            <rect x="-2.5" y="-6" width="1.5" height="2"/>
            <rect x="0" y="-6" width="1.5" height="2"/>
            <rect x="2.5" y="-6" width="1.5" height="2"/>
            <rect x="-1.5" y="0" width="1.2" height="2.5" fill="#1F1A14"/>
            <rect x="0.5" y="0" width="1.2" height="2.5" fill="#1F1A14"/>
          </g>
          <g transform="translate(85, 60)" fill="#5A4838">
            <rect x="-3" y="-1" width="6" height="5"/>
            <rect x="-4" y="-2" width="8" height="1.5"/>
            <rect x="-1" y="1" width="1" height="2" fill="#1F1A14"/>
          </g>
          <g fill="#1A2818">
            <polygon points="15,95 18,82 21,95"/>
            <polygon points="22,98 25,86 28,98"/>
            <polygon points="40,95 43,84 46,95"/>
            <polygon points="260,98 263,86 266,98"/>
            <polygon points="270,100 273,88 276,100"/>
          </g>
        </svg>
      );

    case "taj-mahal":
      return (
        <svg className="scene" viewBox="0 0 280 110" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="110" fill="#F8C8A8"/>
          <rect width="280" height="42" fill="#F0A878"/>
          <rect width="280" height="22" fill="#E89058"/>
          <circle cx="220" cy="30" r="11" fill="#FAE0C0" opacity="0.85"/>
          <circle cx="220" cy="30" r="7" fill="#FAEAC8"/>
          <path d="M 0 55 Q 60 50 120 52 T 240 53 T 280 55 L 280 65 L 0 65 Z" fill="#9A8878" opacity="0.5"/>
          <g fill="#3A4838">
            <ellipse cx="20" cy="73" rx="3" ry="10"/>
            <ellipse cx="30" cy="73" rx="2.5" ry="9"/>
            <ellipse cx="250" cy="73" rx="3" ry="10"/>
            <ellipse cx="260" cy="73" rx="2.5" ry="9"/>
          </g>
          <g transform="translate(140, 50)">
            <rect x="-60" y="22" width="120" height="5" fill="#E0D8C8"/>
            <rect x="-65" y="27" width="130" height="3" fill="#C8C0B0"/>
            <rect x="-55" y="-2" width="3" height="24" fill="white"/>
            <rect x="-56" y="-2" width="5" height="2" fill="#E0D8C8"/>
            <ellipse cx="-53.5" cy="-4" rx="3" ry="2" fill="white"/>
            <rect x="-54" y="-7" width="1" height="3" fill="white"/>
            <rect x="52" y="-2" width="3" height="24" fill="white"/>
            <rect x="51" y="-2" width="5" height="2" fill="#E0D8C8"/>
            <ellipse cx="53.5" cy="-4" rx="3" ry="2" fill="white"/>
            <rect x="53" y="-7" width="1" height="3" fill="white"/>
            <rect x="-35" y="2" width="2.5" height="20" fill="white"/>
            <rect x="-36" y="2" width="4.5" height="1.5" fill="#E0D8C8"/>
            <ellipse cx="-33.75" cy="0" rx="2.5" ry="1.5" fill="white"/>
            <rect x="-34.5" y="-2" width="1" height="3" fill="white"/>
            <rect x="32.5" y="2" width="2.5" height="20" fill="white"/>
            <rect x="31.5" y="2" width="4.5" height="1.5" fill="#E0D8C8"/>
            <ellipse cx="33.75" cy="0" rx="2.5" ry="1.5" fill="white"/>
            <rect x="33.25" y="-2" width="1" height="3" fill="white"/>
            <rect x="-22" y="0" width="44" height="22" fill="white"/>
            <rect x="-22" y="0" width="44" height="2" fill="#E0D8C8"/>
            <path d="M -5 22 L -5 12 Q -5 5 0 5 Q 5 5 5 12 L 5 22 Z" fill="#3A2820"/>
            <g fill="#5A4838" opacity="0.6">
              <path d="M -16 22 L -16 16 Q -16 11 -13 11 Q -10 11 -10 16 L -10 22 Z"/>
              <path d="M 10 22 L 10 16 Q 10 11 13 11 Q 16 11 16 16 L 16 22 Z"/>
            </g>
            <ellipse cx="-15" cy="-2" rx="6" ry="3" fill="white"/>
            <ellipse cx="-15" cy="-2" rx="6" ry="3" fill="none" stroke="#E0D8C8" strokeWidth="0.4"/>
            <rect x="-15.5" y="-5" width="1" height="3" fill="white"/>
            <ellipse cx="15" cy="-2" rx="6" ry="3" fill="white"/>
            <ellipse cx="15" cy="-2" rx="6" ry="3" fill="none" stroke="#E0D8C8" strokeWidth="0.4"/>
            <rect x="14.5" y="-5" width="1" height="3" fill="white"/>
            <path d="M -12 0 Q -12 -16 0 -18 Q 12 -16 12 0 Z" fill="white"/>
            <path d="M -12 0 Q -12 -16 0 -18 Q 12 -16 12 0" fill="none" stroke="#E0D8C8" strokeWidth="0.5"/>
            <rect x="-13" y="0" width="26" height="2" fill="#E0D8C8"/>
            <rect x="-0.5" y="-22" width="1" height="4" fill="white"/>
            <circle cx="0" cy="-23" r="1.2" fill="white"/>
            <rect x="-0.4" y="-27" width="0.8" height="4" fill="#E0D8C8"/>
          </g>
          <rect y="80" width="280" height="30" fill="#3A6478"/>
          <rect y="82" width="280" height="28" fill="#5A8898"/>
          <g transform="translate(140, 80)" opacity="0.5">
            <rect x="-22" y="0" width="44" height="14" fill="white"/>
            <path d="M -12 14 Q -12 26 0 26 Q 12 26 12 14 Z" fill="white"/>
            <rect x="-55" y="0" width="3" height="12" fill="white"/>
            <rect x="52" y="0" width="3" height="12" fill="white"/>
          </g>
          <line x1="0" y1="80" x2="280" y2="80" stroke="#3A4858" strokeWidth="0.4"/>
          <g stroke="#7A98A8" strokeWidth="0.3" opacity="0.6">
            <line x1="20" y1="92" x2="60" y2="92"/>
            <line x1="220" y1="95" x2="260" y2="95"/>
            <line x1="80" y1="100" x2="120" y2="100"/>
            <line x1="160" y1="103" x2="200" y2="103"/>
            <line x1="0" y1="106" x2="280" y2="106"/>
          </g>
          <g fill="#1A2818" opacity="0.85">
            <ellipse cx="50" cy="84" rx="2" ry="5"/>
            <ellipse cx="58" cy="83" rx="1.8" ry="4"/>
            <ellipse cx="222" cy="84" rx="2" ry="5"/>
            <ellipse cx="230" cy="83" rx="1.8" ry="4"/>
          </g>
        </svg>
      );

    default: {
      // Fallback for catalog entries without bespoke art — looks up the
      // catalog row for this id and renders a passport-stamp placeholder.
      const cat = (typeof SUGGESTED_STAMPS !== "undefined" ? SUGGESTED_STAMPS : []).find(s => s.id === id);
      if (!cat) return null;
      const title = (cat.title || cat.name || "").toUpperCase();
      const country = cat.country || "";
      return (
        <svg viewBox="0 0 280 100" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMidYMid slice" style={{ display: "block", width: "100%", height: "100%" }}>
          <rect width="280" height="100" fill="#EEE3CF"/>
          <circle cx="58" cy="50" r="36" fill="none" stroke="#8B3A2F" strokeWidth="1.2" opacity="0.35" strokeDasharray="2 3"/>
          <circle cx="58" cy="50" r="28" fill="#FAF5E8" opacity="0.85"/>
          <text x="58" y="65" textAnchor="middle" fontSize="36" fontFamily="-apple-system, system-ui, sans-serif">{cat.emoji}</text>
          <text x="108" y="44" fontFamily="Georgia, 'Times New Roman', serif" fontSize="15" fill="#8B3A2F" fontWeight="700" letterSpacing="1.5">{title}</text>
          <text x="108" y="60" fontFamily="ui-monospace, Menlo, monospace" fontSize="9" fill="#8B3A2F" letterSpacing="2.5" opacity="0.75">{country}</text>
          <path d="M 108 76 L 256 76" stroke="#8B3A2F" strokeWidth="0.8" opacity="0.35" strokeDasharray="3 2"/>
          <text x="108" y="88" fontFamily="ui-monospace, Menlo, monospace" fontSize="7" fill="#8B3A2F" letterSpacing="2" opacity="0.6">STAMP · VISITED</text>
        </svg>
      );
    }
  }
}
